import JobCustomParametersSummary from '@components/JobCustomParametersSummary';
import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ResizableTitle, MiddleEllipsis, useResizableColumns } from '@components/ResizableTable';
import { JobStatusTag } from '@components/StatusTag';
import { renderAlgorithmVersionCell } from '@components/AlgorithmVersionCell';
import { useVersionTrace } from '@components/VersionTraceModal/useVersionTrace';
import DailySummaryCard, { type DailySummary } from '@components/DailySummaryCard';
import SceneCommentDrawer, { useSceneCommentSummary } from '@components/SceneCommentDrawer';
import CommentFilterPopover, {
  EMPTY_COMMENT_FILTER,
  isCommentFilterActive,
  type CommentFilterValue,
} from '@components/CommentFilterPopover';
import VruRelativeSpeedChart from '@components/VruRelativeSpeedChart';
import MetricAggCharts from '@components/MetricAggCharts';
import CreateJobModal from '@components/CreateJobModal';
import ModelReleaseStaleAlert from '@components/ModelReleaseStaleAlert';
import UpdatePriorityModal, {
  PRIORITY_EDITABLE_STATUSES,
} from '@components/UpdatePriorityModal';
import PriorityTag from '@components/PriorityTag';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import {
  Table,
  Input,
  InputNumber,
  Button,
  Form,
  Space,
  Select,
  TreeSelect,
  Modal,
  Spin,
  Popover,
  App,
  Image,
  Tag,
  Alert,
  Tooltip,
  Switch,
  Radio,
  Typography,
  Empty,
} from 'antd';
import { MoreOutlined, CopyOutlined, ReloadOutlined, DownloadOutlined, LinkOutlined, QuestionCircleOutlined, ArrowUpOutlined, ArrowDownOutlined, DownOutlined, UpOutlined, FileTextOutlined, CommentOutlined, LockFilled, UnlockOutlined, SwapOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import { useCurrentUser } from '@/common/auth/adminAcl';
import { buildFoxgloveUrl, resolveMcapUrl } from '@/common/foxglove';
import { SCENE_REGION_OPTIONS, sceneRegionDisplay } from '@/constants/region';
import { useTaskLogModal } from '@components/TaskLogModal';
import {
  getTaskList,
  getFilteredTaskCount,
  exportTaskReport,
  setScenesSecurityLevel,
  getTaskStatusList,
  getEvaluatorList,
  getEvaluatorNamesByJob,
  getRespEventNamesByJob,
  getCheckNamesByJob,
  getJobSceneLabels,
  getEvaluationFileUrls,
  getJobTaskCount,
  getJobTaskCountByLabel,
  setExcludeCheckFailed,
  getJobList,
  getJobBasicInfo,
  getJobBazelBuildLog,
  retryBazelJob,
  getJobBuildLogs,
  type JobDetailData,
  getSceneTree,
  getJobSceneTreeNodes,
  compareDailyJobs,
  compareDailyJobsEvaluatorMatrix,
  compareDailyJobsEvaluatorMetrics,
  getJobEvaluatorMetrics,
  getJobEvaluatorReasons,
  getJobEvaluatorMetricValues,
  compareDailyJobsMetricDirectionCounts,
  type MetricDirectionCounts,
  getCompareBundle,
  getJobEvaluatorStats,
  type JobEvaluatorStatData,
  getEvaluatorMetricAggSpecs,
  getJobEvaluatorMetricAgg,
  compareJobEvaluatorMetricAgg,
  type EvaluatorMetricAggSpec,
  type EvaluatorMetricAggSceneRow,
  type JobEvaluatorMetricAgg,
  type CompareEvaluatorMetricAgg,
  TaskListItem,
  TaskListParams,
  StatusOption,
  EvaluatorItem,
  CheckerVerdict,
  JobTaskCountByLabelItem,
  JobTaskCountData,
  FilteredTaskCountData,
  JobItem,
  JobSceneTreeNodeOption,
  SceneTreeNode,
  type TaskDiffMode,
  type CompareConsistency,
  type BehaviorFilterValue,
  type BehaviorSceneLabelMap,
  type BehaviorSceneRow,
  getBehaviorSceneLabels,
  getBehaviorSceneRows,
  type CompareConsistencyVerdict,
  type MetricCompare,
  type MetricSort,
  type EvaluatorMetricDef,
  type JobEvaluatorReason,
  getReasonDiff,
  type ReasonDiff,
  type ReasonDiffCell,
  type EvaluatorReasonStat,
  type EvaluatorReasonStats,
  type DailyCompare,
  type DailyCompareEvaluatorMatrix,
  type SimType,
} from '@apis/simulation';
import { getEvaluatorDocUrl, getEvaluatorDisplay, getEvaluatorLabel, isEvaluatorName, useEvaluatorNames } from '@/constants/evaluators';
import { checkerVerdictTone, pickCheckerRows } from '@/utils/checkerVerdict';
import { formatPredictionDataVersion } from '@/utils/versionTag';
import { canonicalEvaluatorName, extractMetricFromItems } from '@/constants/evaluatorMetrics';
import { simTypeColor, simTypeLabel } from '@/constants/simType';
import { taskTypeMode } from '@/constants/taskTypeMode';
import { EMPTY, fmtCount, fmtRatio } from '@/pages/PerceptionReport/format';
import {
  JobStatTiles, JobInfoGrid, SceneSetPopover, FilteredStatRow, type JobInfoField,
} from './components/JobHeader';
import RerunFromQueryConfirmModal from './RerunFromQueryConfirmModal';
import SceneSetComparison from './components/SceneSetComparison';
import { LABEL_META as BEHAVIOR_LABEL_META, STRONG_EVENT_NAMES as BEHAVIOR_STRONG_NAMES, shortDiffText } from './components/BehaviorCompare/format';
import BehaviorSparkline from './components/BehaviorCompare/components/Sparkline';
import BehaviorSummaryBars from './components/BehaviorCompare/components/SummaryBars';
import BehaviorCompareSection from './components/BehaviorCompare';
import BehaviorLaunchButton from './components/BehaviorCompare/LaunchButton';
import { useSceneSetComparison, sceneSetOptions } from './components/useSceneSetComparison';
import { useSceneSetDrill } from './components/useSceneSetDrill';
import { createLatestRequestGuard } from '../JobList/latestRequest';
import { canRetryBazelJob, createBazelRetryController } from '../JobList/bazelRetry';
import { statsDefaultStatus } from './sceneSetFilter';
import { extractNonEmptyFilters, toQueryFilters } from './filterLabels';
import {
  writeTaskFiltersToParams,
  readTaskFiltersFromParams,
  readPaginationFromParams,
  readCommentFilterFromParams,
  pickFormValues,
  writeCompareFiltersToParams,
  readCompareFiltersFromParams,
  EMPTY_COMPARE_FILTERS,
  type CompareFilterState,
} from './urlFilters';
import { findScenePath } from '@/common/sceneSetTree';
import { useEnterToSearch } from '@/common/keyboard';

/** api_code 跳转地址前缀：source=jira 拼 Jira，source=case_flow 拼 Case-Flow（waypulse）。
 *  Case-Flow 三区域 (cn/ae/sea) 各自独立 cluster + 独立 waypulse 域名, 与
 *  @components/ApiCodeLink 同源规约; 按 scene_inspection.region 派生前缀. */
const JIRA_BROWSE_PREFIX = 'https://jira.neolix.net/browse/';
const CASE_FLOW_BROWSE_PREFIX_CN = 'https://waypulse.brood.neolix.cn/?case=';
const CASE_FLOW_BROWSE_PREFIX_AE = 'https://waypulse.neolixgo.ai/?case=';
const CASE_FLOW_BROWSE_PREFIX_SEA = 'https://waypulse.neolix.ai/?case=';

/** ISO 时间戳格式化：`YYYY-MM-DD HH:mm:ss`；空值统一 `—`。 */
const fmtTime = (t?: string | null): string =>
  t ? new Date(t).toLocaleString('zh-CN') : '—';

/** 算法版本：后端透传的 ``algorithm_version`` 可能是完整 OBS 路径
 * （如 ``bos://bucket/dir/release_v1.2.3.tar.gz`` 或 ``package:bos://...zip``），
 * 也可能是短串（``compile:abc1234`` / ``v1.0.0``）。仅取最后一个 ``/`` 之后的片段
 * 用于展示，原值放 Tooltip 便于排查。无 ``/`` 时返回原串。 */
const formatAlgorithmVersion = (raw?: string | null): string => {
  if (!raw) return '—';
  const idx = raw.lastIndexOf('/');
  return idx >= 0 ? (raw.slice(idx + 1) || raw) : raw;
};

/** bazel 编译 Job 从 JobItem / bazelInputs 抽「基线版本号」（Job 提交时的 base_version 入参）。
 *  非 bazel 或字段缺失返回 null，供调用侧决定是否渲染该字段。 */
const pickBaseVersion = (jobItem?: JobItem | null): string | null => {
  if (!jobItem || jobItem.buildType !== 'package_bazel') return null;
  const v = jobItem.bazelInputs?.base_version;
  return v && v.trim() ? v.trim() : null;
};

/** 「当前版本号」= 本次实际使用的 algorithmVersion（buildSpec 优先，回退 JobItem.algorithmVersion）。
 *  bazel 场景就是发布出的 version 全路径；neodata 场景是选定的中台包路径；空返回 null。 */
const pickCurrentVersion = (jobItem?: JobItem | null): string | null => {
  if (!jobItem) return null;
  const spec = jobItem.buildSpec;
  if (spec && (spec.type === 'package_bazel' || spec.type === 'package_neodata')) {
    const v = spec.algorithmVersion?.trim();
    if (v) return v;
  }
  const fallback = jobItem.algorithmVersion?.trim();
  return fallback || null;
};

/** 版本号字段展示壳：单行展示 + Tooltip 原值 + 一键复制按钮；给 grid / 对比页复用。 */
const VersionValue: React.FC<{ value: string }> = ({ value }) => (
  <Tooltip title={value} placement="topLeft">
    <Typography.Text
      copyable={{ text: value, tooltips: ['复制', '已复制'] }}
      style={{
        fontFamily: "'JetBrains Mono', Consolas, monospace",
        fontSize: 12,
        maxWidth: '100%',
      }}
      ellipsis
    >
      {formatAlgorithmVersion(value)}
    </Typography.Text>
  </Tooltip>
);

/** 拼装普通模式 Task 列表页头「Job 元信息」grid 字段。
 *
 * `jobMeta`（`JobItem`）字段比 statsMode 用的 `jobBasicInfo` 更全（含 buildType /
 * bazelInputs / sourceName / addToSceneSetName / sceneGroupRefs），页头这里直接吃 JobItem
 * 的富信息。sceneTree 空时场景集合列由 SceneSetPopover 自身回退为「-」。 */
function buildJobInfoFields(
  jobMeta: JobItem | null,
  sceneTree: SceneTreeNode[],
  onTrace?: (version: string) => void,
): JobInfoField[] {
  if (!jobMeta) return [];
  const fields: JobInfoField[] = [
    { label: '任务类型', value: jobMeta.taskTypeName || '—' },
    { label: '优先级', value: jobMeta.priorityName || '—' },
    { label: '创建人', value: jobMeta.creator || '—' },
  ];
  if (jobMeta.customParams && Object.keys(jobMeta.customParams).length > 0) {
    fields.push({ label: '自定义参数', full: true, value: <JobCustomParametersSummary params={jobMeta.customParams} /> });
  }
  if (jobMeta.sourceName) {
    fields.push({ label: '数据源', value: jobMeta.sourceName });
  }
  fields.push(
    { label: '创建时间', value: fmtTime(jobMeta.createTime) },
    { label: '结束时间', value: fmtTime(jobMeta.endTime) },
  );
  if (jobMeta.addToSceneSetName) {
    fields.push({ label: '入集目标', value: jobMeta.addToSceneSetName });
  }
  // 基线版本号（仅 bazel 编译 job；package_neodata 无此语义）
  const baseVer = pickBaseVersion(jobMeta);
  if (baseVer) {
    fields.push({ label: '基线版本号', value: <VersionValue value={baseVer} /> });
  }
  // 当前版本号 = 本次实际使用的 algorithmVersion；无值时不加此字段。
  const curVer = pickCurrentVersion(jobMeta);
  if (curVer) {
    fields.push({ label: '当前版本号', value: <VersionValue value={curVer} /> });
  }
  // 算法版本：直接复用 JobList 表列同款 AlgorithmVersionCell —— 支持包路径
  // 弹层、bazel base_version + module branch/commit、buildType 派生路径, 保持
  // Job 列表与 Task 列表页头两处渲染完全一致。bazel base_version 已被本组件
  // 内部展示，无需上方再单起「编译版本」字段。
  fields.push({
    label: '算法版本',
    full: true,
    value: renderAlgorithmVersionCell(jobMeta, onTrace),
  });
  // 预测数据版本（LPL/PNC 仿真 job 建任务时冻结的实际生效值，如
  // prediction.release.10.12.34）；非 LPL / 存量 job 为空，不加该字段。
  if (jobMeta.predictionDataVersion) {
    fields.push({
      label: '预测数据版本',
      full: true,
      value: <VersionValue value={formatPredictionDataVersion(jobMeta.predictionDataVersion)} />,
    });
  }
  fields.push({
    label: '场景集合',
    full: true,
    value: (
      <SceneSetPopover
        sceneSetIds={jobMeta.sceneSetIds}
        sceneGroupRefs={jobMeta.sceneGroupRefs}
        sceneSource={jobMeta.sceneSource}
        sceneTree={sceneTree}
      />
    ),
  });
  return fields;
}

/** 筛选区「任务级 / 评估器级」分组容器样式：整行排布、左侧色条标识一组。 */
const FILTER_GROUP_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  rowGap: 8,
  width: '100%',
  marginBottom: 12,
  paddingLeft: 8,
  borderLeft: '3px solid #f0f0f0',
};

/** 分组名 Tag 样式：与右侧筛选项垂直居中，留出间距。 */
const FILTER_GROUP_TAG_STYLE: React.CSSProperties = {
  marginRight: 12,
  alignSelf: 'center',
};

/**
 * diffMode → 「**当前侧**（基准 Job）是未通过还是通过」：决定「原因」筛选下拉里挂
 * 哪一组原因**类别**。
 *
 * 口径与后端 ``daily_service._diff_scene_ids`` 的 ``<对比>_<基准>`` 命名严格一致：
 * ``fail_fail`` / ``pass_fail`` / ``new_fail`` 的当前侧都是**未通过** → 只可能上报
 * **失败原因**（kind=fail）；``pass_pass`` / ``fail_pass`` / ``new_success`` 的当前侧
 * 都是**通过** → 只可能上报**豁免原因**（kind=exempt）。
 *
 * 不在表里（未限定象限 / ``all``）→ 两类都展示：这时行集本来就横跨通过与未通过。
 */
const REASON_KIND_BY_DIFF_MODE: Record<string, 'fail' | 'exempt'> = {
  fail_fail: 'fail',
  pass_fail: 'fail',
  new_fail: 'fail',
  pass_pass: 'exempt',
  fail_pass: 'exempt',
  new_success: 'exempt',
};

/**
 * stats 页「评估项状态」→ 允许选的原因种类（与上面对比页的象限映射同语义）。
 *
 * - `pass`（通过）→ 只可能上报**豁免原因**（靠豁免才通过）；失败原因在通过的场景里不存在，
 *   留着「失败原因」下拉只会筛出空集；
 * - `fail`（不通过）→ 只可能是**失败原因**；
 * - `error`（异常）/ 未选 → 不限定（异常终态没有可靠的评测明细，也不替用户猜）。
 */
const REASON_KIND_BY_EVAL_STATUS: Record<string, 'fail' | 'exempt'> = {
  pass: 'exempt',
  fail: 'fail',
};

/**
 * 「原因」筛选下拉里，**类别**选项的值前缀（判定桶选项直接用 ``same`` / ``diff`` /
 * ``only_current``，不加前缀）。
 *
 * 用前缀把「一个控件里两种取值」编码成一个字符串，而不是加第二个下拉：用户要的是
 * 「原因」这一个筛选入口，里面既有判定也有类别；拆成两个控件会让人以为可以叠加。
 * 后端 `reasonDiff` / `reasonCode` 两个参数本身仍是正交可叠加的，只是 UI 这一期
 * 不给叠加入口。
 */
const REASON_CODE_VALUE_PREFIX = 'code:';

/** 按 code 字面格式路由外链目标(不看后端 source):
 *  - ``XX-\d+`` (2+ 位项目前缀 + 短横 + 全数字,如 DC-4145326 / PNC-123) → Jira
 *  - 其余 → Case-Flow(如 LPL0000239532 / 内部数据平台/回收任务/波形推送 case id)
 *  空 code 回退占位符。source 入参保留但不再影响路由,便于后续再切回按 source 判定。 */
const JIRA_CODE_PATTERN = /^[A-Za-z]{2,}-\d+$/;

function ApiCodeLink({ code, source: _source, region }: { code?: string | null; source?: string | null; region?: string | null }) {
  const value = (code || '').trim();
  if (!value) {
    return <span style={{ color: '#9b9da3' }}>-</span>;
  }
  const isJira = JIRA_CODE_PATTERN.test(value);
  const r = (region || '').toLowerCase();
  // Case-Flow 三分域: cn (默认) / ae / sea 分别跳独立 waypulse 域名 —
  // 三 cluster 数据不通, 跳错域名会查不到 case (跟 @components/ApiCodeLink 同源规约).
  let caseFlowPrefix = CASE_FLOW_BROWSE_PREFIX_CN;
  if (r === 'ae') caseFlowPrefix = CASE_FLOW_BROWSE_PREFIX_AE;
  else if (r === 'sea') caseFlowPrefix = CASE_FLOW_BROWSE_PREFIX_SEA;
  const href = isJira
    ? `${JIRA_BROWSE_PREFIX}${encodeURIComponent(value)}`
    : `${caseFlowPrefix}${encodeURIComponent(value)}`;
  const label = isJira ? 'Jira' : 'Case-Flow';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`在 ${label} 打开 ${value}`}
    >
      {value}
    </a>
  );
}

function toTreeSelectData(nodes: JobSceneTreeNodeOption[]): object[] {
  return nodes.map((n) => ({
    title: n.name,
    value: n.id,
    // 目录节点与叶子节点均可勾选：勾目录 = 选中其下全部场景（后端展开成叶子过滤）。
    children: n.children?.length ? toTreeSelectData(n.children) : undefined,
  }));
}

/**
 * 对比 preview 单侧画布：单张图独立缩放（滚轮）+ 拖拽平移；
 * 双击恢复 1× 居中。两侧实例独立 state，互不干扰，便于真"并排对比观看"。
 *
 * 不复用 antd Image preview：antd 的 PreviewGroup 是 carousel 形态（前后翻页），
 * 不能并排同屏；若加 css overlay hack 会反过来干扰它内部的缩放/拖拽逻辑。
 */
function ComparePreviewPane({
  url,
  label,
  taskName,
  emptyHint,
}: {
  url: string | undefined;
  label: string;
  taskName: string | null | undefined;
  emptyHint: string;
}) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [drag, setDrag] = useState<{ x: number; y: number; tx0: number; ty0: number } | null>(null);

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    setScale((s) => Math.min(8, Math.max(0.2, s * delta)));
  };
  const onDoubleClick = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDrag({ x: e.clientX, y: e.clientY, tx0: tx, ty0: ty });
  };
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!drag) return;
    setTx(drag.tx0 + (e.clientX - drag.x));
    setTy(drag.ty0 + (e.clientY - drag.y));
  };
  const onMouseUp = () => setDrag(null);

  return (
    <div
      style={{
        flex: 1,
        background: '#0a0a0a',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: '1px solid #1f1f1f',
      }}
    >
      {/* 顶部栏：当前任务/对比任务 + scene name */}
      <div
        style={{
          padding: '6px 12px',
          color: '#e5e7eb',
          fontSize: 12,
          background: 'rgba(255,255,255,0.06)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <span style={{ fontWeight: 600, color: '#fff' }}>{label}</span>
        <span style={{ color: '#9ca3af' }}>{taskName || '-'}</span>
        <span style={{ marginLeft: 'auto', color: '#6b7280' }}>{Math.round(scale * 100)}%</span>
      </div>
      {/* 画布区 */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: drag ? 'grabbing' : url ? 'grab' : 'default',
          userSelect: 'none',
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onDoubleClick={onDoubleClick}
      >
        {url ? (
          <img
            src={url}
            alt={label}
            draggable={false}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) translate(${tx}px, ${ty}px) scale(${scale})`,
              transformOrigin: 'center center',
              maxWidth: 'none',
              maxHeight: 'none',
              transition: drag ? 'none' : 'transform 0.05s linear',
            }}
          />
        ) : (
          <div
            style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#6b7280', fontSize: 14,
            }}
          >
            {emptyHint}
          </div>
        )}
      </div>
    </div>
  );
}

export default function JobTaskListPage() {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { onTrace, modal: versionTraceModal } = useVersionTrace();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = jobIdParam ? Number(jobIdParam) : 0;

  /**
   * 「返回 Job 列表」：优先 navigate(-1) 回到来源列表页（可恢复其筛选/分页）；
   * 但在新标签页 / 直接输入 URL 打开时，当前页是该标签历史栈的首个条目
   * （location.key === 'default'），navigate(-1) 无处可去 → 点了没反应。
   * 此时兜底跳到任务列表路由（JobList 默认渲染 planning）。
   */
  const handleBackToJobList = useCallback(() => {
    if (location.key === 'default') {
      navigate('/task-list');
    } else {
      navigate(-1);
    }
  }, [location.key, navigate]);

  // 评测器中文名映射改由后端提供：组件挂载时拉取，加载完成后自动重渲染本页展示。
  useEvaluatorNames();

  /**
   * 从 URL query 解析 Daily 仿真差异筛选参数：
   *   ?compareJobId=123&diffMode=new_success
   * 由 Daily 仿真页面 SummaryCard 的「新增成功 / 新增失败」点击跳转携带。
   */
  const diffFilter = useMemo<{
    compareJobId?: number;
    diffMode?: TaskDiffMode;
  }>(() => {
    const cmp = searchParams.get('compareJobId');
    const mode = searchParams.get('diffMode');
    // 与后端 daily_service._ALL_DIFF_MODES 对齐：旧 new_*（带单边）+ 新 4 矩阵（仅交集）
    const VALID_MODES: ReadonlySet<string> = new Set([
      'new_success', 'new_fail',
      'pass_pass', 'pass_fail', 'fail_pass', 'fail_fail',
    ]);
    const validMode: TaskDiffMode | undefined =
      mode && VALID_MODES.has(mode) ? (mode as TaskDiffMode) : undefined;
    return {
      compareJobId: cmp ? Number(cmp) : undefined,
      diffMode: validMode,
    };
  }, [searchParams]);

  // ?mode=compare 时进入「任务对比」独立页：固定基准任务（当前 jobId），
  // 在页面顶部选择对比任务（同 simType）。原有 Modal 形态仍然保留以兼容旧调用方。
  const compareMode = searchParams.get('mode') === 'compare';

  // ?mode=stats 时进入「单 Job 统计」视图：在普通 task 列表页基础上额外展示
  // 「按评测器聚合明细」，并默认按 ?taskStatus= 预筛选（由 Job 列表「不通过」下钻携带，
  // taskStatus=20 对应 not_pass）。普通入口（无 mode）行为不变。
  const statsMode = searchParams.get('mode') === 'stats';
  const statsTaskStatus = useMemo<number | undefined>(() => {
    const raw = searchParams.get('taskStatus');
    if (raw == null || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }, [searchParams]);

  const { message } = App.useApp();
  const { hasPerm } = useCurrentUser();
  const canManageSecurity = hasPerm('data:confidential:manage');

  const [form] = Form.useForm();
  const [data, setData] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);

  // 「基于当前查询发起任务」入口:FilteredStatRow 右侧按钮 → confirm 弹窗(核对
  // filter 快照)→ 用户点「下一步」→ 打开 CreateJobModal,场景池锁定为当前
  // queryParams 快照。confirmOpen 控制 confirm 弹窗,rerunCreateOpen 控制
  // CreateJobModal;两者用同一份 rerunFilters 快照。
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  const [rerunCreateOpen, setRerunCreateOpen] = useState(false);
  // 打开 confirm 时冻结当时的 queryParams —— 之后用户改列表 filter 不影响已在弹窗
  // 里核对/提交的那份。confirm→CreateJobModal 也用这份,保证语义一致。
  const [rerunFilters, setRerunFilters] = useState<TaskListParams | null>(null);
  const [evaluatorNameOptions, setEvaluatorNameOptions] = useState<string[]>([]);
  const [evaluatorNameLoading, setEvaluatorNameLoading] = useState(false);
  const [respEventNameOptions, setRespEventNameOptions] = useState<string[]>([]);
  const [respEventNameLoading, setRespEventNameLoading] = useState(false);
  const [checkNameOptions, setCheckNameOptions] = useState<string[]>([]);
  const [checkNameLoading, setCheckNameLoading] = useState(false);
  const [sceneLabelOptions, setSceneLabelOptions] = useState<string[]>([]);
  const [sceneLabelLoading, setSceneLabelLoading] = useState(false);
  const [sceneTreeNodes, setSceneTreeNodes] = useState<JobSceneTreeNodeOption[]>([]);
  const [sceneTreeLoading, setSceneTreeLoading] = useState(false);
  const [queryParams, setQueryParams] = useState<TaskListParams>({
    jobId,
    current: 1,
    pageSize: 10,
  });
  // 评论筛选(关键词 + scope + 有/无)三档合并到一个受控 state,由 CommentFilterPopover 消费
  const [commentFilter, setCommentFilter] = useState<CommentFilterValue>(EMPTY_COMMENT_FILTER);

  // Task 日志 Modal：状态 + 拉取 + 渲染已提到 @components/TaskLogModal（感知报表页共用）
  const { openTaskLog, modal: taskLogModalEl } = useTaskLogModal();

  const [evaluatorPopoverTaskId, setEvaluatorPopoverTaskId] = useState<number | null>(null);
  const [evaluatorListData, setEvaluatorListData] = useState<EvaluatorItem[]>([]);
  const [evaluatorListLoading, setEvaluatorListLoading] = useState(false);
  const [evaluatorCache, setEvaluatorCache] = useState<Record<number, EvaluatorItem[]>>({});
  // 「仿真结果」列单个 checker 明细弹层：当前打开的 `${taskId}:${checkerName}`，及正在拉明细的 task
  const [checkerPopoverKey, setCheckerPopoverKey] = useState<string | null>(null);
  const [checkerDetailLoadingTaskId, setCheckerDetailLoadingTaskId] = useState<number | null>(null);
  const [previewUrlMap, setPreviewUrlMap] = useState<Record<number, string>>({});
  const [previewLoading, setPreviewLoading] = useState(false);

  const [taskCount, setTaskCount] = useState<JobTaskCountData | null>(null);
  const [taskCountLoading, setTaskCountLoading] = useState(false);
  // 「筛选后统计」：查询区下方单独一行，跟随当前筛选条件展示 场景数/通过/不通过/异常。
  // 仅在 查询/重置/评论筛选/评测器格子联动 等「非翻页」查询后刷新；null 时整行不渲染。
  const [filteredCount, setFilteredCount] = useState<FilteredTaskCountData | null>(null);
  const [filteredCountLoading, setFilteredCountLoading] = useState(false);
  // 「忽略 check 不通过场景」开关：值由 taskCount.excludeCheckFailed 同步；
  // 切换时写后端 + 重新请求 count（让总览/对比一并刷新）。
  const [excludeCheckToggling, setExcludeCheckToggling] = useState(false);

  // 「按评测器聚合明细」（statsMode 专用）：单 Job 各评测器的场景数 / 占比 / 通过 / 不通过
  const [evaluatorStats, setEvaluatorStats] = useState<JobEvaluatorStatData | null>(null);
  const [evaluatorStatsLoading, setEvaluatorStatsLoading] = useState(false);
  // 「按评测器聚合明细」pass/fail 单元格联动筛选选中态（statsMode 专用）
  const [evalCellFilter, setEvalCellFilter] = useState<{ name: string; verdict: 'pass' | 'fail' | 'error' } | null>(null);

  // ── statsMode 专用「指标」列（单 Job 看参数）──
  // 与对比页的 compareMetricDefs 分开存：那边是「两 Job 共有场景」口径，这边是
  // 单 Job 口径（同一个 evaluator 在单/双 Job 下能报出的指标集可能不同）。
  const [statsMetricDefs, setStatsMetricDefs] = useState<EvaluatorMetricDef[]>([]);
  const [statsMetricDefsLoading, setStatsMetricDefsLoading] = useState(false);
  // stats 页「失败原因 / 豁免原因」两个下拉的候选：按 (job, 评估项) 拉该评估器**实际上报过**的原因，
  // 前端按 kind 分两组。评估器自描述、无注册表；没上报任何原因 → 两个下拉都置灰（当普通场景）。
  const [statsReasonDefs, setStatsReasonDefs] = useState<JobEvaluatorReason[]>([]);
  const [statsReasonDefsLoading, setStatsReasonDefsLoading] = useState(false);
  // 以**已查询**的状态（queryParams，与列表同源）为准，而不是表单里还没提交的值：
  // 下拉可选与否必须与当前列表里看到的行一致。
  const statsReasonScope: 'fail' | 'exempt' | undefined =
    REASON_KIND_BY_EVAL_STATUS[queryParams.evaluatorStatus ?? ''];
  const statsFailReasons = useMemo(() => statsReasonDefs.filter((r) => r.kind === 'fail'), [statsReasonDefs]);
  const statsExemptReasons = useMemo(() => statsReasonDefs.filter((r) => r.kind === 'exempt'), [statsReasonDefs]);
  // 选中指标后**一次性**拉整 Job 的 {sceneId: value}，表格按 record.sceneId 查表渲染。
  // 这一点是本列的性能前提：若改成逐行/逐页请求，翻页会变成 N 次 HTTP 往返；
  // 整 Job 一次取回后，翻页、改列宽、拖拽都不再产生请求。底层 reader 有请求内缓存，
  // 与服务端已有的 metrics 路径共用同一份 evaluate_info，不会重复 detoast。
  const [statsMetricValueMap, setStatsMetricValueMap] = useState<Record<number, number>>({});
  const [statsMetricValuesLoading, setStatsMetricValuesLoading] = useState(false);

  // 「评估器参数」聚合卡片（statsMode 专用）：iLQR / stop_sign 等纯统计型评估器
  // 恒定只出 success，价值全在结构化 metrics。这些评估器由系统配置里的 agg-spec 登记，
  // 从上面 pass/fail 表拆出、单独在此卡片里点选查看跨场景聚合 KPI + 逐场景明细。
  const [metricAggSpecs, setMetricAggSpecs] = useState<EvaluatorMetricAggSpec[]>([]);
  // 卡片点选器当前选中的评估器（null = 未选，提示引导）。
  const [selectedMetricEvaluator, setSelectedMetricEvaluator] = useState<string | null>(null);
  const [metricAggData, setMetricAggData] = useState<JobEvaluatorMetricAgg | null>(null);
  const [metricAggLoading, setMetricAggLoading] = useState(false);
  // 逐场景明细默认收起，点击「展开明细」才渲染 Table（场景多时避免一进页面就撑慢）。
  const [metricAggDetailOpen, setMetricAggDetailOpen] = useState(false);
  // 对比页「评估器参数对比」：选中评估器在两 Job 上的聚合 KPI 当前/对比/差值。
  const [compareMetricAgg, setCompareMetricAgg] = useState<CompareEvaluatorMetricAgg | null>(null);
  const [compareMetricAggLoading, setCompareMetricAggLoading] = useState(false);
  const [compareMetricEvaluator, setCompareMetricEvaluator] = useState<string | null>(null);

  // 「不通过统计」独立页基本信息卡（statsMode 专用）：Job 名称 / 任务类型 / 创建人 / 时间 / 算法版本
  const [jobBasicInfo, setJobBasicInfo] = useState<JobDetailData | null>(null);
  // 页头「优先级」可编辑:点击弹 UpdatePriorityModal;仅 status ∈ {3,5,7} 时可点。
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [jobBasicInfoLoading, setJobBasicInfoLoading] = useState(false);

  // Job 列表项元信息（含 status / buildType），供标题栏「状态」与「编译日志」按钮使用。
  const [jobMeta, setJobMeta] = useState<JobItem | null>(null);
  // 对比任务的元信息（对比模式专用），与 jobMeta 对称。二者在进对比页时一次
  // getJobList({ jobIds:[jobId, compareId] }) 并排拉全，供「对比双方基本信息」
  // 卡片的算法版本单元格（含分支/commit 弹框）与顶部对比框首屏即完整渲染，
  // 不再依赖只有打开对比选择弹层才 populate 的 compareJobList.find。
  const [compareJobMeta, setCompareJobMeta] = useState<JobItem | null>(null);
  // 编译日志弹窗：compile_platform 走弹窗展示日志内容；package_bazel 直接新开标签页。
  const [buildLogVisible, setBuildLogVisible] = useState(false);
  const [buildLogContent, setBuildLogContent] = useState('');
  const [buildLogUrl, setBuildLogUrl] = useState<string | null>(null);
  const [buildLogLoading, setBuildLogLoading] = useState(false);

  // 「按场景分类」抽屉状态：懒加载，仅首次展开请求；切换 jobId 自动清缓存
  const [labelBreakdownOpen, setLabelBreakdownOpen] = useState(false);
  const [labelBreakdown, setLabelBreakdown] = useState<JobTaskCountByLabelItem[] | null>(null);
  const [labelBreakdownLoading, setLabelBreakdownLoading] = useState(false);

  // 任务对比相关状态
  const [compareSelectVisible, setCompareSelectVisible] = useState(false);
  const [compareResultVisible, setCompareResultVisible] = useState(false);
  const [compareJobList, setCompareJobList] = useState<JobItem[]>([]);
  const [compareJobListLoading, setCompareJobListLoading] = useState(false);
  // 初始对比任务取自 URL（分享链接 ?mode=compare&compareJobId=...），保证对方打开看到同一对比；
  // 缺省时由 handleOpenCompareSelect 兜底挑默认对比任务。
  const [selectedCompareJobId, setSelectedCompareJobId] = useState<number | null>(() => {
    if (searchParams.get('mode') !== 'compare') return null;
    const cmp = searchParams.get('compareJobId');
    return cmp && !Number.isNaN(Number(cmp)) ? Number(cmp) : null;
  });
  const [compareLoading, setCompareLoading] = useState(false);
  /**
   * 对比页筛选条件的 URL 初值,只在挂载时读一次(lazy init)。
   *
   * 下面那批 compare* state 用它做初值,使 ?mode=compare&c_diffMode=... 这类
   * 分享链接打开即复现同一筛选。之后 URL 由 syncCompareFiltersToUrl 单向回写,
   * 不再反向驱动 state —— 否则每次回写都会重新触发一轮读取。
   */
  const compareUrlInit = useRef<CompareFilterState | null>(null);
  if (compareUrlInit.current === null) {
    compareUrlInit.current = readCompareFiltersFromParams(searchParams);
  }
  /**
   * 首次 handleCompareConfirm 是否应保留 URL 带来的筛选。
   *
   * handleCompareConfirm 在选定对比任务后会把所有筛选清空(正常语义:换了对比
   * 对象,旧筛选作废)。但挂载时它由 [compareMode, selectedCompareJobId] effect
   * 自动触发一次 —— 若不豁免,刚从 URL 还原的筛选会被这次自动调用立刻清掉。
   * 故这里放一张一次性「免清票」,首次消费后置 false,之后换对比任务照常清空。
   */
  const keepUrlCompareFiltersRef = useRef(true);
  const [comparePreviewUrlMap, setComparePreviewUrlMap] = useState<Record<number, string>>({});
  const [comparePreviewDone, setComparePreviewDone] = useState(false);
  const [compareEvaluatorCache, setCompareEvaluatorCache] = useState<Record<number, EvaluatorItem[]>>({});

  interface CompareRow {
    key: string;
    sceneName: string;
    sceneType: string;
    /** 关联 scene_inspection.api_code（Jira Issue Key / Case-Flow case id，按 source 区分） */
    apiCode: string | null;
    /** 场景来源（scene_inspection.source），决定 apiCode 跳转目标 */
    source: string | null;
    /** case 场景区域, 决定 case-flow apiCode 跳的 waypulse 域名 */
    region: string | null;
    currentTask: TaskListItem | null;
    compareTask: TaskListItem | null;
    onlyInCurrent: boolean;
  }
  // 对比 preview 放大 modal：null=关闭，非空时弹双图全屏 lightbox（左 currentTask / 右 compareTask）
  const [compareLightboxRow, setCompareLightboxRow] = useState<CompareRow | null>(null);
  // 任务列表评论 Drawer 上下文：null=关闭
  const [commentTarget, setCommentTarget] = useState<{
    sceneId: number; sceneName: string | null; jobId: number; taskId: number;
  } | null>(null);
  // 操作 Popover 受控开关（按 taskId 区分）；点击「评论」前先收掉，避免遮挡 Drawer
  const [actionPopoverTaskId, setActionPopoverTaskId] = useState<number | null>(null);
  const [compareData, setCompareData] = useState<CompareRow[]>([]);
  const [compareTotal, setCompareTotal] = useState(0);
  // compareMode「筛选后统计」:跟 filteredCount 同口径,只是走对比页专属 filter。
  // loadComparePage 成功后触发一次,给「基于当前查询发起任务」按钮展示场景数。
  const [compareFilteredCount, setCompareFilteredCount] = useState<FilteredTaskCountData | null>(null);
  const [compareFilteredCountLoading, setCompareFilteredCountLoading] = useState(false);
  const [comparePage, setComparePage] = useState(compareUrlInit.current.page);
  const [comparePageSize, setComparePageSize] = useState(compareUrlInit.current.pageSize);
  const [compareJobName, setCompareJobName] = useState('');
  const [currentJobName, setCurrentJobName] = useState('');
  const [compareFilterSceneSetId, setCompareFilterSceneSetId] = useState<number | undefined>(compareUrlInit.current.compareSceneSetId);
  const compareDetailRef = useRef<HTMLDivElement>(null);
  const compareLoadRequestRef = useRef(0);
  const [compareFilterSceneName, setCompareFilterSceneName] = useState<string | undefined>(compareUrlInit.current.sceneName);
  const [compareFilterStatus, setCompareFilterStatus] = useState<string | number | undefined>(compareUrlInit.current.status);
  // 评论筛选 —— 与普通视图共用 <CommentFilterPopover>,一个按钮承载
  // (评论关键词 + scope + 有/无评论)三档;value 落到 loadComparePage.filters。
  const [compareCommentFilter, setCompareCommentFilter] = useState<CommentFilterValue>(compareUrlInit.current.comment);
  // 对比任务侧的同款筛选器,口径落在 compareJobId 上。两侧的组合方式由 commentCombine
  // 决定:'and'(默认)如「当前=有评论 + 对比=无评论」= 本次新增了评论的场景;
  // 'or' 如「两侧都=有评论」= 任一边有评论的全部场景。
  const [compareSideCommentFilter, setCompareSideCommentFilter] = useState<CommentFilterValue>(compareUrlInit.current.compareSideComment);
  // 仅在两侧都设了条件时才有意义,故 UI 上也只在那时显示「且/或」开关。
  const [commentCombine, setCommentCombine] = useState<'and' | 'or'>(compareUrlInit.current.commentCombine);
  const [compareDiffMode, setCompareDiffMode] = useState<TaskDiffMode | undefined>(compareUrlInit.current.diffMode);
  // 对比明细「差异」列筛选：undefined=全部 / 'same'=一致 / 'diff'=不一致
  const [compareDiffConsistency, setCompareDiffConsistency] = useState<CompareConsistency | undefined>(compareUrlInit.current.diffConsistency);
  /** 行为对比标签筛选（NEEDS_REVIEW / SIGNIFICANT / SLIGHT / CONSISTENT / INCONCLUSIVE / MISMATCH）：
   *  与翻转象限、「差异」列正交取交集，由后端 behavior_compare_scene 产出场景集。 */
  /** 行为结论列用的 {sceneId: {label, mismatch}}，进入对比 / 换对比任务时一次性拉取；
   *  与「行为」筛选读同一张报告表，列上标什么、筛出来的就是什么。无报告为空对象。 */
  const [compareBehaviorMap, setCompareBehaviorMap] = useState<BehaviorSceneLabelMap>({});
  /** 行为对比「强事件」筛选（六类强事件 code 或 ANY）：由总览里的强事件标签驱动，与「行为」「翻转象限」等正交取交集。 */
  /** 点原表「行为」标签要打开的场景（抽屉在行为对比区块里，经 props 传入）。 */
  const [behaviorDetailSceneId, setBehaviorDetailSceneId] = useState<number | null>(null);
  const [compareBehaviorStrong, setCompareBehaviorStrong] = useState<string | undefined>(compareUrlInit.current.behaviorStrong);
  const [compareBehaviorLabel, setCompareBehaviorLabel] = useState<BehaviorFilterValue | undefined>(compareUrlInit.current.behaviorLabel);
  // 整场景一致性判定缓存 {sceneId: same/diff/only_current}，对比确认后一次性拉取，
  // 「差异」列与筛选同源读取，去掉对逐 task 评测明细缓存的依赖。
  const [compareConsistencyMap, setCompareConsistencyMap] = useState<Record<number, CompareConsistencyVerdict>>({});
  /**
   * 「原因」列筛选（fail 失败原因 / pass 豁免原因）：undefined=全部 / same / diff / only_current。
   *
   * 须先选评测器 —— **原因是评测器维度的**（每个评测器报自己的失败/豁免原因），
   * 没有评测器就无从谈「原因是否不同」，故换评测器 / 清评测器时一律清空。
   */
  const [compareFilterReasonDiff, setCompareFilterReasonDiff] = useState<ReasonDiff | undefined>(compareUrlInit.current.reasonDiff);
  /**
   * 「原因」筛选的**类别**维度：只留**当前侧**该评测器 `reason.code` 等于此值的场景。
   *
   * 与 `compareFilterReasonDiff` 在 UI 上是**同一个下拉**的两种取值（选中即互斥），
   * 故状态分成两个：判定桶（same/diff/only_current）走 `c_reasonDiff`，类别 code 走
   * `c_reasonCode` —— 后端也是两个正交参数（交集），拆开存才能把 URL 还原成原样。
   *
   * 取当前侧而不取对比侧：同一 code 若两次出现在不同侧，含义就不定了。对比侧的原因
   * 照常在「原因」列并列展示，但不参与筛选（详见 daily_service.reason_code_scene_ids）。
   *
   * 类别清单**不在前端写死**：从 `reasonDiffMap` 的 today 侧汇总（评估器自描述上报），
   * 故评估器新增一类失败原因时前端零改动。
   */
  const [compareFilterReasonCode, setCompareFilterReasonCode] = useState<string | undefined>(compareUrlInit.current.reasonCode);
  /**
   * 「原因」列数据 `{sceneId: {verdict, today, compare}}`，选中评测器后一次性拉整个 Job。
   *
   * 与 `/task/list?reasonDiff=` 的筛选**同源**（后端共用 `_reason_scene_map`），故「列上
   * 标着的判定」与「筛出来的行」恒等。翻页不再补请求。
   *
   * 算法侧尚未上报 `reason` 时，后端给每侧 `{fail: [], exempt: []}`、判定 same —— 据此整列不渲染
   * （见 reasonColumnActive），不会挂出一列空白。
   */
  const [reasonDiffMap, setReasonDiffMap] = useState<Record<number, ReasonDiffCell>>({});
  const [reasonDiffLoading, setReasonDiffLoading] = useState(false);
  /**
   * 「原因」筛选下拉里的类别清单：从 `reasonDiffMap` 的 **today 侧**汇总，按 kind 分两组。
   *
   * 三个刻意的选择：
   * - **不从后端单独拉**：`/daily/reason-diff` 已经把两侧 `{fail: [...], exempt: [...]}`
   *   统计（含 `{kind, code, label, count, time, position}`）都带回来了（判定源唯一），
   *   再开一个「有哪些 code」的端点就是同一份数据算第二遍。
   * - **取 today 侧**：与 `reasonCode` 筛选口径一致（见 daily_service.reason_code_scene_ids）
   *   —— 下拉里给的选项必须与选中后筛出的行一一对应，否则会出现「选了个选项却筛出 0 行」。
   * - **按桶分组**：`fail` 桶进失败组、`exempt` 桶进豁免组；`code=null` 的失败行跳过
   *   （只计数，无法按 code 筛选）。
   *
   * 评估器自描述，前端零注册表：算法侧新增一类失败原因，这里自动出现。
   */
  const reasonCatalog = useMemo(() => {
    const fail = new Map<string, string>();
    const exempt = new Map<string, string>();
    Object.values(reasonDiffMap).forEach((cell) => {
      const stats = cell?.today;
      if (!stats) return;
      (['fail', 'exempt'] as const).forEach((kind) => {
        const bucket = kind === 'exempt' ? exempt : fail;
        // code=null（算法侧未上报 reason 的失败行）只计数，不进下拉：无法回传 code 筛选
        stats[kind].forEach((r) => {
          if (!r.code) return;
          if (!bucket.has(r.code)) bucket.set(r.code, r.label || r.code);
        });
      });
    });
    const dump = (m: Map<string, string>) =>
      Array.from(m, ([code, label]) => ({ code, label }));
    return { fail: dump(fail), exempt: dump(exempt) };
  }, [reasonDiffMap]);
  /**
   * 该 diffMode 下「原因」下拉应挂哪一类类别（undefined = 两类都挂，见 REASON_KIND_BY_DIFF_MODE）。
   */
  const reasonKindScope: 'fail' | 'exempt' | undefined =
    REASON_KIND_BY_DIFF_MODE[compareDiffMode ?? ''];
  /**
   * 换象限时，若已选的原因类别不属于新象限允许的那一类，就该清掉 ——
   * 否则会留下一个「下拉里已经看不到、但仍在后端生效」的隐形筛选，筛得列表空空的
   * 却找不到是谁在筛。
   *
   * **判据只用「它明确出现在另一类里」**：清单尚未加载（reasonDiffMap 还空着，比如从
   * URL 恢复 `c_reasonCode` 的首屏）时两侧都查不到，此时保守保留 —— 宁可留一个暂时
   * 看不出类别的条件，也不能把用户链接里带的筛选条件擅自丢掉。
   */
  /**
   * 「原因」下拉的选项：**判定桶**一组 + **原因类别**一组（按象限只挂其中一类）。
   *
   * 分组的用意就是「表述明确」：一个扁平列表里混着「一致 / 不一致 / 黄灯驶入」，
   * 用户没法判断「黄灯驶入」到底是筛两侧一致还是筛某一侧；组头写清「当前任务 ·
   * 失败原因」，语义就落在字面上了 —— 筛的是**当前任务这一侧**上报的那类原因。
   *
   * 类别组为空（该评测器这一象限没上报过任何该类原因）时**整组不渲染**，不留一个
   * 点开是空的组头。
   */
  const reasonFilterOptions = useMemo(() => {
    const groups: { label: string; options: { value: string; label: string }[] }[] = [
      {
        label: '两侧比对',
        options: [
          { value: 'same', label: '一致' },
          { value: 'diff', label: '不一致' },
          // code、次数都相同，但发生点的位置/时间在阈值内配不上（如 2 对 2 却发生在不同路口）
          { value: 'diff_spatial', label: '原因同·时空异' },
          { value: 'only_current', label: '仅当前' },
        ],
      },
    ];
    const codeGroup = (kind: 'fail' | 'exempt') => ({
      label: kind === 'fail' ? '当前任务 · 失败原因' : '当前任务 · 豁免原因',
      options: (kind === 'fail' ? reasonCatalog.fail : reasonCatalog.exempt)
        .map((o) => ({ value: `${REASON_CODE_VALUE_PREFIX}${o.code}`, label: o.label })),
    });
    if (reasonKindScope === 'fail') {
      if (reasonCatalog.fail.length) groups.push(codeGroup('fail'));
    } else if (reasonKindScope === 'exempt') {
      if (reasonCatalog.exempt.length) groups.push(codeGroup('exempt'));
    } else {
      // 未限定象限（未选矩阵 / 选评测器后的 'all'）：行集横跨通过与未通过，两组都给。
      if (reasonCatalog.fail.length) groups.push(codeGroup('fail'));
      if (reasonCatalog.exempt.length) groups.push(codeGroup('exempt'));
    }
    return groups;
  }, [reasonCatalog, reasonKindScope]);
  /** 该评测器**真的上报过**原因（任一类）—— 没有就整条原因筛选不可用（算法侧未落地）。 */
  const reasonFilterHasData = reasonCatalog.fail.length > 0 || reasonCatalog.exempt.length > 0;

  const reasonCodeSurvivesMode = useCallback(
    (mode: TaskDiffMode | undefined, code?: string): boolean => {
      if (!code) return true;
      const scope: 'fail' | 'exempt' | undefined = REASON_KIND_BY_DIFF_MODE[mode ?? ''];
      if (!scope) return true; // 未限定象限 → 两类都展示，无需清
      const inScopePool = scope === 'fail' ? reasonCatalog.fail : reasonCatalog.exempt;
      const otherPool = scope === 'fail' ? reasonCatalog.exempt : reasonCatalog.fail;
      return inScopePool.some((o) => o.code === code)
        || !otherPool.some((o) => o.code === code);
    },
    [reasonCatalog],
  );
  // 「按评测器聚合的 2×2 矩阵」联动筛选下方明细用：选中的 evaluator_name
  const [compareFilterEvaluatorName, setCompareFilterEvaluatorName] = useState<string | undefined>(compareUrlInit.current.evaluatorName);
  /**
   * 「原因」下拉是否禁用。
   *
   * 三种情况：① 没选评测器（原因是评测器维度的）；② 选了但该评测器**一条原因都没上报**
   * —— 此时全部场景都判定为「两侧都无原因 = 一致」，给个能选却必然筛出 0 行的控件只会
   * 误导，按用户要求「当普通场景，不进行筛选」；③ **加载中不算**（`reasonDiffLoading`
   * 期间 map 还是空的，不能据此判死），否则每换一次评测器都会闪一下「未上报原因」。
   */
  const reasonFilterDisabled = !compareFilterEvaluatorName
    || (!reasonDiffLoading && !reasonFilterHasData);
  // 评测器量化指标下钻：选中的指标 key 与对比方向（increase/decrease）。
  // 须与 compareFilterEvaluatorName 联动（未选评测器时指标不可选）。选中后：
  // ①「差异项」列改展示该指标两侧数值 + 差值；② 跨分页按方向筛选场景（后端 metricKey/metricCompare）。
  const [compareFilterMetricKey, setCompareFilterMetricKey] = useState<string | undefined>(compareUrlInit.current.metricKey);
  const [compareFilterMetricCompare, setCompareFilterMetricCompare] = useState<MetricCompare | undefined>(compareUrlInit.current.metricCompare);
  /**
   * 指标列服务端排序方向（'desc' = 差值从大到小，见 MetricSort）。
   *
   * 与 `compareFilterMetricCompare` **正交**：那个是「按差值方向筛掉一批场景」，
   * 这个只改**行的先后**，行集不变。故只点表头排序、不选增大/缩小时也要能把
   * metricSort 送到后端（`buildCompareQueryParams` 里为此单开一个分支，否则
   * 会退回「metricKey 必须与 metricCompare 同传」的老条件而被静默吞掉）。
   *
   * undefined = 不排序，走列表默认序（status desc, task_id desc）—— 也是表头
   * 箭头循环到第三态（点两下之后）的样子。
   */
  const [compareFilterMetricSort, setCompareFilterMetricSort] = useState<MetricSort | undefined>(compareUrlInit.current.metricSort);
  // 当前选中评测器的量化指标清单（纯结构化，动态从后端 /daily/compare/evaluator-metrics 拉取）。
  // 评估器上报什么就有什么，前端不写死。选评测器时刷新；未选或该评测器无 metrics 时为空。
  const [compareMetricDefs, setCompareMetricDefs] = useState<EvaluatorMetricDef[]>([]);
  // 选中指标后立即拉取的「增大/缩小/持平」方向计数（两 Job 共有场景全集口径）。
  // 未选指标 / 拉取失败时为 null，不渲染计数 chip。点某个计数即按该方向筛选场景。
  const [metricDirCounts, setMetricDirCounts] = useState<MetricDirectionCounts | null>(null);
  // 从清单里按 key 找定义（拿 label / higherIsWorse），替代旧的写死注册表 getMetricDef。
  const findMetricDef = useCallback(
    (key?: string) => (key ? compareMetricDefs.find((m) => m.key === key) : undefined),
    [compareMetricDefs],
  );
  // 「按评测器聚合的 2×2 矩阵」数据 + loading（compareMode 专用）
  const [compareEvaluatorMatrix, setCompareEvaluatorMatrix] = useState<DailyCompareEvaluatorMatrix | null>(null);
  const [compareEvaluatorMatrixLoading, setCompareEvaluatorMatrixLoading] = useState(false);
  // 评测器矩阵默认仅展示前 N 行，超出折叠，点击「展开全部」放开
  const [evalMatrixExpanded, setEvalMatrixExpanded] = useState(false);
  const [compareSummary, setCompareSummary] = useState<DailySummary | null>(null);
  const [compareSummaryLoading, setCompareSummaryLoading] = useState(false);
  // 「任务对比」独立页要展示基本信息 + 4 矩阵 + 里程，原 compareSummary 已被裁剪为
  // DailySummaryCard 子集（仅 passRate/anomalyRate/newSuccess/newFail）。这里另存
  // 后端返回的完整 DailyCompare，给新模块直接读全字段。
  const [compareRaw, setCompareRaw] = useState<DailyCompare | null>(null);
  const sceneSetComparison = useSceneSetComparison(jobId, selectedCompareJobId, compareRaw);
  const compareSceneSetOptions = useMemo(() => sceneSetOptions(sceneSetComparison.nodes ?? []), [sceneSetComparison.nodes]);
  const taskDetailRef = useRef<HTMLDivElement>(null);
  // 切换 Job 时丢弃 URL 上残留的场景集下钻（首次挂载保留，用于分享链接复现）
  const sceneSetPrevJobIdRef = useRef<number | null>(null);
  // 对比页「双方基本信息」里当前任务/对比任务各自的「忽略 check 不通过场景」开关切换中
  // loading 态（镜像单 Job 页的 excludeCheckToggling，但对比页双方独立）。
  const [excludeCheckTogglingBase, setExcludeCheckTogglingBase] = useState(false);
  const [excludeCheckTogglingCompare, setExcludeCheckTogglingCompare] = useState(false);
  // compareMode 下需要把 scene_set_ids (CSV) 解析为「场景集合路径名」展示，
  // 复用 SceneManagement / JobList 的 getSceneTree(true) + findScenePath 逻辑。
  const [compareSceneTree, setCompareSceneTree] = useState<SceneTreeNode[]>([]);
  const compareJobIdRef = { current: selectedCompareJobId };

  // 当前页 task 的评论摘要（按 (sceneId, jobId=jobId) 维度查；评论列展示用）。
  // 对比模式下当前侧评论也走这个 hook：合并主 data 与对比明细里 currentTask.sceneId，
  // 后者覆盖"按差异筛选只显示子集"等场景。
  const taskSceneIds = useMemo(
    () => {
      const main = data.map((t) => t.sceneId);
      const curFromCompare = compareMode
        ? compareData.map((r) => r.currentTask?.sceneId ?? 0)
        : [];
      return Array.from(new Set([...main, ...curFromCompare].filter((x) => x > 0)));
    },
    [data, compareData, compareMode],
  );
  const { summary: commentSummary, refresh: refreshCommentSummary } = useSceneCommentSummary(
    taskSceneIds, jobId > 0 ? jobId : undefined,
  );
  // 对比任务侧评论摘要：仅在对比模式 + 选定 compareJobId 时拉，按 (sceneId, compareJobId) 维度。
  // 与当前侧评论分别归属各自 Job，互不串台。
  const compareSceneIdsForComment = useMemo(
    () => compareMode
      ? Array.from(new Set(
        compareData
          .map((r) => r.compareTask?.sceneId ?? r.currentTask?.sceneId ?? 0)
          .filter((x) => x > 0),
      ))
      : [],
    [compareData, compareMode],
  );
  const { summary: compareCommentSummary, refresh: refreshCompareCommentSummary } =
    useSceneCommentSummary(
      compareSceneIdsForComment,
      compareMode && selectedCompareJobId ? selectedCompareJobId : undefined,
    );

  const [exportLoading, setExportLoading] = useState(false);
  const [foxgloveLoadingTaskId, setFoxgloveLoadingTaskId] = useState<number | null>(null);

  const { columnWidths, setColumnWidths, resizeHeaderCell } = useResizableColumns({
    // 普通视图默认列宽 —— 第四轮:sceneName -40%,其他列再合计 -200px。
    // 用户可拖拽 header 边界个性化,不影响他人。
    taskId: 27,
    sceneName: 46,
    region: 40,
    apiCode: 32,
    taskTypeName: 56,
    statusName: 32,
    sceneType: 100,
    label: 42,
    createTime: 38,
    endTime: 38,
    // 仿真结果列下方逐个列出 checker 的加宽(40 → 90)见下方 isPerceptionJob effect
    evaluator: 40,
    // statsMode「指标」列（选中指标才出现）。宽度按最多 5 位数字估，
    // 但 key 必须**静态存在**于初始宽度表里 —— 否则 columnWidths.statsMetric
    // 是 undefined，拖拽与 scroll.x 求和都会塌掉。
    statsMetric: 56,
    preview: 80,
    foxglove: 34,
    comment: 36,
    action: 24,
    // ── 对比视图专用列宽(compare mode) ──
    // 与普通视图 key 不共享:普通/对比的展示密度不同,列宽独立最直观。
    // 按用户反馈默认调窄:场景名 200→150、差异项 190→140、Foxglove 120→80、评论 130→90。
    cmpSceneName: 120,
    cmpApiCode: 96,
    cmpDiff: 56,
    cmpBehavior: 84,
    cmpBehaviorDiffs: 240,
    cmpBehaviorCurve: 176,
    cmpBehaviorSummary: 160,
    cmpVruSpeedDiff: 108,
    // 「原因」列（选中评测器且该评测器上报了 reason 才出现）。两侧 label 并列，
    // 故与「差异项」同量级；同样**必须静态存在**于初始宽度表里，否则
    // columnWidths.cmpReason 是 undefined，拖拽与 scroll.x 求和都会塌掉。
    cmpReason: 170,
    // 当前/对比两侧共享同一组子列宽 —— 拖左侧「状态」也会同步右侧「状态」,
    // 与两侧同结构对齐的语义一致。
    cmpSideStatus: 44,
    cmpSideEval: 54,
    cmpSidePreview: 124,
    cmpSideFox: 50,
    cmpSideComment: 64,
    cmpSideAction: 44,
  });

  const loadStatusList = useCallback(async () => {
    try {
      const list = await getTaskStatusList();
      setStatusList(list);
    } catch (err) {
      console.error('加载状态列表失败:', err);
    }
  }, []);

  const loadEvaluatorNames = useCallback(async (id: number) => {
    if (!id || Number.isNaN(id)) return;
    setEvaluatorNameLoading(true);
    try {
      const names = await getEvaluatorNamesByJob(id);
      setEvaluatorNameOptions(names);
    } catch (err) {
      console.error('加载评测器名称列表失败:', err);
    } finally {
      setEvaluatorNameLoading(false);
    }
  }, []);

  // 「有无责事件」下拉候选：按 job 读 type=2 责任事件名（与 respEventName 精确匹配同口径）
  const loadRespEventNames = useCallback(async (id: number) => {
    if (!id || Number.isNaN(id)) return;
    setRespEventNameLoading(true);
    try {
      const names = await getRespEventNamesByJob(id);
      setRespEventNameOptions(names);
    } catch (err) {
      console.error('加载有无责事件名称列表失败:', err);
    } finally {
      setRespEventNameLoading(false);
    }
  }, []);

  // 「检查项」下拉候选：按 job 读 type=3 检查项名（与 checkName 精确匹配同口径）
  const loadCheckNames = useCallback(async (id: number) => {
    if (!id || Number.isNaN(id)) return;
    setCheckNameLoading(true);
    try {
      const names = await getCheckNamesByJob(id);
      setCheckNameOptions(names);
    } catch (err) {
      console.error('加载检查项名称列表失败:', err);
    } finally {
      setCheckNameLoading(false);
    }
  }, []);

  /** Job 下 scene_inspection.label 候选（用于「场景标签」筛选下拉）；
   *  末尾的 "__unlabeled__" 哨兵在 UI 显示为「未分类」，对应 NULL/空 label */
  const loadSceneLabels = useCallback(async (id: number) => {
    if (!id || Number.isNaN(id)) return;
    setSceneLabelLoading(true);
    try {
      const labels = await getJobSceneLabels(id);
      setSceneLabelOptions(labels);
    } catch (err) {
      console.error('加载场景标签列表失败:', err);
    } finally {
      setSceneLabelLoading(false);
    }
  }, []);

  const loadSceneTree = useCallback(async (id: number) => {
    if (!id || Number.isNaN(id)) return;
    setSceneTreeLoading(true);
    try {
      const nodes = await getJobSceneTreeNodes(id);
      setSceneTreeNodes(nodes);
    } catch (err) {
      console.error('加载场景集合失败:', err);
    } finally {
      setSceneTreeLoading(false);
    }
  }, []);

  // 竞态守卫：stats 页的评估项 / 指标 / 状态 / 原因下拉都是「选完即查」，连续快速切换时先发的
  // 请求可能后返回，不守卫就会出现「表单显示新评估项、列表是上一个评估项的数据」。只让最后一次
  // 发起的请求写 state，与 loadComparePage 的 compareLoadRequestRef 同款。
  const taskListRequestRef = useRef(0);
  const loadTaskList = useCallback(
    async (params: TaskListParams) => {
      const requestId = ++taskListRequestRef.current;
      setLoading(true);
      try {
        const response = await getTaskList(params);
        if (requestId !== taskListRequestRef.current) return;
        setData(response.tasks);
        setTotal(response.total);
      } catch (err) {
        if (requestId !== taskListRequestRef.current) return;
        console.error('加载Task列表失败:', err);
      } finally {
        // 过期请求不能把「后一次请求仍在加载」的 loading 提前关掉
        if (requestId === taskListRequestRef.current) setLoading(false);
      }
    },
    []
  );

  const loadTaskCount = useCallback(async (id: number) => {
    setTaskCountLoading(true);
    try {
      const count = await getJobTaskCount(id);
      setTaskCount(count);
    } catch (err) {
      console.error('加载Task数量统计失败:', err);
    } finally {
      setTaskCountLoading(false);
    }
  }, []);

  // 「筛选后统计」：与列表传同一份 params（后端 count_only 复用同一条过滤链），
  // 拿到 场景数/通过/不通过/异常。仅在非翻页查询后调用，翻页不刷新此行。
  // 同 taskListRequestRef：场景集下钻等快速连点时只让最后一次请求写 state，丢弃过期响应。
  const filteredCountGuardRef = useRef(createLatestRequestGuard());
  const loadFilteredCount = useCallback(async (params: TaskListParams) => {
    const guard = filteredCountGuardRef.current;
    const seq = guard.begin();
    setFilteredCountLoading(true);
    try {
      const count = await getFilteredTaskCount(params);
      if (!guard.isLatest(seq)) return;
      setFilteredCount(count);
    } catch (err) {
      if (!guard.isLatest(seq)) return;
      console.error('加载筛选后统计失败:', err);
    } finally {
      if (guard.isLatest(seq)) setFilteredCountLoading(false);
    }
  }, []);

  // statsMode 专用：加载单 Job「按评测器聚合明细」。失败不阻塞主流程。
  // 注意：必须定义在 handleToggleExcludeCheckFailed 之前——后者在函数体与依赖数组里
  // 引用了它，const 绑定存在 TDZ，若定义在后会触发 "Cannot access before initialization"。
  const loadEvaluatorStats = useCallback(async (id: number) => {
    setEvaluatorStatsLoading(true);
    try {
      const stats = await getJobEvaluatorStats(id);
      setEvaluatorStats(stats);
    } catch (err) {
      console.error('加载评测器明细统计失败:', err);
      setEvaluatorStats(null);
    } finally {
      setEvaluatorStatsLoading(false);
    }
  }, []);

  // statsMode 专用：加载「已登记聚合声明的评估器」清单（评估器参数卡片的点选候选，
  // 同时也是 pass/fail 表要排除的评估器集合）。失败不阻塞主流程、退化为空表。
  const loadMetricAggSpecs = useCallback(async () => {
    try {
      const specs = await getEvaluatorMetricAggSpecs();
      setMetricAggSpecs(specs);
    } catch (err) {
      console.error('加载评估器参数聚合声明失败:', err);
      setMetricAggSpecs([]);
    }
  }, []);

  // statsMode 专用：加载某评估器在当前 Job 上的跨场景聚合 KPI + 逐场景明细。
  // withScenes=false（默认）：只取 summary + charts——sum_keys 多时（如 uturn 40 键）逐场景
  // 明细是 payload 大头，首屏用不上就不搬；展开「逐场景明细」时才带 withScenes=true 重取。
  // seq 防竞态：慢的明细请求回来时若已有更新的请求，丢弃旧响应（旧数据会盖掉新评估器的结果）。
  const metricAggSeqRef = useRef(0);
  const loadMetricAgg = useCallback(async (id: number, evaluator: string, withScenes: boolean = false) => {
    const seq = ++metricAggSeqRef.current;
    setMetricAggLoading(true);
    try {
      const data = await getJobEvaluatorMetricAgg(id, evaluator, withScenes);
      if (seq === metricAggSeqRef.current) setMetricAggData(data);
    } catch (err) {
      console.error('加载评估器参数聚合明细失败:', err);
      if (seq === metricAggSeqRef.current) setMetricAggData(null);
    } finally {
      if (seq === metricAggSeqRef.current) setMetricAggLoading(false);
    }
  }, []);

  /**
   * 「指标」下拉的候选：按 (job, 评估项) 动态拉该评估器**实际上报过**的指标清单。
   *
   * 不写死注册表 —— 与对比页同哲学：评估器上报什么就有什么，包含普通通过型
   * 评估器（用户明确要求它们也要能看参数）。驱动源是 ``queryParams.evaluatorName``
   * 而非表单里的值：列可见性与本 effect 必须同源，否则会出现「列出来了但清单是
   * 上一个评估器的」。
   */
  useEffect(() => {
    if (!statsMode || !jobId || Number.isNaN(jobId)) return;
    const ev = queryParams.evaluatorName;
    if (!ev) {
      setStatsMetricDefs([]);
      return;
    }
    let cancelled = false;
    setStatsMetricDefsLoading(true);
    getJobEvaluatorMetrics(jobId, ev)
      .then((defs) => {
        if (cancelled) return;
        setStatsMetricDefs(defs);
        // 指标隶属于评估器：换了评估项后旧 metricKey 可能不在新清单里。此处就地剪掉，
        // 而不是只在某个 onChange 里清 —— 这样连 URL 分享链接带进来的失效组合
        // （?evaluatorName=A&metricKey=k1 但 A 不报 k1）也一并自愈，否则会出一列
        // 表头写着孤儿 key、单元格全是 `—` 的空白。
        const cur = queryParams.metricKey;
        if (cur && !defs.some((d) => d.key === cur)) {
          form.setFieldsValue({ metricKey: undefined });
          setQueryParams((prev) => ({ ...prev, metricKey: undefined, metricSort: undefined }));
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('metricKey');
            next.delete('metricSort');
            return next;
          }, { replace: true });
        }
      })
      .catch((err) => {
        console.error('加载指标清单失败:', err);
        if (!cancelled) setStatsMetricDefs([]);
      })
      .finally(() => { if (!cancelled) setStatsMetricDefsLoading(false); });
    return () => { cancelled = true; };
    // queryParams.metricKey 有意不进依赖：本 effect 是「评估项变了才重拉清单」，
    // 把 metricKey 放进来会让选指标也触发一次清单重拉（清单不变）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsMode, jobId, queryParams.evaluatorName]);

  /**
   * 「失败原因 / 豁免原因」两个下拉的候选：与上面「指标」同构，评估项变了才重拉。
   *
   * 原因隶属于评估器：换了评估项，旧的 reasonKind/reasonCode 可能不在新清单里。就地剪掉
   * （连 URL 分享链接带进来的失效组合一并自愈），否则会留一个看不见的筛选把行集筛成空。
   * 评估项清空 → 清单清空、筛选一并清。
   */
  useEffect(() => {
    if (!statsMode || !jobId || Number.isNaN(jobId)) return;
    const ev = queryParams.evaluatorName;
    const dropStale = () => {
      if (!queryParams.reasonKind && !queryParams.reasonCode) return;
      form.setFieldsValue({ reasonFailCode: undefined, reasonExemptCode: undefined });
      setQueryParams((prev) => ({ ...prev, reasonKind: undefined, reasonCode: undefined }));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('reasonKind');
        next.delete('reasonCode');
        return next;
      }, { replace: true });
    };
    if (!ev) {
      setStatsReasonDefs([]);
      dropStale();
      return;
    }
    let cancelled = false;
    setStatsReasonDefsLoading(true);
    getJobEvaluatorReasons(jobId, ev)
      .then((defs) => {
        if (cancelled) return;
        setStatsReasonDefs(defs);
        const code = queryParams.reasonCode;
        // 只剪「明确不在清单里」的：清单里有 → 保留并回填到对应下拉
        if (code && !defs.some((d) => d.code === code)) dropStale();
        else if (queryParams.reasonKind && !defs.some((d) => d.kind === queryParams.reasonKind)) dropStale();
      })
      .catch((err) => {
        console.error('加载原因清单失败:', err);
        if (!cancelled) setStatsReasonDefs([]);
      })
      .finally(() => { if (!cancelled) setStatsReasonDefsLoading(false); });
    return () => { cancelled = true; };
    // queryParams.reasonKind/reasonCode 有意不进依赖：本 effect 是「评估项变了才重拉清单」，
    // 选原因本身不该触发重拉（同上面「指标」那个 effect）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsMode, jobId, queryParams.evaluatorName]);

  /**
   * 「指标」选中项 → 整 Job 的 {sceneId: value}。
   *
   * 与指标清单一同样是「评估项 + 指标」双条件，缺任一个都把映射清空（列也随之消失）。
   * 结果按 sceneId 索引而不是 taskId：同一 job 内同一 scene 可能有多条 task，
   * 而指标是场景维度的评测结论，多条 task 共用同一值。
   */
  useEffect(() => {
    if (!statsMode || !jobId || Number.isNaN(jobId)) return;
    const ev = queryParams.evaluatorName;
    const mk = queryParams.metricKey;
    if (!ev || !mk) {
      setStatsMetricValueMap({});
      return;
    }
    let cancelled = false;
    setStatsMetricValuesLoading(true);
    getJobEvaluatorMetricValues(jobId, ev, mk)
      .then((m) => { if (!cancelled) setStatsMetricValueMap(m); })
      .catch((err) => {
        console.error('加载指标值失败:', err);
        if (!cancelled) setStatsMetricValueMap({});
      })
      .finally(() => { if (!cancelled) setStatsMetricValuesLoading(false); });
    return () => { cancelled = true; };
  }, [statsMode, jobId, queryParams.evaluatorName, queryParams.metricKey]);

  /**
   * 切换「忽略 check 不通过场景」开关。
   * 写后端成功后重新拉 taskCount 与（statsMode 下）评测器明细，让总览/通过率与
   * 「按评测器聚合明细」面板同步反映新口径；失败 Toast 提示，前端 state 不动。
   */
  const handleToggleExcludeCheckFailed = useCallback(
    async (next: boolean) => {
      if (!jobId) return;
      setExcludeCheckToggling(true);
      try {
        await setExcludeCheckFailed(Number(jobId), next);
        // 开关改变了「哪些场景计入」的口径，故三处都要按新口径重拉：
        //   - taskCount：总览/通过率计数
        //   - 评测器明细：仅 statsMode 渲染
        //   - task 列表：下方场景明细表格（后端按 job 级持久开关过滤，重拉即生效）
        // 三者并行，省往返。
        await Promise.all([
          loadTaskCount(Number(jobId)),
          loadTaskList(queryParams),
          statsMode ? loadEvaluatorStats(Number(jobId)) : Promise.resolve(),
        ]);
        message.success(next ? '已忽略 check 不通过场景' : '已恢复全部场景计入');
      } catch (err) {
        console.error('切换 exclude_check_failed 失败:', err);
        message.error('切换失败，请稍后重试');
      } finally {
        setExcludeCheckToggling(false);
      }
    },
    [jobId, statsMode, loadTaskCount, loadTaskList, loadEvaluatorStats, queryParams, message],
  );

  // statsMode 专用：加载 Job 基本信息（轻量，仅取元数据）。失败不阻塞主流程。
  const loadJobBasicInfo = useCallback(async (id: number) => {
    setJobBasicInfoLoading(true);
    try {
      const info = await getJobBasicInfo(id);
      setJobBasicInfo(info);
    } catch (err) {
      console.error('加载 Job 基本信息失败:', err);
      setJobBasicInfo(null);
    } finally {
      setJobBasicInfoLoading(false);
    }
  }, []);

  // 加载 Job 列表项（按 jobId 过滤取单条），拿到 status / buildType 等元信息。
  // /job/detail 不含 buildType，故标题栏「状态」「编译日志」走 /job/list 的 JobItem。
  const loadJobMeta = useCallback(async (id: number) => {
    try {
      const res = await getJobList({ jobId: id, current: 1, pageSize: 1 });
      setJobMeta(res.tasks.find((j) => j.jobId === id) ?? res.tasks[0] ?? null);
    } catch (err) {
      console.error('加载 Job 元信息失败:', err);
      setJobMeta(null);
    }
  }, []);

  /**
   * 对比模式：一次拉「当前 + 对比」双方完整 JobItem。
   *
   * 用后端新增的 jobIds 多值精确查（?jobIds=cur&jobIds=cmp），一个请求拿双方，
   * 分别灌进 jobMeta / compareJobMeta。这两个是「对比双方基本信息」卡片算法版本
   * 单元格（含分支/commit 弹框）与顶部对比框的**权威数据源**，首屏即完整，
   * 不再依赖只有打开对比选择弹层才 populate 的 compareJobList.find。
   * pageSize 给 2 足够（按 id 精确查，后端跳过 30 天时间窗）。
   */
  const loadCompareMetas = useCallback(async (curId: number, cmpId: number) => {
    try {
      const res = await getJobList({ jobIds: [curId, cmpId], current: 1, pageSize: 2 });
      const byId = new Map(res.tasks.map((j) => [j.jobId, j]));
      setJobMeta(byId.get(curId) ?? null);
      setCompareJobMeta(byId.get(cmpId) ?? null);
    } catch (err) {
      console.error('加载对比双方元信息失败:', err);
    }
  }, []);

  /**
   * 「按场景分类」抽屉懒加载：仅在展开且未缓存时请求。
   *
   * 切换 jobId / 调用 ``handleToggleLabelBreakdown`` 收起再展开 时强制 refetch；
   * 关闭抽屉不清缓存，再次展开秒开。
   */
  const loadLabelBreakdown = useCallback(async (id: number) => {
    setLabelBreakdownLoading(true);
    try {
      const data = await getJobTaskCountByLabel(id);
      setLabelBreakdown(data.items);
    } catch (err) {
      console.error('加载按场景分类统计失败:', err);
      setLabelBreakdown([]);
    } finally {
      setLabelBreakdownLoading(false);
    }
  }, []);

  const handleToggleLabelBreakdown = () => {
    const next = !labelBreakdownOpen;
    setLabelBreakdownOpen(next);
    if (next && !labelBreakdown && jobId && !Number.isNaN(jobId)) {
      loadLabelBreakdown(jobId);
    }
  };

  useEffect(() => {
    loadStatusList();
  }, [loadStatusList]);

  useEffect(() => {
    // 任务对比独立页不需要主表数据；这里直接跳过，避免无用请求与列表 loading 闪烁
    if (compareMode) return;
    if (jobId && !Number.isNaN(jobId)) {
      // 分页与筛选初值都从 URL 读,使分享链接 / 从场景编辑返回(navigate(-1))时
      // 恢复到离开前的查询条件与页码;缺省 1/10。compare 模式有各自 URL 同步。
      const { current: urlCurrent, pageSize: urlPageSize } = readPaginationFromParams(searchParams);
      const urlFilters = readTaskFiltersFromParams(searchParams);
      const jobSwitched = sceneSetPrevJobIdRef.current !== null && sceneSetPrevJobIdRef.current !== jobId;
      sceneSetPrevJobIdRef.current = jobId;
      if (jobSwitched || (diffFilter.compareJobId && diffFilter.diffMode)) {
        delete urlFilters.sceneSetId;
        delete urlFilters.sceneSetVerdict;
      }
      // statsMode 默认按 ?taskStatus= 预筛选（不通过下钻入口携带）；同步回填筛选表单。
      // 场景集判定下钻时不叠加（见 statsDefaultStatus）。
      const statsStatus = statsDefaultStatus(statsMode, statsTaskStatus, urlFilters);
      const statsStatusFilter = statsStatus !== undefined ? { status: statsStatus } : {};
      const urlCommentFilter = readCommentFilterFromParams(searchParams);
      const initialParams: TaskListParams = {
        jobId,
        current: urlCurrent,
        pageSize: urlPageSize,
        ...urlFilters,
        ...(diffFilter.compareJobId && diffFilter.diffMode
          ? { compareJobId: diffFilter.compareJobId, diffMode: diffFilter.diffMode }
          : {}),
        ...statsStatusFilter,
      };
      setQueryParams(initialParams);
      setCommentFilter(urlCommentFilter);
      // 回填筛选表单,让链接打开后「列表结果」与「表单上显示的条件」一致。
      // statsMode 的 ?taskStatus= 优先级更高,放在 URL filter 之后覆盖 status。
      form.setFieldsValue(pickFormValues(initialParams));
      // reasonKind + reasonCode 是请求参数，表单里是 reasonFailCode / reasonExemptCode 两个下拉：
      // 按 kind 落回对应下拉，链接打开后下拉显示的与列表实际筛选一致。
      if (initialParams.reasonCode) {
        form.setFieldsValue(initialParams.reasonKind === 'exempt'
          ? { reasonExemptCode: initialParams.reasonCode }
          : { reasonFailCode: initialParams.reasonCode });
      }
      if (statsStatus !== undefined) {
        form.setFieldsValue({ status: statsStatus });
      }
      loadTaskList(initialParams);
      loadTaskCount(jobId);
      loadJobMeta(jobId);
      loadEvaluatorNames(jobId);
      loadRespEventNames(jobId);
      loadCheckNames(jobId);
      loadSceneLabels(jobId);
      loadSceneTree(jobId);
      if (statsMode) {
        loadEvaluatorStats(jobId);
        loadJobBasicInfo(jobId);
        loadMetricAggSpecs();
      }
      // 切换 job 时丢弃按 label 分组缓存：避免抽屉再次展开时看到旧 job 数据
      setLabelBreakdown(null);
      setLabelBreakdownOpen(false);
    }
  }, [compareMode, statsMode, statsTaskStatus, jobId, diffFilter.compareJobId, diffFilter.diffMode, form, loadTaskList, loadTaskCount, loadJobMeta, loadEvaluatorNames, loadRespEventNames, loadCheckNames, loadSceneLabels, loadSceneTree, loadEvaluatorStats, loadJobBasicInfo, loadMetricAggSpecs]);

  // 「本 Job 实际跑过的」评估器参数 = 系统配置登记的候选 ∩ 本 Job evaluatorStats
  // 里出现过的评估器。登记 ≠ 这个 Job 真跑了：不做交集，空 Job 也会弹出一张全 0 的
  // 卡片（后端对没有数据的 sum_key 也回 0）。交集后一个都没有 → 整卡不渲染。
  const jobMetricAggSpecs = useMemo(() => {
    if (metricAggSpecs.length === 0) return [];
    const ran = new Set(
      (evaluatorStats?.evaluators ?? []).map((r) => canonicalEvaluatorName(r.evaluatorName)),
    );
    return metricAggSpecs.filter((s) => ran.has(canonicalEvaluatorName(s.evaluator)));
  }, [metricAggSpecs, evaluatorStats]);

  // 评估器参数卡片：拉到候选后确定选中项。优先 URL 上的 ?metricEvaluator=
  // （分享链接可带「看的是哪个评估器」），其次沿用当前选中，最后默认第一项。
  // 候选取「本 Job 实际跑过的」子集，避免选中一个本 Job 没数据的评估器。
  useEffect(() => {
    if (!statsMode) return;
    if (jobMetricAggSpecs.length === 0) {
      setSelectedMetricEvaluator(null);
      return;
    }
    setSelectedMetricEvaluator((prev) => {
      if (prev && jobMetricAggSpecs.some((s) => s.evaluator === prev)) return prev;
      const fromUrl = searchParams.get('metricEvaluator');
      if (fromUrl && jobMetricAggSpecs.some((s) => s.evaluator === fromUrl)) return fromUrl;
      return jobMetricAggSpecs[0].evaluator;
    });
  }, [statsMode, jobMetricAggSpecs, searchParams]);

  // 选中评估器变化 → 拉该评估器在当前 Job 上的聚合明细。
  useEffect(() => {
    setMetricAggDetailOpen(false); // 切评估器时收起上一份明细
    if (!statsMode || !jobId || !selectedMetricEvaluator) {
      setMetricAggData(null);
      return;
    }
    loadMetricAgg(Number(jobId), selectedMetricEvaluator);
  }, [statsMode, jobId, selectedMetricEvaluator, loadMetricAgg]);

  // 对比页：拉到候选后确定选中评估器（沿用当前选中，否则默认第一项）。
  useEffect(() => {
    if (!compareMode) return;
    if (metricAggSpecs.length === 0) {
      setCompareMetricEvaluator(null);
      return;
    }
    setCompareMetricEvaluator((prev) => {
      if (prev && metricAggSpecs.some((s) => s.evaluator === prev)) return prev;
      return metricAggSpecs[0].evaluator;
    });
  }, [compareMode, metricAggSpecs]);

  // 对比页：选中评估器 + 对比任务就绪 → 拉两 Job 聚合对比。
  useEffect(() => {
    if (!compareMode || !jobId || !selectedCompareJobId || !compareMetricEvaluator) {
      setCompareMetricAgg(null);
      return;
    }
    setCompareMetricAggLoading(true);
    compareJobEvaluatorMetricAgg(Number(jobId), Number(selectedCompareJobId), compareMetricEvaluator)
      .then((d) => setCompareMetricAgg(d))
      .catch((err) => {
        console.error('加载评估器参数聚合对比失败:', err);
        setCompareMetricAgg(null);
      })
      .finally(() => setCompareMetricAggLoading(false));
  }, [compareMode, jobId, selectedCompareJobId, compareMetricEvaluator]);

  /**
   * 把普通列表模式的查询条件回写到 URL(replace,不污染历史)。
   *
   * 每次改筛选/翻页都调一次 —— 地址栏始终是「当前所见」的可分享快照,用户
   * 直接复制地址栏即可把带条件的页面分享出去。
   * 用 setSearchParams 的函数式写法读最新 query,避免闭包里拿到过期的
   * searchParams 把别处(compareJobId / mode 等)刚写的参数覆盖掉。
   */
  const syncTaskFiltersToUrl = useCallback(
    (params: TaskListParams) => {
      setSearchParams((prev) => writeTaskFiltersToParams(prev, params), { replace: true });
    },
    [setSearchParams],
  );

  /** 关闭 Daily 差异筛选：移除 URL query，恢复普通查询。 */
  const clearDiffFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('compareJobId');
    next.delete('diffMode');
    setSearchParams(next, { replace: true });
  };

  /**
   * 查看编译日志（仅编译类 Job）：
   * - package_bazel：取编译平台回调里的 buildLog URL，新标签页打开；
   * - compile_platform：拉日志内容 + 下载链接，在弹窗内展示。
   */
  const handleViewBuildLog = async () => {
    if (!jobMeta) return;
    if (jobMeta.buildType === 'package_bazel') {
      try {
        const url = await getJobBazelBuildLog(jobMeta.jobId);
        if (!url) {
          message.warning('未找到编译日志，请联系管理员查看');
          return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err) {
        console.error('查询编译日志失败:', err);
        message.error('查询编译日志失败，请联系管理员查看');
      }
      return;
    }
    // compile_platform：弹窗展示日志正文
    setBuildLogVisible(true);
    setBuildLogContent('');
    setBuildLogUrl(null);
    setBuildLogLoading(true);
    try {
      const { logContent, logDownloadUrl } = await getJobBuildLogs(jobMeta.jobId);
      setBuildLogContent(logContent);
      setBuildLogUrl(logDownloadUrl ?? null);
    } catch (err) {
      console.error('加载编译日志失败:', err);
      setBuildLogContent('加载编译日志失败');
    } finally {
      setBuildLogLoading(false);
    }
  };

  // 「重试编译」：与 Job 列表页同一判定与确认流程，成功后刷新标题栏状态。
  const bazelRetryControllerRef = useRef(createBazelRetryController());
  const handleRetryBazelJob = () => {
    if (!jobMeta) return;
    bazelRetryControllerRef.current.open(jobMeta.jobId, {
      confirm: Modal.confirm,
      submit: retryBazelJob,
      reload: () => loadJobMeta(jobMeta.jobId),
      success: message.success,
      error: (err) => {
        console.error('重试 Bazel 编译失败:', err);
        message.error('重试编译提交或状态刷新失败，请查看提示后重试');
      },
    });
  };

  // 标题栏状态优先用 /job/list 的元信息，回退 statsMode 的 jobBasicInfo。
  const headerStatusInfo = jobMeta ?? jobBasicInfo;
  // 编译类 Job 才展示「编译日志」按钮（buildType 仅 /job/list 回填）。
  const isCompileJob =
    jobMeta?.buildType === 'package_bazel' || jobMeta?.buildType === 'compile_platform';

  // 感知 Job 走「评估报表」（多 case 指标汇总），PNC 走「任务对比」（evaluator 四象限）。
  // 判断依据是 task_type.sim_type（后端 JobVo.simType）——不能用 source（那是创建入口，
  // 手工建的感知 Job 也是 manual）或 taskTypeId（bigserial，test/prod 两环境不一致）。
  // jobMeta 异步加载，返回前按钮短暂显示 PNC 文案，属已知可接受窗口。
  const isPerceptionJob = jobMeta?.simType === 'perception';

  // 侧栏高亮依赖 URL 上的 ?sim_type=;各处 <Link to="/job-tasks/:id"> 都没带,
  // Sidebar 拿不到就 fallback 到 'planning',感知任务详情会误高亮到 PNC。
  // 拿到 jobMeta.simType 后回填一次,一处收敛,新入口漏拼也自愈。
  useEffect(() => {
    if (!jobMeta?.simType) return;
    // 函数式更新:基于最新 query 增量改。若用闭包里的 searchParams 快照重建,
    // 会把同一轮里别处(筛选条件回写等)刚写进去的参数一并抹掉。
    setSearchParams((prev) => {
      if (prev.get('sim_type') === jobMeta.simType) return prev;
      const next = new URLSearchParams(prev);
      next.set('sim_type', jobMeta.simType!);
      return next;
    }, { replace: true });
  }, [jobMeta?.simType, setSearchParams]);

  // 「仿真结果」列的 checker 标签（renderCheckerList）只给感知 Job 展示，列宽随之一并切换：
  // 默认 40，感知 Job 加宽到 90 让标签有换行空间；PNC 及其它 simType 保持原列宽不动。
  // jobMeta 异步加载，故不能写进 useResizableColumns 的初值（那时还判不出 simType），
  // 拿到后按一次即可。只在列宽仍是默认 40 时加宽，用户手动拖过的宽度不覆盖；
  // 反向（感知 → PNC）不缩回，避免把用户拖过的宽列硬掰回去。
  useEffect(() => {
    if (!isPerceptionJob) return;
    setColumnWidths((prev) => (prev.evaluator === 40 ? { ...prev, evaluator: 90 } : prev));
  }, [isPerceptionJob, setColumnWidths]);

  // 评估器参数点选：更新选中态，并把选中评估器增量写进 URL（?metricEvaluator=），
  // 让链接可分享「看的是哪个评估器」。空值删除，保持链接干净。
  const handleSelectMetricEvaluator = useCallback((name: string) => {
    setSelectedMetricEvaluator(name);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (name) next.set('metricEvaluator', name);
      else next.delete('metricEvaluator');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // 展开「逐场景明细」：首屏聚合按 withScenes=false 拉取（无 scenes），首次展开才带
  // withScenes=true 重取完整明细；已持有明细后再开合不重复请求。
  const handleMetricAggDetailToggle = () => {
    const opening = !metricAggDetailOpen;
    setMetricAggDetailOpen(opening);
    if (
      opening && jobId && selectedMetricEvaluator
      && !(metricAggData?.scenes?.length ?? 0)
    ) {
      // 加载中也可触发：seq 守卫保证后发的明细请求胜出，旧的瘦请求响应被丢弃
      loadMetricAgg(Number(jobId), selectedMetricEvaluator, true);
    }
  };

  const compareEntryText = isPerceptionJob ? '评估报表' : '任务对比';
  const compareEntryHint = isPerceptionJob
    ? '跳转「评估报表」页（当前 Job 的多 case 指标汇总，新标签页打开）'
    : '跳转「任务对比」独立页（以当前 Job 为基准，新标签页打开）';
  // 两处按钮（查询栏 / 不通过统计页头）共用同一跳转，避免两份逐字复制再次漂移。
  const openCompareEntry = () => {
    window.open(
      isPerceptionJob
        ? `/perception-report?jobId=${jobId}`
        : `/job-tasks/${jobId}?mode=compare`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  // CommentFilterValue → 后端 TaskListParams 片段 (commentKeyword/commentScope/hasComment)。
  // 关键词空即不带 commentKeyword;scope 只在有关键词时才透传,减少无用 query 噪声。
  const commentFilterToParams = (v: CommentFilterValue): Partial<TaskListParams> => {
    const out: Partial<TaskListParams> = {};
    const kw = v.keyword?.trim();
    if (kw) {
      out.commentKeyword = kw;
      out.commentScope = v.scope;
    }
    if (v.hasComment !== undefined) out.hasComment = v.hasComment;
    return out;
  };

  // 同上,但落到「对比任务侧」参数(口径限 scene + compareJobId)。两侧各管一侧、
  // 后端同传取 AND,故「当前有评论 + 对比无评论」这类组合无需额外的与/或开关。
  const compareCommentFilterToParams = (v: CommentFilterValue): Partial<TaskListParams> => {
    const out: Partial<TaskListParams> = {};
    const kw = v.keyword?.trim();
    if (kw) {
      out.compareCommentKeyword = kw;
      out.compareCommentScope = v.scope;
    }
    if (v.hasComment !== undefined) out.compareHasComment = v.hasComment;
    return out;
  };

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    // 每次查询时把评论筛选 3 项 同步覆盖到 params。清空评论筛选也要
    // 明确写 undefined 到旧字段,避免残留导致翻页/其它操作时把老值带回去。
    const commentPart = commentFilterToParams(commentFilter);
    // stats 页指标列排序（metricSort）不是表单字段，靠 `...queryParams` 展开带过来。
    // 评估项 / 指标一变，旧排序对新指标没有意义（值域、单位都不同），必须清掉；指标清空
    // 时该列整列不渲染，排序也无从谈起。否则会出现「换了指标，列上箭头还亮着旧方向」。
    // stats 页「失败原因 / 豁免原因」两个下拉是**表单字段**（reasonFailCode / reasonExemptCode），
    // 后端收的是 reasonKind + reasonCode：选哪个下拉就带哪个 kind。两个互斥（选一个会清另一个，
    // 见各自 onChange），这里仍按 fail 优先兜底，保证最多发出一组。
    const { reasonFailCode, reasonExemptCode, ...formRest } = values as Record<string, unknown> & {
      reasonFailCode?: string; reasonExemptCode?: string;
    };
    const reasonPart: Pick<TaskListParams, 'reasonKind' | 'reasonCode'> = reasonFailCode
      ? { reasonKind: 'fail', reasonCode: reasonFailCode }
      : reasonExemptCode
        ? { reasonKind: 'exempt', reasonCode: reasonExemptCode }
        : { reasonKind: undefined, reasonCode: undefined };
    const sortStale =
      values.metricKey !== queryParams.metricKey
      || values.evaluatorName !== queryParams.evaluatorName
      || !values.metricKey;
    const newParams: TaskListParams = {
      ...queryParams,
      ...formRest,
      ...reasonPart,
      ...(sortStale ? { metricSort: undefined } : {}),
      commentKeyword: undefined,
      commentScope: undefined,
      hasComment: undefined,
      ...commentPart,
      current: 1,
    };
    setQueryParams(newParams);
    syncTaskFiltersToUrl(newParams);
    await loadTaskList(newParams);
    void loadFilteredCount(newParams);
  };

  const enterToSearch = useEnterToSearch(() => { void handleSearch(); });

  const handleReset = () => {
    form.resetFields();
    setEvalCellFilter(null);
    setCommentFilter(EMPTY_COMMENT_FILTER);
    setFilteredCount(null);
    const newParams: TaskListParams = {
      jobId,
      current: 1,
      pageSize: queryParams.pageSize || 10,
      // 复位时保留 Daily 差异筛选（关闭请用 banner 上的「清除筛选」）
      ...(diffFilter.compareJobId && diffFilter.diffMode
        ? { compareJobId: diffFilter.compareJobId, diffMode: diffFilter.diffMode }
        : {}),
    };
    setQueryParams(newParams);
    syncTaskFiltersToUrl(newParams);
    loadTaskList(newParams);
  };

  // statsMode 专用：点击「按评测器聚合明细」pass/fail/error 单元格 → 不再单独发请求，
  // 直接把该评测器 + 判定灌进下方「评估项 / 评估项状态」表单，复用列表统一筛选（query_list
  // 的 evaluator_name + evaluator_status），保证联动结果与手动选筛选、与表格口径三方一致。
  // 再次点同一格 → 取消联动，清掉这两个表单、恢复 ?taskStatus= 默认。
  const applyEvaluatorCellFilter = (name: string, verdict: 'pass' | 'fail' | 'error') => {
    const isToggleOff = evalCellFilter?.name === name && evalCellFilter?.verdict === verdict;
    if (isToggleOff) {
      setEvalCellFilter(null);
      form.setFieldsValue({ evaluatorName: undefined, evaluatorStatus: undefined, metricKey: undefined, reasonFailCode: undefined, reasonExemptCode: undefined, status: statsTaskStatus });
      const newParams: TaskListParams = {
        jobId,
        current: 1,
        pageSize: queryParams.pageSize || 10,
        ...(statsTaskStatus !== undefined ? { status: statsTaskStatus } : {}),
      };
      setQueryParams(newParams);
      syncTaskFiltersToUrl(newParams);
      void loadTaskList(newParams);
      void loadFilteredCount(newParams);
      return;
    }
    setEvalCellFilter({ name, verdict });
    // 选中评估项 + 评估项状态；清掉任务状态避免叠加冲突。
    // metricKey 一并清掉：该列的值是按评估器取的，换评估器后旧指标不再成立
    // （newParams 里本就不带它，这里同步表单，避免下拉残留一个已失效的选中值）。
    form.setFieldsValue({ evaluatorName: name, evaluatorStatus: verdict, metricKey: undefined, reasonFailCode: undefined, reasonExemptCode: undefined, status: undefined });
    const newParams: TaskListParams = {
      jobId,
      current: 1,
      pageSize: queryParams.pageSize || 10,
      evaluatorName: name,
      evaluatorStatus: verdict,
    };
    setQueryParams(newParams);
    syncTaskFiltersToUrl(newParams);
    void loadTaskList(newParams);
    void loadFilteredCount(newParams);
  };

  const {
    sceneSetFilterAllowed, sceneSetStats, jobSceneSetOptions, handleSceneSetTreeChange, sceneSetStatsNode,
  } = useSceneSetDrill({
    jobId, compareMode, diffFilter, statsMode, statsTaskStatus, queryParams, form,
    setQueryParams, setEvalCellFilter, setCommentFilter, syncTaskFiltersToUrl,
    loadTaskList, loadFilteredCount, taskDetailRef,
  });

  // 设为保密 / 取消保密：作用于该 task 对应的场景（scene_id）。
  const handleToggleTaskSecurity = async (record: TaskListItem, confidential: boolean) => {
    setActionPopoverTaskId(null);
    try {
      await setScenesSecurityLevel([record.sceneId], confidential ? 0 : 1);
      message.success(confidential ? '已取消保密' : '已设为保密');
      await loadTaskList(queryParams);
    } catch (err) {
      console.error('设置保密失败:', err);
    }
  };

  // 本地仿真命令：按 task_type 编码取该类型在平台上实际用的跑法（--mode）。
  // 未登记的类型不加 --mode，走 scene_run.py 的默认模式。映射表见
  // @/constants/taskTypeMode（新增跑法只改那一处）。
  const buildReproduceCommand = (record: TaskListItem): string => {
    const base = `python3 /home/caros/tools/simulation_tools/scene_run.py --name ${record.sceneName || ''}`;
    const mode = taskTypeMode(record.taskTypeCode);
    return mode ? `${base} --mode ${mode}` : base;
  };

  const handleCopyReproduceCommand = (record: TaskListItem) => {
    const cmd = buildReproduceCommand(record);

    // 优先使用现代 Clipboard API（HTTPS / localhost 可用）
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(cmd).then(
        () => message.success('已复制本地仿真命令'),
        () => message.error('复制失败')
      );
      return;
    }

    // 兼容旧环境或非安全上下文：使用 textarea + execCommand 兜底
    try {
      const textarea = document.createElement('textarea');
      textarea.value = cmd;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        message.success('已复制本地仿真命令');
      } else {
        message.error('复制失败');
      }
    } catch {
      message.error('复制失败');
    }
  };

  // docker 池 / 取模选实例 / URL 拼装已提到 @/common/foxglove（感知报表页共用）
  const handleOpenFoxglove = async (taskId: number, sceneName: string) => {
    setFoxgloveLoadingTaskId(taskId);
    try {
      const url = await resolveMcapUrl(taskId, sceneName);
      if (!url) {
        message.warning('未找到对应的 mcap 文件');
        return;
      }
      window.open(buildFoxgloveUrl(url, taskId), '_blank');
    } catch {
      message.error('获取 mcap 文件链接失败');
    } finally {
      setFoxgloveLoadingTaskId(null);
    }
  };

  const handleDownloadMcap = async (taskId: number, sceneName: string) => {
    try {
      const url = await resolveMcapUrl(taskId, sceneName);
      if (!url) {
        message.warning('未找到对应的 mcap 文件');
        return;
      }
      window.open(url, '_blank');
    } catch {
      message.error('获取 mcap 文件下载链接失败');
    }
  };

  /**
   * 通用「操作」Popover 内容。task 必填(null 无对应任务时按钮 disabled,
   * 上层自行处理)。同一份内容被普通任务列表 + 任务对比双侧共用。
   *
   * 保密切换的刷新:普通视图走 loadTaskList,对比视图暂不自动刷(用户可
   * 手动点顶部「查询」)。保持行为与老 popover 一致,不引入跨视图 side effect。
   */
  const renderTaskActionPopover = (task: TaskListItem) => (
    <Popover
      open={actionPopoverTaskId === task.taskId}
      onOpenChange={(open) => setActionPopoverTaskId(open ? task.taskId : null)}
      content={
        <div style={{ minWidth: 200 }}>
          <div style={{ marginBottom: 8 }}>
            <a
              onClick={() => openTaskLog(task.taskId)}
              style={{ color: '#5e6ad2', cursor: 'pointer' }}
            >
              Task日志
            </a>
          </div>
          <div style={{ marginBottom: 8 }}>
            调度任务ID: {task.taskDispatchId || '-'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span>
              本地仿真命令：{buildReproduceCommand(task)}
            </span>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyReproduceCommand(task)}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <a
              onClick={() => handleDownloadMcap(task.taskId, task.sceneName)}
              style={{ color: '#5e6ad2', cursor: 'pointer' }}
            >
              <DownloadOutlined style={{ marginRight: 4 }} />
              mcap文件下载
            </a>
          </div>
          {canManageSecurity && (
            <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
              {(task.securityLevel ?? 0) > 0 ? (
                <a
                  onClick={() => handleToggleTaskSecurity(task, true)}
                  style={{ color: '#5e6ad2', cursor: 'pointer' }}
                >
                  <UnlockOutlined style={{ marginRight: 4 }} />
                  取消保密（场景）
                </a>
              ) : (
                <a
                  onClick={() => handleToggleTaskSecurity(task, false)}
                  style={{ color: '#cf1322', cursor: 'pointer' }}
                >
                  <LockFilled style={{ marginRight: 4 }} />
                  设为保密（场景）
                </a>
              )}
            </div>
          )}
        </div>
      }
      trigger="click"
    >
      <Button size="small" icon={<MoreOutlined />} />
    </Popover>
  );

  /**
   * 合并「可拖拽列宽」和「header 底色」两个 onHeaderCell 返回值。
   * 对比视图的分组子列需要同时:
   *   - 走 ResizableTitle 拿 onResize/width
   *   - 保留原分组底色(场景灰/当前紫/对比橙)让用户一眼看到三大分区
   * 单独用 resizeHeaderCell 会覆盖 style,故封装一个 helper。
   */
  const cmpHeaderCell = (key: string, bg: string) => () => ({
    ...resizeHeaderCell(key)(),
    style: { background: bg },
  });

  /**
   * 点开单个 checker：复用 chip 浮层同一份 evaluatorCache / 接口（含 type=1/2/3 全部行），
   * 未缓存时拉一次；失败只记日志并关闭加载态，弹层显示「暂无明细」。
   */
  const handleCheckerPopoverOpenChange = async (open: boolean, taskId: number, checkerName: string) => {
    if (!open) {
      setCheckerPopoverKey(null);
      return;
    }
    setCheckerPopoverKey(`${taskId}:${checkerName}`);
    if (evaluatorCache[taskId]) return;
    setCheckerDetailLoadingTaskId(taskId);
    try {
      const list = await getEvaluatorList(taskId, true);
      setEvaluatorCache((prev) => ({ ...prev, [taskId]: list }));
    } catch (err) {
      console.error('加载 checker 明细失败:', err);
    } finally {
      setCheckerDetailLoadingTaskId((prev) => (prev === taskId ? null : prev));
    }
  };

  const handleEvaluatorPopoverOpenChange = async (open: boolean, taskId: number) => {
    if (!open) {
      setEvaluatorPopoverTaskId(null);
      return;
    }

    setEvaluatorPopoverTaskId(taskId);

    const cached = evaluatorCache[taskId];
    if (cached) {
      setEvaluatorListData(cached);
      setEvaluatorListLoading(false);
      return;
    }

    setEvaluatorListData([]);
    setEvaluatorListLoading(true);
    try {
      // 浮层同时拉评测器(type=1)与有无责任(type=2)，分两节渲染
      const list = await getEvaluatorList(taskId, true);
      setEvaluatorCache((prev) => ({ ...prev, [taskId]: list }));
      setEvaluatorListData(list);
    } catch (err) {
      console.error('加载仿真结果失败:', err);
    } finally {
      setEvaluatorListLoading(false);
    }
  };

  // 打开对比选择弹窗(compareMode 时不弹 Modal,仅准备数据 + 选默认对比)。
  //
  // 历史上这里一把梭拉 pageSize=999 全表在前端 filter,存在两个问题:
  // (1) 超过 999 条时对比池会漏历史;
  // (2) 每次开抽屉全表回填 4 桶 breakdown/bazel_map/sim_type_map,90% 浪费。
  // 改为:
  // - 打开时并发拉两件:{jobId=当前} 拿 1 条精确的当前 Job 元信息;
  //   {simType 过滤, pageSize=10} 拿最近 10 条候选池;
  // - 搜索框输入时用 handleCompareCandidateSearch(见下)按 jobId/jobName 走后端过滤,
  //   不再客户端 filter。
  const handleOpenCompareSelect = useCallback(async () => {
    if (!compareMode) {
      setCompareSelectVisible(true);
      setSelectedCompareJobId(null);
    }
    setCompareJobListLoading(true);
    try {
      // 拿当前 Job 元信息:jobId 精确查一条,不再依赖候选池里 find。
      const curResp = await getJobList({ current: 1, pageSize: 1, jobId });
      const curJob = curResp.tasks[0];
      if (curJob) {
        setCurrentJobName(curJob.jobName || '');
        // 当前任务 JobItem 存起来,供 compareMode 基本信息模块渲染「算法版本」
        // 的 AlgorithmVersionCell 使用(compareMode useEffect 里 return 早跳过了
        // loadJobMeta,这里顺手补上)。
        setJobMeta(curJob);
      }
      // 候选池:后端直接按 simType 过滤只拉 10 条,jobId DESC(最新在前)。
      // simType 缺失(老后端)时不带 simType 参数,退化为全量最近 10 条;
      // 与后续 compare 接口的同类型校验并不严格耦合(校验在后端 compare 端)。
      const baseSimType = (curJob?.simType ?? undefined) as SimType | undefined;
      const candResp = await getJobList({
        current: 1, pageSize: 10,
        simType: baseSimType,
      });
      // 排除当前 Job 自己;后端不知道要排除自己,前端补一手。
      const filtered = candResp.tasks.filter((j) => j.jobId !== jobId);
      setCompareJobList(filtered);

      // compareMode:自动挑选默认对比任务(最新一条 daily Job;fallback 同仿真类型最新一条)。
      // getJobList 默认按 jobId desc 返回,因此 filtered[0] 即为同仿真类型最新。
      if (compareMode && filtered.length > 0) {
        const daily = filtered.find((j) =>
          (j.jobName || '').toLowerCase().includes('daily'),
        );
        const defaultJob = daily ?? filtered[0];
        setSelectedCompareJobId((prev) => prev ?? defaultJob.jobId);
      }
    } catch (err) {
      console.error('加载Job列表失败:', err);
    } finally {
      setCompareJobListLoading(false);
    }
  }, [compareMode, jobId]);

  // 对比任务下拉的搜索:防抖 300ms 后按 jobId (纯数字) 或 jobName (ilike) + simType
  // 过滤走后端拉 20 条。空关键字回退到默认 10 条候选。
  // 输入判定:纯数字 → jobId 精确;含非数字 → jobName 子串。
  // 注意:与本文件里更下方的 handleCompareSearch(对比明细的筛选查询)是两回事,
  // 后者管的是「对比详情表格」的服务器分页刷新,别混。
  const compareSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleCompareCandidateSearch = useCallback((keyword: string) => {
    if (compareSearchTimerRef.current) {
      clearTimeout(compareSearchTimerRef.current);
    }
    compareSearchTimerRef.current = setTimeout(async () => {
      setCompareJobListLoading(true);
      try {
        const baseSimType = (jobMeta?.simType ?? undefined) as SimType | undefined;
        const trimmed = keyword.trim();
        const isNumeric = /^\d+$/.test(trimmed);
        const resp = await getJobList({
          current: 1,
          pageSize: trimmed ? 20 : 10,
          simType: baseSimType,
          jobId: isNumeric ? Number(trimmed) : undefined,
          jobName: !isNumeric && trimmed ? trimmed : undefined,
        });
        setCompareJobList(resp.tasks.filter((j) => j.jobId !== jobId));
      } catch (err) {
        console.error('搜索对比任务失败:', err);
      } finally {
        setCompareJobListLoading(false);
      }
    }, 300);
  }, [jobId, jobMeta?.simType]);

  // 紧凑时间：去掉年份，只留「MM-DD HH:mm:ss」，供合并后的时间列节省横向空间。


  // 异步加载当页预览图和评估结果（不阻塞表格展示）
  const loadCompareExtras = useCallback(async (
    currentTasks: TaskListItem[],
    compareTasks: TaskListItem[],
  ) => {
    const allTaskIds = [
      ...currentTasks.map((t) => t.taskId),
      ...compareTasks.map((t) => t.taskId),
    ];
    if (allTaskIds.length === 0) return;

    // 预览图和评估结果并行加载
    const previewPromise = (async () => {
      const keys = allTaskIds.map((id) => `${id}/preview_image/preview.webp`);
      const keyToId: Record<string, number> = {};
      keys.forEach((k, i) => { keyToId[k] = allTaskIds[i]; });
      try {
        const files = await getEvaluationFileUrls(keys);
        const map: Record<number, string> = {};
        files.forEach((f) => {
          const tid = keyToId[f.path];
          if (tid) map[tid] = f.url;
        });
        setComparePreviewUrlMap((prev) => ({ ...prev, ...map }));
      } catch { /* ignore */ }
      setComparePreviewDone(true);
    })();

    const evalPromise = (async () => {
      const allTasksFlat = [...currentTasks, ...compareTasks];
      const taskIdsForEval = allTaskIds.filter((id) => {
        if (compareEvaluatorCache[id]) return false;
        const t = allTasksFlat.find((task) => task.taskId === id);
        return !t || t.status <= 20;
      });
      if (taskIdsForEval.length === 0) return;
      // includeRespEvent=true：与 task list 单点拉时一致，附带 type=2 有无责任行；
      // 否则对比页 Popover 的「有无责任」段会空（与 task 列表不一致）。
      const evalResults = await Promise.allSettled(
        taskIdsForEval.map((id) => getEvaluatorList(id, true))
      );
      const newCache: Record<number, EvaluatorItem[]> = {};
      evalResults.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          newCache[taskIdsForEval[i]] = result.value;
        }
      });
      setCompareEvaluatorCache((prev) => ({ ...prev, ...newCache }));
    })();

    await Promise.allSettled([previewPromise, evalPromise]);
  }, [compareEvaluatorCache]);

  // 分页加载对比数据的核心函数
  /** 对比页的一组筛选条件（不含 jobId/分页 —— 那两个由调用方单独传）。
   *
   *  抽成具名类型而非在两处各写一遍内联字面量：`buildCompareQueryParams` 与
   *  `loadComparePage` 原本各有一份 11 字段的相同声明，加字段时漏改一处就会让
   *  某个入口静默丢筛选（comment 系列已经踩过这个坑）。
   */
  interface ComparePageFilters {
    compareSceneSetId?: number;
    sceneName?: string;
    status?: string | number;
    diffMode?: TaskDiffMode;
    evaluatorName?: string;
    evaluatorDiff?: boolean;
    diffConsistency?: CompareConsistency;
    /** 行为对比标签 —— **会收窄行集**，与 diffConsistency 同款：进 count 出口。 */
    behaviorLabel?: BehaviorFilterValue;
    /** 行为对比强事件 —— 同样**会收窄行集**：进 count 出口。 */
    behaviorStrong?: string;
    /** fail/pass 原因比对筛选 —— **会收窄行集**（与 metricSort 相反），故与
     *  diffConsistency 同款：进 count 出口、须与 evaluatorName 同传。 */
    reasonDiff?: ReasonDiff;
    /** 原因**类别**筛（当前侧 reason.code）—— 与 reasonDiff 正交，同传取交集；
     *  同样**会收窄行集**，故与 reasonDiff 同款进 count 出口 + 须与 evaluatorName 同传。 */
    reasonCode?: string;
    metricKey?: string;
    metricCompare?: MetricCompare;
    /** 指标列排序方向 —— 只改行序不改行集，与 metricCompare 正交。 */
    metricSort?: MetricSort;
    comment?: CommentFilterValue;
    compareSideComment?: CommentFilterValue;
    commentCombine?: 'and' | 'or';
  }

  /** 把 compareMode 各种 filter 拼成 TaskListParams 快照。
   *
   *  单点管理"对比页当前查询"的过滤条件构造 —— loadComparePage 拉列表、
   *  loadCompareFilteredCount 拉 sceneCount、rerun-from-query 发新任务
   *  三处共用,避免任一处漏 filter 导致口径飘。
   *
   *  与 loadComparePage 里内联的 queryFilter 拼装 100% 对齐。
   */
  const buildCompareQueryParams = useCallback((
    compareJobId: number,
    page: number = 1,
    pageSize: number = 10,
    filters?: ComparePageFilters,
  ): TaskListParams => {
    // 对比页所有 snapshot 一律带 compareJobId,标记"我处于对比模式"。
    // 之前只在 diffMode/diffConsistency/metricKey/compareSideComment 触发时才设,
    // 导致「只勾了当前 job 侧评论」的场景下 snapshot.compareJobId==null → rerun 触
    // 发按钮的 `isFromCompare = rerunFilters.compareJobId != null` 判断为 false,
    // 错读到旧的普通模式 filteredCount(可能是历史遗留 1),而 CreateJobModal
    // 预览用同一份错读值,前后一致但都不是真实场景数。
    const queryFilter: TaskListParams = { jobId, current: page, pageSize, compareJobId };
    if (filters?.compareSceneSetId !== undefined) queryFilter.compareSceneSetId = filters.compareSceneSetId;
    if (filters?.sceneName) queryFilter.sceneName = filters.sceneName;
    if (filters?.status !== undefined && filters?.status !== '') {
      queryFilter.status = filters.status;
    }
    if (filters?.evaluatorName) queryFilter.evaluatorName = filters.evaluatorName;
    if (filters?.comment) Object.assign(queryFilter, commentFilterToParams(filters.comment));
    if (filters?.compareSideComment) {
      const cmpPart = compareCommentFilterToParams(filters.compareSideComment);
      if (Object.keys(cmpPart).length > 0) {
        Object.assign(queryFilter, cmpPart);
        const curActive = filters?.comment && isCommentFilterActive(filters.comment);
        if (curActive && filters?.commentCombine === 'or') queryFilter.commentCombine = 'or';
      }
    }
    if (filters?.diffMode) {
      queryFilter.diffMode = filters.diffMode;
      if (filters?.evaluatorDiff && filters?.evaluatorName) {
        queryFilter.evaluatorDiff = true;
      }
    }
    if (filters?.diffConsistency) {
      queryFilter.diffConsistency = filters.diffConsistency;
    }
    // 行为标签筛选：与一致性同款 —— **会收窄行集**，漏发 = 静默全量。
    if (filters?.behaviorLabel) {
      queryFilter.behaviorLabel = filters.behaviorLabel;
    }
    if (filters?.behaviorStrong) {
      queryFilter.behaviorStrong = filters.behaviorStrong;
    }
    // 原因比对筛选：与一致性同款 —— **会收窄行集**，故必须发出去（漏发 = 静默全量）。
    // 后端还要求 evaluatorName（原因是评测器维度的），此处一并把关。
    if (filters?.reasonDiff && filters?.evaluatorName) {
      queryFilter.reasonDiff = filters.reasonDiff;
    }
    // 原因**类别**筛：与上面同款（会收窄行集，漏发 = 静默全量）。与 reasonDiff 是两个
    // 正交参数，各自独立把关 —— 不是「二选一」的分支，虽然 UI 上共用一个下拉（互斥）。
    if (filters?.reasonCode && filters?.evaluatorName) {
      queryFilter.reasonCode = filters.reasonCode;
    }
    if (filters?.metricKey && filters?.metricCompare && filters?.evaluatorName) {
      queryFilter.metricKey = filters.metricKey;
      queryFilter.metricCompare = filters.metricCompare;
    }
    // 排序**独立成支**：后端只要求 metricSort 与 metricKey + evaluatorName 同传，
    // **不要求** metricCompare（只点表头排序时不带方向筛选）。
    // 若把它并进上面那支，就会出现最难查的一类 bug：表头箭头亮着、metricSort 却被
    // 条件吞掉没发出去，顺序纹丝不动而 UI 一切正常。
    if (filters?.metricSort && filters?.metricKey && filters?.evaluatorName) {
      queryFilter.metricKey = filters.metricKey;
      queryFilter.metricSort = filters.metricSort;
    }
    return queryFilter;
  // 依赖只 jobId(其它 compareCommentFilterToParams/commentFilterToParams/
  // isCommentFilterActive 都是纯函数),不会引发重复重建。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const loadComparePage = useCallback(async (
    compareJobId: number,
    page: number,
    pageSize: number,
    filters?: ComparePageFilters,
  ) => {
    const requestId = ++compareLoadRequestRef.current;
    setCompareLoading(true);
    try {
      const queryFilter = buildCompareQueryParams(compareJobId, page, pageSize, filters);
      const currentResp = await getTaskList(queryFilter);
      if (requestId !== compareLoadRequestRef.current) return;
      const currentTasks = currentResp.tasks;
      setCompareTotal(currentResp.total);
      // 顺带拉一次「筛选后统计」给对比页发任务按钮展示 sceneCount。
      // 不阻塞主流(fire-and-forget),失败静默(compareFilteredCount 保持上次值)。
      (async () => {
        setCompareFilteredCountLoading(true);
        try {
          const count = await getFilteredTaskCount(queryFilter);
          if (requestId === compareLoadRequestRef.current) setCompareFilteredCount(count);
        } catch { /* axios 拦截器已提示 */ }
        finally { if (requestId === compareLoadRequestRef.current) setCompareFilteredCountLoading(false); }
      })();

      const sceneIds = currentTasks.map((t) => t.sceneId).filter((id) => id != null);
      let compareTasks: TaskListItem[] = [];
      if (sceneIds.length > 0) {
        const compareResp = await getTaskList({
          jobId: compareJobId,
          latestPerScene: true,
          sceneIds,
          current: 1,
          pageSize: sceneIds.length,
        });
        compareTasks = compareResp.tasks;
      }

      if (requestId !== compareLoadRequestRef.current) return;
      const compareMap = new Map<number, TaskListItem>();
      compareTasks.forEach((t) => compareMap.set(t.sceneId, t));

      const rows: CompareRow[] = currentTasks.map((cur) => {
        const cmp = compareMap.get(cur.sceneId) || null;
        return {
          // 行唯一 key 用 task_id + scene_id：同一 job 内同一 scene 可能有多条 task
          // （运行时放入了重复场景，非重试），仅用 scene_id 会撞 key 导致渲染去重（显示行数
          // < 实际 total）。current 侧按 task 粒度展示（保留重复场景），compare 侧仍按 scene
          // 关联（跨 job 唯一可对齐的是 scene_id）。
          key: `${cur.taskId}-${cur.sceneId}`,
          sceneName: cur.sceneName,
          sceneType: cur.sceneType || '-',
          apiCode: cur.apiCode ?? cmp?.apiCode ?? null,
          source: cur.source ?? cmp?.source ?? null,
          region: cur.region ?? cmp?.region ?? null,
          currentTask: cur,
          compareTask: cmp,
          onlyInCurrent: !cmp,
        };
      });

      setCompareData(rows);
      setCompareLoading(false);
      setComparePreviewDone(false);

      // 预览图和评估结果在表格展示后异步加载
      loadCompareExtras(currentTasks, compareTasks);
    } catch (err) {
      if (requestId !== compareLoadRequestRef.current) return;
      console.error('加载对比数据失败:', err);
      message.error('加载对比数据失败');
      setCompareLoading(false);
    }
  }, [buildCompareQueryParams, message, loadCompareExtras]);

  // 执行对比（确认选中后加载第一页）
  const handleCompareConfirm = async () => {
    if (!selectedCompareJobId) {
      message.warning('请选择要对比的任务');
      return;
    }

    // 名称优先取 compareJobMeta（jobIds 多值查的权威源，首屏即有），
    // 退回候选列表 find，最后才退化成 `Job {id}`——消除对 compareJobList
    // 加载时序的依赖（此前首屏 list 未 populate 时顶部框只显示 id）。
    const selectedJob =
      compareJobMeta?.jobId === selectedCompareJobId
        ? compareJobMeta
        : compareJobList.find((j) => j.jobId === selectedCompareJobId);
    setCompareJobName(selectedJob?.jobName || `Job ${selectedCompareJobId}`);
    compareJobIdRef.current = selectedCompareJobId;
    setCompareSelectVisible(false);
    setCompareResultVisible(true);
    setComparePreviewUrlMap({});
    setCompareEvaluatorCache({});
    // 挂载时本函数由 [compareMode, selectedCompareJobId] effect 自动跑一次；
    // 此时若清筛选，会把刚从分享链接还原的条件冲掉，故首次跑保留 URL 初值。
    // 之后用户主动换对比任务时照常清空（换了对比对象，旧筛选作废）。
    const keepUrlFilters = keepUrlCompareFiltersRef.current;
    keepUrlCompareFiltersRef.current = false;
    const urlInit = compareUrlInit.current;
    const restored: CompareFilterState =
      keepUrlFilters && urlInit ? urlInit : EMPTY_COMPARE_FILTERS;
    setComparePage(restored.page);
    setComparePageSize(keepUrlFilters ? restored.pageSize : 10);
    setCompareFilterSceneSetId(restored.compareSceneSetId);
    setCompareFilterSceneName(restored.sceneName);
    setCompareFilterStatus(restored.status);
    setCompareFilterEvaluatorName(restored.evaluatorName);
    setCompareFilterMetricKey(restored.metricKey);
    setCompareFilterMetricCompare(restored.metricCompare);
    setCompareFilterMetricSort(restored.metricSort);
    setCompareCommentFilter(restored.comment);
    setCompareSideCommentFilter(restored.compareSideComment);
    setCommentCombine(restored.commentCombine);
    setCompareMetricDefs([]);
    setMetricDirCounts(null);
    setCompareDiffConsistency(restored.diffConsistency);
    setCompareBehaviorLabel(restored.behaviorLabel);
    setCompareBehaviorStrong(restored.behaviorStrong);
    setCompareConsistencyMap({});
    setCompareFilterReasonDiff(restored.reasonDiff);
    setCompareFilterReasonCode(restored.reasonCode);
    setReasonDiffMap({});
    // 非首次（用户换对比任务）时仍沿用原逻辑：仅当 Daily 下钻的 compareJobId
    // 与本次选中的对比任务一致，才带上它携带的 diffMode。
    const initialMode = keepUrlFilters
      ? restored.diffMode
      : (diffFilter.compareJobId === selectedCompareJobId ? diffFilter.diffMode : undefined);
    setCompareDiffMode(initialMode);

    // 首屏 bundle 端点一次拉全:指标 + 评测器矩阵 + 一致性,共享后端 request-scoped
    // 缓存,3 次 RTT / 3× 冗余 SQL 压到 1 次。任一失败降级取默认值,不阻塞主流程。
    setCompareEvaluatorMatrix(null);
    setCompareEvaluatorMatrixLoading(true);
    setCompareSummaryLoading(true);
    try {
      const bundle = await getCompareBundle(jobId, selectedCompareJobId);
      const cmp = bundle?.compare || null;
      setCompareRaw(cmp);
      if (!cmp) {
        setCompareSummary(null);
      } else {
        setCompareSummary({
          todayJobName: undefined,
          compareJobName: selectedJob?.jobName || `Job ${selectedCompareJobId}`,
          currentPassRate: cmp.today.passRate,
          comparePassRate: cmp.compare.passRate,
          anomalyRate: cmp.today.anomalyRate,
          anomalyThreshold: 1,
          newSuccessSceneCount: cmp.newSuccessSceneCount,
          newFailSceneCount: cmp.newFailSceneCount,
        });
      }
      setCompareEvaluatorMatrix(bundle?.evaluatorMatrix || null);
      setCompareConsistencyMap(bundle?.consistency || {});
    } catch (err) {
      console.error('加载对比 bundle 失败:', err);
      setCompareSummary(null);
      setCompareRaw(null);
      setCompareEvaluatorMatrix(null);
      setCompareConsistencyMap({});
    } finally {
      setCompareSummaryLoading(false);
      setCompareEvaluatorMatrixLoading(false);
    }

    // 预加载评测器名称列表（白名单，含指标型评测器），供筛选下拉使用。
    // /job/names 读的是全局白名单，与具体 job 无关，加载一次即可。
    if (evaluatorNameOptions.length === 0 && !evaluatorNameLoading) {
      loadEvaluatorNames(jobId);
    }
    // 首屏按还原出的筛选 + 页码加载（非首次时 restored 为空筛选、page=1，
    // 与改动前的 loadComparePage(…, 1, 10, { diffMode }) 行为一致）。
    await loadComparePage(selectedCompareJobId, restored.page, keepUrlFilters ? restored.pageSize : 10, {
      compareSceneSetId: restored.compareSceneSetId,
      sceneName: restored.sceneName,
      status: restored.status,
      diffMode: initialMode,
      evaluatorName: restored.evaluatorName,
      evaluatorDiff: !!restored.evaluatorName,
      diffConsistency: restored.diffConsistency,
      behaviorLabel: restored.behaviorLabel,
      behaviorStrong: restored.behaviorStrong,
      reasonDiff: restored.reasonDiff,
      reasonCode: restored.reasonCode,
      metricKey: restored.metricKey,
      metricCompare: restored.metricCompare,
      metricSort: restored.metricSort,
      comment: restored.comment,
      compareSideComment: restored.compareSideComment,
      commentCombine: restored.commentCombine,
    });
  };

  /**
   * 「原因」列数据加载：选中评测器后一次性拉整个 Job 的 `{sceneId: {verdict, 两侧原因}}`。
   *
   * 挂在 `compareFilterEvaluatorName` 上而不是挂在 loadComparePage 里，是因为它与分页 /
   * 其它筛选**无关**：判定只取决于「哪两个 Job + 哪个评测器」，翻页 / 改状态筛选都不该
   * 重拉。挂进分页路径反而会给每次翻页平白加一次整 Job 的 evaluate_info 遍历。
   *
   * 未选评测器 / 不在对比模式时清空 —— 该列与「原因」筛选都依附于评测器（原因是评测器
   * 维度的），没有评测器就没有可显示、可筛的内容。
   *
   * `compareFilterEvaluatorName` 一换就重拉，故 `compareFilterReasonDiff` 不参与本 effect
   * 的依赖（它只筛行集、不影响判定），避免「换筛选项 → 重拉一次相同数据」。
   */
  useEffect(() => {
    if (!compareMode || !selectedCompareJobId || !compareFilterEvaluatorName) {
      setReasonDiffMap({});
      return;
    }
    let cancelled = false;
    setReasonDiffLoading(true);
    getReasonDiff(jobId, selectedCompareJobId, compareFilterEvaluatorName)
      .then((map) => {
        if (!cancelled) setReasonDiffMap(map);
      })
      .catch((err) => {
        console.error('加载原因比对失败:', err);
        if (!cancelled) setReasonDiffMap({});
      })
      .finally(() => {
        if (!cancelled) setReasonDiffLoading(false);
      });
    return () => { cancelled = true; };
  }, [compareMode, jobId, selectedCompareJobId, compareFilterEvaluatorName]);

  /**
   * 对比页「双方基本信息」里切换某侧的「忽略 check 不通过场景」开关。
   * 写后端后复刻 handleCompareConfirm 的矩阵 refetch（整体 + 评测器），并按**当前**
   * 筛选重载任务表（不清筛选）。开关只改单侧 job，后端 ``_excluded_scene_ids`` live
   * 读，故两个矩阵都会按新口径重算。不动 selectedCompareJobId，故
   * [compareMode, selectedCompareJobId] effect 不会重触、不会清筛选。
   */
  const handleToggleCompareExcludeCheck = async (side: 'base' | 'compare', next: boolean) => {
    const jobIdVal = side === 'base' ? Number(jobId) : Number(selectedCompareJobId);
    if (!jobIdVal || !selectedCompareJobId) return;
    const setToggling =
      side === 'base' ? setExcludeCheckTogglingBase : setExcludeCheckTogglingCompare;
    setToggling(true);
    try {
      await setExcludeCheckFailed(jobIdVal, next);
      // 评测器矩阵 + 整体矩阵并行刷新；任务表按当前筛选重载（回 page 1）
      setCompareEvaluatorMatrix(null);
      setCompareEvaluatorMatrixLoading(true);
      compareDailyJobsEvaluatorMatrix(jobId, selectedCompareJobId)
        .then((m) => setCompareEvaluatorMatrix(m))
        .catch((err) => {
          console.error('刷新评测器聚合矩阵失败:', err);
          setCompareEvaluatorMatrix(null);
        })
        .finally(() => setCompareEvaluatorMatrixLoading(false));
      try {
        const cmp = await compareDailyJobs(jobId, selectedCompareJobId);
        setCompareRaw(cmp);
      } catch (err) {
        console.error('刷新对比指标失败:', err);
        setCompareRaw(null);
      }
      if (compareJobIdRef.current) {
        await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
          compareSceneSetId: compareFilterSceneSetId,
          sceneName: compareFilterSceneName,
          status: compareFilterStatus,
          diffMode: compareDiffMode,
          evaluatorName: compareFilterEvaluatorName,
          evaluatorDiff: !!compareFilterEvaluatorName,
          diffConsistency: compareDiffConsistency,
          behaviorLabel: compareBehaviorLabel,
          behaviorStrong: compareBehaviorStrong,
          reasonDiff: compareFilterReasonDiff,
          reasonCode: compareFilterReasonCode,
          metricKey: compareFilterMetricKey,
          metricCompare: compareFilterMetricCompare,
          metricSort: compareFilterMetricSort,
          comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
        });
      }
      message.success(next ? '已忽略 check 不通过场景' : '已恢复全部场景计入');
    } catch (err) {
      console.error('切换 exclude_check_failed 失败:', err);
      message.error('切换失败，请稍后重试');
    } finally {
      setToggling(false);
    }
  };

  // 切换「差异」列一致/不一致筛选：与 diffMode/评测器联动正交，保留其它筛选条件。
  const handleCompareConsistencyChange = async (value?: CompareConsistency) => {
    setCompareDiffConsistency(value);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: value,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  /** 原表当页场景的行为详情（折线 / 摘要 / 主要差异）：只取当页那几行，不随任务规模增长。
   *  整任务拉取折线 3 万场景约 40MB，所以不能像 compareBehaviorMap 那样一次拉全。 */
  const [compareBehaviorRows, setCompareBehaviorRows] = useState<Record<number, BehaviorSceneRow>>({});

  // 行为结论列：进入对比 / 换对比任务时拉一次。无 menu:behavior-compare 权限不请求（后端会 403）。
  // 单独拉取而不塞进首屏 bundle：不拖慢首屏，失败也只是这一列空着，不影响其它对比内容。
  const canSeeBehavior = hasPerm('menu:behavior-compare');
  /** 行为对比进度变化（翻转场景算完 / 全部算完）时自增，让行为列与总览区块重新取数。 */
  const [behaviorRefreshKey, setBehaviorRefreshKey] = useState(0);
  useEffect(() => {
    if (!compareMode || !canSeeBehavior || !jobId || !selectedCompareJobId) {
      setCompareBehaviorMap({});
      return undefined;
    }
    let alive = true;
    getBehaviorSceneLabels(jobId, selectedCompareJobId)
      .then((m) => alive && setCompareBehaviorMap(m))
      .catch(() => alive && setCompareBehaviorMap({}));
    return () => { alive = false; };
  }, [compareMode, canSeeBehavior, jobId, selectedCompareJobId, behaviorRefreshKey]);

  // 当页行为详情：compareData（原表当页数据）变了就重取。无报告、无权限、或没有场景时清空。
  useEffect(() => {
    const ids = compareData
      .map((r) => r.currentTask?.sceneId)
      .filter((x): x is number => typeof x === 'number');
    if (!compareMode || !canSeeBehavior || !jobId || !selectedCompareJobId || ids.length === 0) {
      setCompareBehaviorRows({});
      return undefined;
    }
    let alive = true;
    getBehaviorSceneRows(jobId, selectedCompareJobId, ids)
      .then((m) => alive && setCompareBehaviorRows(m))
      .catch(() => alive && setCompareBehaviorRows({}));
    return () => { alive = false; };
  }, [compareMode, canSeeBehavior, jobId, selectedCompareJobId, compareData, behaviorRefreshKey]);

  /** 总览区块 / 「行为」下拉共用：同时改「行为结论」与「强事件」两个筛选，**只发一次请求**
   *  （分开设置会各发一次，且第二次会读到第一次还没生效的旧状态）。传 undefined 表示清掉该项。 */
  const applyBehaviorFilters = async (next: { label?: BehaviorFilterValue; strong?: string }) => {
    setCompareBehaviorLabel(next.label);
    setCompareBehaviorStrong(next.strong);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: next.label,
        behaviorStrong: next.strong,
        reasonDiff: compareFilterReasonDiff,
        reasonCode: compareFilterReasonCode,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  // 进入「任务对比」独立页：拉取同 simType 的候选任务并自动选默认对比
  // 注意：必须放在 handleOpenCompareSelect / handleCompareConfirm 定义之后，
  // 否则 const 声明的 TDZ 会触发 "Cannot access ... before initialization"
  useEffect(() => {
    if (compareMode && jobId && !Number.isNaN(jobId)) {
      void handleOpenCompareSelect();
    }
  }, [compareMode, jobId, handleOpenCompareSelect]);

  // 对比页也要拉「已登记聚合的评估器」清单——用于把评估器参数从评测器矩阵拆出，
  // 并渲染「评估器参数对比」卡片。
  useEffect(() => {
    if (compareMode) void loadMetricAggSpecs();
  }, [compareMode, loadMetricAggSpecs]);

  // 任务对比页内切换 Select 后自动重新加载对比数据（不依赖 Modal 流程）
  // handleCompareConfirm 内部依赖 selectedCompareJobId / compareJobList 闭包，
  // 放进 deps 反而会因 stale closure 多次触发；这里只观测目标变化。
  useEffect(() => {
    if (!compareMode || !selectedCompareJobId) return;
    void handleCompareConfirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, selectedCompareJobId]);

  // 对比双方元信息：一次拉当前 + 对比双方完整 JobItem（jobIds 多值查），
  // 供基本信息卡片算法版本单元格与顶部对比框首屏即完整。与上面的 confirm
  // effect 独立——confirm 拉的是指标/矩阵/一致性 bundle，这里拉的是双方 Job
  // 行元信息（算法版本/分支/创建人等），职责分开、互不阻塞。
  useEffect(() => {
    if (!compareMode || !jobId || Number.isNaN(jobId) || !selectedCompareJobId) return;
    void loadCompareMetas(jobId, selectedCompareJobId);
  }, [compareMode, jobId, selectedCompareJobId, loadCompareMetas]);

  // compareJobMeta 就绪后同步顶部对比框名称：handleCompareConfirm 与
  // loadCompareMetas 并发，confirm 先跑时 meta 可能未到位，顶部框会短暂显示
  // `Job {id}`；这里在 meta 到达后补正为真实 jobName，无需用户重新查询。
  useEffect(() => {
    if (!compareMode || !selectedCompareJobId) return;
    if (compareJobMeta?.jobId === selectedCompareJobId && compareJobMeta.jobName) {
      setCompareJobName(compareJobMeta.jobName);
    }
  }, [compareMode, selectedCompareJobId, compareJobMeta]);

  // 把当前选中的对比任务回写到 URL（replace，不污染历史），使链接可分享：
  // 对方打开 ?mode=compare&compareJobId=... 即复现同一对比。
  useEffect(() => {
    if (!compareMode) return;
    const next = selectedCompareJobId != null ? String(selectedCompareJobId) : null;
    // 同上,函数式更新 —— 与下面的筛选回写同轮触发时,快照写法会互相覆盖。
    setSearchParams((prev) => {
      if (prev.get('compareJobId') === next) return prev;
      const sp = new URLSearchParams(prev);
      if (next) {
        sp.set('compareJobId', next);
      } else {
        sp.delete('compareJobId');
      }
      return sp;
    }, { replace: true });
  }, [compareMode, selectedCompareJobId, setSearchParams]);

  /**
   * 把对比页的筛选条件回写到 URL（replace，不污染历史），与上面的 compareJobId
   * 一起构成可分享链接：对方打开即复现同一对比 + 同一组筛选 + 同一页。
   *
   * 这里用「观测全部 compare* filter state」的方式集中回写，而不是在十几个
   * setXxx 调用点各写一遍 —— 那些 handler（矩阵象限联动、指标下钻、评论筛选、
   * 翻页…）会成组地改多个 state，逐点回写既易漏又会产生中间态链接。
   */
  useEffect(() => {
    if (!compareMode) return;
    const next: CompareFilterState = {
      compareSceneSetId: compareFilterSceneSetId,
      sceneName: compareFilterSceneName,
      status: compareFilterStatus,
      diffMode: compareDiffMode,
      diffConsistency: compareDiffConsistency,
      behaviorLabel: compareBehaviorLabel,
      behaviorStrong: compareBehaviorStrong,
      reasonDiff: compareFilterReasonDiff,
      reasonCode: compareFilterReasonCode,
      evaluatorName: compareFilterEvaluatorName,
      metricKey: compareFilterMetricKey,
      metricCompare: compareFilterMetricCompare,
      metricSort: compareFilterMetricSort,
      comment: compareCommentFilter,
      compareSideComment: compareSideCommentFilter,
      commentCombine,
      page: comparePage,
      pageSize: comparePageSize,
    };
    setSearchParams((prev) => writeCompareFiltersToParams(prev, next), { replace: true });
  }, [
    compareMode, setSearchParams,
    compareFilterSceneSetId, compareFilterSceneName, compareFilterStatus, compareDiffMode, compareDiffConsistency,
    compareBehaviorLabel, compareBehaviorStrong,
    compareFilterReasonDiff,
    // 类别筛必须也在依赖里：该 effect 是「观测全部 compare* state 回写 URL」，
    // 漏一个就会出现「筛选生效了、链接却不带它」，分享出去的人看不到同一批行。
    compareFilterReasonCode,
    compareFilterEvaluatorName, compareFilterMetricKey, compareFilterMetricCompare,
    compareFilterMetricSort,
    compareCommentFilter, compareSideCommentFilter, commentCombine,
    comparePage, comparePageSize,
  ]);

  // 复制当前对比页链接到剪贴板（链接已带 compareJobId，对方打开即复现同一对比）。
  const handleCopyCompareLink = useCallback(async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // 降级：不支持 Clipboard API（如非 https）时用临时 textarea 兜底
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('对比链接已复制，可直接分享');
    } catch (err) {
      console.error('复制对比链接失败:', err);
      message.error('复制失败，请手动复制浏览器地址栏链接');
    }
  }, [message]);

  // 复制当前页面链接（含全部 query：mode/taskStatus 等），分享后他人可看到相同视图。
  const handleCopyPageLink = useCallback(async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // 降级：非 https 等不支持 Clipboard API 时用临时 textarea 兜底
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      message.success('页面链接已复制，可直接分享');
    } catch (err) {
      console.error('复制页面链接失败:', err);
      message.error('复制失败，请手动复制浏览器地址栏链接');
    }
  }, [message]);

  // 场景树加载（带 hidden）：普通模式与 compareMode 都要（前者用来在 header 显示
  // 场景集合 Popover，后者把 scene_set_ids 解析为路径名）。statsMode 独立页无需。
  useEffect(() => {
    if (statsMode) return;
    let mounted = true;
    getSceneTree(true)
      .then((tree) => {
        if (mounted) setCompareSceneTree(tree);
      })
      .catch((err) => console.error('加载场景树失败:', err));
    return () => {
      mounted = false;
    };
  }, [statsMode]);

  // 对比分页切换
  const handleComparePageChange = async (page: number, pageSize: number) => {
    setComparePage(page);
    setComparePageSize(pageSize);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, page, pageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        // 评测器联动当且仅当选中了评测器名（仅 applyEvaluatorMatrixCell 会设置）
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: compareFilterReasonDiff,
        reasonCode: compareFilterReasonCode,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  // 对比筛选查询
  const handleCompareSearch = async () => {
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: compareFilterReasonDiff,
        reasonCode: compareFilterReasonCode,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  // 对比筛选重置
  const handleCompareFilterReset = async () => {
    setCompareFilterSceneSetId(undefined);
    setCompareFilterSceneName(undefined);
    setCompareFilterStatus(undefined);
    setCompareDiffMode(undefined);
    setCompareFilterEvaluatorName(undefined);
    setCompareDiffConsistency(undefined);
    setCompareBehaviorLabel(undefined);
    setCompareBehaviorStrong(undefined);
    // 原因列/筛选都依附于评测器：评测器清了，原因筛选也一并清。
    setCompareFilterReasonDiff(undefined);
    setCompareFilterReasonCode(undefined);
    setCompareFilterMetricKey(undefined);
    setCompareFilterMetricCompare(undefined);
    // 排序依附于指标列：指标清了，表头箭头也该收起（否则箭头亮着却无从排序）。
    setCompareFilterMetricSort(undefined);
    setCompareMetricDefs([]);
    setCompareCommentFilter(EMPTY_COMMENT_FILTER);
    setCompareSideCommentFilter(EMPTY_COMMENT_FILTER);
    setCommentCombine('and');
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, { diffMode: undefined });
    }
  };

  // 点顶部「整体 2×2 矩阵」象限 → 场景级整体 pass/fail 联动筛选（_diff_scene_ids 口径）。
  // 这是与评测器矩阵互斥的另一种下钻：清掉评测器联动选择，避免两套口径叠加打架。
  const applyCompareDiffMode = async (mode?: TaskDiffMode, sceneSetId?: number) => {
    setCompareFilterSceneSetId(sceneSetId);
    setCompareDiffMode(mode);
    setCompareFilterEvaluatorName(undefined);
    // 指标筛选依附于评测器，评测器被清则指标一并清（否则残留一个无评测器归属的指标）。
    setCompareFilterMetricKey(undefined);
    setCompareFilterMetricCompare(undefined);
    setCompareFilterMetricSort(undefined);
    // 原因比对也依附于评测器（每个评测器报自己的原因），评测器被清则一并清。
    setCompareFilterReasonDiff(undefined);
    // 类别筛同理依附于评测器，且与象限绑定 —— 一并清。
    setCompareFilterReasonCode(undefined);
    setCompareMetricDefs([]);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: sceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: mode,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  const applySceneSetMatrixCell = async (nodeId: number, mode: TaskDiffMode) => {
    setCompareFilterSceneSetId(nodeId);
    setCompareDiffMode(mode);
    setCompareFilterSceneName(undefined);
    setCompareFilterStatus(undefined);
    setCompareFilterEvaluatorName(undefined);
    setCompareDiffConsistency(undefined);
    setCompareBehaviorLabel(undefined);
    setCompareBehaviorStrong(undefined);
    setCompareFilterMetricKey(undefined);
    setCompareFilterMetricCompare(undefined);
    setCompareMetricDefs([]);
    setMetricDirCounts(null);
    setCompareCommentFilter(EMPTY_COMMENT_FILTER);
    setCompareSideCommentFilter(EMPTY_COMMENT_FILTER);
    setCommentCombine('and');
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, { compareSceneSetId: nodeId, diffMode: mode });
      compareDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const applyCompareSceneSet = async (nodeId?: number) => {
    setCompareFilterSceneSetId(nodeId);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: nodeId, sceneName: compareFilterSceneName, status: compareFilterStatus,
        diffMode: compareDiffMode, evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName, diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        metricKey: compareFilterMetricKey, metricCompare: compareFilterMetricCompare,
        comment: compareCommentFilter, compareSideComment: compareSideCommentFilter, commentCombine,
      });
    }
  };

  // 点「按评测器聚合矩阵」的某个象限 / 「场景数」列 → 联动筛选下方明细：
  // 按「单评测器口径」(evaluatorDiff) 圈定场景，与矩阵格子数严格对齐
  // （不再借场景级整体 pass/fail，否则会出现「矩阵 2 条、明细 1 条」错位）。
  // mode='all' 为「场景数」列联动（该评测器全部可判定场景 = 4 象限并集）。
  // 再次点同一格 → 取消该联动筛选。切换到别的评测器时，清掉旧评测器的指标下钻选择。
  const applyEvaluatorMatrixCell = async (name: string, mode: TaskDiffMode) => {
    const isToggleOff = compareFilterEvaluatorName === name && compareDiffMode === mode;
    const nextName = isToggleOff ? undefined : name;
    const nextMode = isToggleOff ? undefined : mode;
    const evaluatorChanged = nextName !== compareFilterEvaluatorName;
    setCompareFilterEvaluatorName(nextName);
    setCompareDiffMode(nextMode);
    // 指标依附于评测器：评测器变化（含取消/换名）→ 清空指标选择并刷新指标清单。
    let nextMetricKey = compareFilterMetricKey;
    let nextMetricCompare = compareFilterMetricCompare;
    let nextMetricSort = compareFilterMetricSort;
    // 原因比对依附于评测器：换名 / 取消（evaluatorChanged）→ 清空原因筛选，
    // 否则会留下一个属于旧评测器的「原因不一致」条件去筛新评测器的场景。
    let nextReasonDiff = compareFilterReasonDiff;
    // 原因**类别**筛（reasonCode）同理依附于评测器；另外它还依附于**象限**：
    // fail_fail 下选的是失败原因，切到 pass_pass 后那组选项整个不显示了，
    // 不清就会留下一个隐形筛选（见 reasonCodeSurvivesMode）。
    let nextReasonCode = compareFilterReasonCode;
    if (evaluatorChanged) {
      nextMetricKey = undefined;
      nextMetricCompare = undefined;
      nextMetricSort = undefined;
      nextReasonDiff = undefined;
      nextReasonCode = undefined;
      setCompareFilterMetricKey(undefined);
      setCompareFilterMetricCompare(undefined);
      setCompareFilterMetricSort(undefined);
      setCompareFilterReasonDiff(undefined);
      setCompareFilterReasonCode(undefined);
      setMetricDirCounts(null);
      // 动态拉取该评测器实际上报过的指标清单（纯结构化，评估器有什么就有什么）。
      if (nextName && compareJobIdRef.current) {
        compareDailyJobsEvaluatorMetrics(jobId, compareJobIdRef.current, nextName)
          .then((defs) => setCompareMetricDefs(defs))
          .catch((err) => { console.error('加载评测器指标清单失败:', err); setCompareMetricDefs([]); });
      } else {
        setCompareMetricDefs([]);
      }
    } else if (!reasonCodeSurvivesMode(nextMode, nextReasonCode)) {
      nextReasonCode = undefined;
      setCompareFilterReasonCode(undefined);
    }
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: nextMode,
        evaluatorName: nextName,
        evaluatorDiff: !!nextName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: nextReasonDiff,
        reasonCode: nextReasonCode,
        metricKey: nextMetricKey,
        metricCompare: nextMetricCompare,
        metricSort: nextMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  /**
   * 已选评测器后，在「结果类型」下拉里换象限：**评测器保持不变**，只换 diffMode。
   *
   * 与 applyEvaluatorMatrixCell 的区别：那个是「点矩阵格子」（换评测器 + 象限，可取消），
   * 这个只改象限。清空象限（allowClear）回到 `all` = 该评测器全部可判定场景，与评测器下拉
   * 选中时的语义一致，而不是退成无象限（那会让评测器联动失去 diffMode 门控）。
   *
   * 评测器没换 → 指标 / 指标排序 / 原因判定桶都仍然成立，保留；只有「原因类别」绑定象限
   * （失败原因 vs 豁免原因），换象限后不在新下拉里的类别要清（见 reasonCodeSurvivesMode）。
   * 评测器没变，故**不重拉**指标清单、`reasonDiffMap` 也不动（判定只取决于评测器）。
   */
  const applyEvaluatorQuadrant = async (mode?: TaskDiffMode) => {
    const nextMode: TaskDiffMode = mode ?? 'all';
    setCompareDiffMode(nextMode);
    let nextReasonCode = compareFilterReasonCode;
    if (!reasonCodeSurvivesMode(nextMode, nextReasonCode)) {
      nextReasonCode = undefined;
      setCompareFilterReasonCode(undefined);
    }
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: nextMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: compareFilterReasonDiff,
        reasonCode: nextReasonCode,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  // 筛选栏评测器下拉框选中：行为与矩阵点击类似，但 diffMode 固定 'all'
  //（该评测器全部可判定场景），不限定象限。供指标型评测器等无法从矩阵选中的场景使用。
  const handleEvalFilterSelect = async (name: string | undefined) => {
    const evaluatorChanged = name !== compareFilterEvaluatorName;
    setCompareFilterEvaluatorName(name);
    // 下拉框选评测器不限定象限 → diffMode='all'（该评测器全部可判定场景）
    setCompareDiffMode(name ? 'all' : undefined);
    // 指标依附于评测器：评测器变化（含取消/换名）→ 清空指标选择并刷新指标清单。
    let nextMetricKey = compareFilterMetricKey;
    let nextMetricCompare = compareFilterMetricCompare;
    let nextMetricSort = compareFilterMetricSort;
    // 同 applyEvaluatorMatrixCell：原因是评测器维度的，换评测器即清原因筛选。
    let nextReasonDiff = compareFilterReasonDiff;
    // 也不限定象限（diffMode='all'）→ 两类别组都展示，任何已选类别都仍在选项里，
    // 故这里**不需要** reasonCodeSurvivesMode 那一层；只有换评测器才清。
    let nextReasonCode = compareFilterReasonCode;
    if (evaluatorChanged) {
      nextMetricKey = undefined;
      nextMetricCompare = undefined;
      nextMetricSort = undefined;
      nextReasonDiff = undefined;
      nextReasonCode = undefined;
      setCompareFilterMetricKey(undefined);
      setCompareFilterMetricCompare(undefined);
      setCompareFilterMetricSort(undefined);
      setCompareFilterReasonDiff(undefined);
      setCompareFilterReasonCode(undefined);
      setMetricDirCounts(null);
      if (name && compareJobIdRef.current) {
        compareDailyJobsEvaluatorMetrics(jobId, compareJobIdRef.current, name)
          .then((defs) => setCompareMetricDefs(defs))
          .catch((err) => { console.error('加载评测器指标清单失败:', err); setCompareMetricDefs([]); });
      } else {
        setCompareMetricDefs([]);
      }
    }
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: name ? 'all' : compareDiffMode,
        evaluatorName: name,
        evaluatorDiff: !!name,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: nextReasonDiff,
        reasonCode: nextReasonCode,
        metricKey: nextMetricKey,
        metricCompare: nextMetricCompare,
        metricSort: nextMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  // 切换指标下钻的「指标」或「对比类型」：两者齐全（且已选评测器）才生效筛选，
  // 保留其它筛选条件。缺方向时仅切「差异项」列展示、不做跨分页筛选。
  const applyMetricFilter = async (metricKey?: string, metricCompare?: MetricCompare) => {
    setCompareFilterMetricKey(metricKey);
    setCompareFilterMetricCompare(metricCompare);
    // 换指标时**保留**排序方向：用户「按差值从小到大看」的意图在换指标后同样成立，
    // 每换一次就重置回 desc 反而要来回点两下。清空指标时才把排序一并清掉。
    if (!metricKey) setCompareFilterMetricSort(undefined);
    setComparePage(1);
    // 选中指标即拉「增大/缩小/持平」方向计数（共有场景全集）；清空指标则清计数。
    // 指标未变时不重拉（点方向 chip 只切 metricCompare、不改 metricKey，计数不变）。
    if (metricKey && metricKey !== compareFilterMetricKey && compareFilterEvaluatorName && compareJobIdRef.current) {
      compareDailyJobsMetricDirectionCounts(jobId, compareJobIdRef.current, compareFilterEvaluatorName, metricKey)
        .then((c) => setMetricDirCounts(c))
        .catch((err) => { console.error('加载指标方向计数失败:', err); setMetricDirCounts(null); });
    } else if (!metricKey) {
      setMetricDirCounts(null);
    }
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: compareFilterReasonDiff,
        reasonCode: compareFilterReasonCode,
        metricKey,
        metricCompare,
        metricSort: metricKey ? compareFilterMetricSort : undefined,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  /**
   * 切换「原因」筛选的**判定桶**（两侧原因一致 / 不一致 / 仅当前）。
   *
   * 与 applyMetricFilter 同构：改筛选即回第 1 页并重载列表。**不重拉** reasonDiffMap ——
   * 判定只取决于「哪两个 Job + 哪个评测器」，与筛哪个桶无关（后端两出口共用一份判定源）。
   *
   * 未选评测器时后端会静默忽略该参数（`reason_active` 要求 evaluatorName），前端此处也
   * 一并挡掉，避免发出一个必然被忽略的条件让用户以为筛过了。
   *
   * 与 `applyReasonCodeFilter`（按原因类别）在 UI 上共用同一个下拉、**互斥**：这里会
   * 顺手清掉 `reasonCode`。后端两个参数本身正交可叠加，只是本期不给叠加入口。
   */
  const applyReasonDiffFilter = async (value?: ReasonDiff) => {
    setCompareFilterReasonDiff(value);
    // 判定桶与类别位在下拉里是同一个控件的两种取值 → 互斥，选一个即放弃另一个。
    setCompareFilterReasonCode(undefined);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: compareFilterEvaluatorName ? value : undefined,
        reasonCode: undefined,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  /**
   * 切换「原因」筛选的**类别位**：只留**当前侧**该评测器上报的指定 `reason.code`。
   *
   * 用途正是用户点名的那两个场景：`fail_fail` / `pass_fail`（当前侧未通过）里按
   * 「为什么挂」聚类；`fail_pass` / `pass_pass`（当前侧通过）里按「为什么被豁免」聚类。
   * 选项清单来自 `reasonCatalog`（评估器自描述），**不维护注册表**。
   *
   * 与 `applyReasonDiffFilter` 同构：回第 1 页、重载列表、清掉另一维（互斥）、
   * 未选评测器时前后端都挡掉（后端 `reason_code_active` 要求 evaluatorName）。
   */
  const applyReasonCodeFilter = async (code?: string) => {
    setCompareFilterReasonCode(code);
    setCompareFilterReasonDiff(undefined);
    setComparePage(1);
    if (compareJobIdRef.current) {
      await loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
        compareSceneSetId: compareFilterSceneSetId,
        sceneName: compareFilterSceneName,
        status: compareFilterStatus,
        diffMode: compareDiffMode,
        evaluatorName: compareFilterEvaluatorName,
        evaluatorDiff: !!compareFilterEvaluatorName,
        diffConsistency: compareDiffConsistency,
        behaviorLabel: compareBehaviorLabel,
        behaviorStrong: compareBehaviorStrong,
        reasonDiff: undefined,
        reasonCode: compareFilterEvaluatorName ? code : undefined,
        metricKey: compareFilterMetricKey,
        metricCompare: compareFilterMetricCompare,
        metricSort: compareFilterMetricSort,
        comment: compareCommentFilter,
        compareSideComment: compareSideCommentFilter,
        commentCombine,
      });
    }
  };

  /**
   * 「原因」下拉的统一入口：选项值带 `code:` 前缀的是**类别**，否则是**判定桶**。
   * 清空（allowClear）走判定桶分支传 undefined，两个 state 一起归零。
   */
  const handleReasonFilterChange = (v?: string) => {
    if (v && v.startsWith(REASON_CODE_VALUE_PREFIX)) {
      void applyReasonCodeFilter(v.slice(REASON_CODE_VALUE_PREFIX.length));
      return;
    }
    void applyReasonDiffFilter((v || undefined) as ReasonDiff | undefined);
  };

  /**
   * 导出报表：整份 CSV 由后端生成（POST /task/export，流式）。
   *
   * 旧实现在前端拉全量任务 + 逐场景拉评论 + 逐任务拉评估项，再拼 CSV：
   * 大 Job 下是几百次请求 + 整份数据进浏览器内存。现在只发一个请求，
   * 行集与列表共用后端同一条过滤链（传同一份 queryParams），分页字段
   * 后端忽略，导出的是全部命中行。
   */
  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      const { blob, filename } = await exportTaskReport(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      message.success('报表导出成功');
    } catch (err) {
      console.error('导出报表失败:', err);
      message.error(err instanceof Error && err.message ? err.message : '导出报表失败');
    } finally {
      setExportLoading(false);
    }
  };

  // 对比表状态渲染：复用 JobStatusTag 保持与主表一致；空 task 用占位符
  const renderStatusText = (task: TaskListItem | null | undefined) => {
    if (!task || !task.statusName) {
      return <span style={{ color: '#d1d5db' }}>—</span>;
    }
    return <JobStatusTag status={task.status} statusName={task.statusName} />;
  };

  // 渲染预览图（固定容器尺寸，避免加载后行高跳动）
  const comparePreviewCellStyle: React.CSSProperties = {
    width: 160,
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  };

  /**
   * 对比模式 preview cell 渲染。
   * 点击放大 → 触发 ``setCompareLightboxRow(row)``，弹左右双图对比 modal；
   * 与 antd Image 默认单图 preview 区别在于「双侧同步看」，这是对比页核心需求。
   * ``row`` 里同时含 currentTask + compareTask，供 modal 拿到对侧 taskId。
   */
  const renderComparePreview = (taskId: number | undefined, row?: CompareRow) => {
    if (!taskId) return <div style={comparePreviewCellStyle}><span style={{ color: '#d1d5db' }}>—</span></div>;
    const url = comparePreviewUrlMap[taskId];
    if (!url) {
      if (comparePreviewDone) return <div style={comparePreviewCellStyle}><span style={{ color: '#9b9da3' }}>-</span></div>;
      return <div style={comparePreviewCellStyle}><Spin size="small" /></div>;
    }
    return (
      <div
        style={{ ...comparePreviewCellStyle, position: 'relative', cursor: 'pointer' }}
        onClick={() => row && setCompareLightboxRow(row)}
        title="点击放大对比"
      >
        <img
          src={url}
          alt="预览"
          width={160}
          height={100}
          style={{ display: 'block', objectFit: 'cover', borderRadius: 4 }}
        />
        {/* hover 半透明遮罩（去掉文字，避免挡住图像内容；点击仍能放大对比） */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.25)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0)'; }}
        />
      </div>
    );
  };

  /**
   * 「按场景分类」抽屉内容。
   *
   * 布局拆分（用户要求 v2）：
   * - 顶部：「责任分布」一行汇总，全 job 维度（与 label 解耦），
   *   左侧 split bar + 右侧 `有责 N · 无责 M · X%` 三字段
   * - 下方：「按场景标签」紧凑表，仅含 总数 / 通过率 / 不通过 / 异常，不再含责任列
   *
   * 紧凑度：行高 24px、padding `2px 10px`、字号 11/12、进度条 4px、
   * 双段 split bar 4px，整体信息密度按 Linear 风格压到极致。
   */
  const renderLabelBreakdownPanel = () => {
    return (
      <div
        style={{
          margin: '-8px 0 16px',
          background: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        }}
      >
        {labelBreakdownLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        ) : !labelBreakdown || labelBreakdown.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9b9da3', fontSize: 12 }}>
            暂无场景分类数据
          </div>
        ) : (
          (() => {
            // 全 job 责任汇总：直接由 items 累加，避免后端二次请求
            const sumTotal = labelBreakdown.reduce((s, it) => s + (it.totalCount || 0), 0);
            const sumRespWith = labelBreakdown.reduce((s, it) => s + (it.respWithCount || 0), 0);
            const sumRespWithout = Math.max(sumTotal - sumRespWith, 0);
            const respRate = sumTotal > 0 ? sumRespWith / sumTotal : 0;
            // 极小但 >0 时 toFixed(1) 会截成 "0.0%"，与"无有责"歧义；显式标记 `<0.1%`
            const respRatePct = respRate * 100;
            const respRateLabel =
              respRatePct < 0.1 ? '<0.1%' : `${respRatePct.toFixed(1)}%`;
            const hasResp = sumRespWith > 0;
            return (
              <>
                {/* 顶部责任分布汇总条（全 job 维度，与 label 解耦）
                    设计放弃 split bar：有责率天然稀有（< 1%），ratio 进度条退化成
                    一条死灰线，徒占空间没信息。改用 "告警徽章" 范式：
                    - 有责数 > 0 → 红色软徽章 + 圆点 + 数字 + 百分比，作为强警示
                    - 有责数 = 0 → 绿色软徽章 + ✓，明确传达「全部无责」
                    - 无责数 → 退到灰字背景，仅作为分母上下文
                    分母 = 全 job task 总数；分子 = 有责 task 数（EXISTS evaluator.type==2） */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    // 左右 padding 与下方 label 表 th/td 一致（10px），让"责任分布"
                    // 左缘与"场景标签"列头左缘对齐，右侧"无责/共"贴右与"异常"列右缘对齐
                    padding: '6px 10px',
                    background: '#fafafa',
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: 12,
                    fontVariantNumeric: 'tabular-nums',
                    color: '#374151',
                  }}
                >
                  <span
                    style={{ color: '#6b6f76', fontWeight: 500, flex: '0 0 auto' }}
                    title="task 中存在「有责」有无责事件（type=2 且 pass=false）的占比；分母为全 Job task 总数"
                  >
                    责任分布
                  </span>
                  {hasResp ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '1px 8px',
                        borderRadius: 4,
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        fontWeight: 600,
                        lineHeight: '18px',
                      }}
                      title={`有责 task ${sumRespWith} / 合计 ${sumTotal}`}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: '#dc2626',
                          display: 'inline-block',
                        }}
                      />
                      有责 {sumRespWith}
                      <span style={{ color: '#fca5a5', fontWeight: 400 }}>·</span>
                      <span style={{ fontWeight: 500 }}>{respRateLabel}</span>
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '1px 8px',
                        borderRadius: 4,
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        color: '#16a34a',
                        fontWeight: 600,
                        lineHeight: '18px',
                      }}
                      title="全部 task 均无有责事件"
                    >
                      ✓ 无有责
                    </span>
                  )}
                  {/* spacer 推到右侧；让 无责 / 共 与表格的右数列右缘对齐 */}
                  <span style={{ flex: 1 }} />
                  <span style={{ color: '#9ca3af', fontSize: 11, flex: '0 0 auto' }}>
                    无责 {sumRespWithout} / 共 {sumTotal}
                  </span>
                </div>

                {/* 按场景标签 紧凑表 */}
                <div style={{ maxHeight: 320, overflow: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 12,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          position: 'sticky',
                          top: 0,
                          background: '#f9fafb',
                          color: '#6b6f76',
                          fontSize: 11,
                          fontWeight: 500,
                          textAlign: 'left',
                          borderBottom: '1px solid #e5e7eb',
                          zIndex: 1,
                        }}
                      >
                        <th style={{ padding: '4px 10px', minWidth: 160 }}>场景标签</th>
                        <th style={{ padding: '4px 10px', width: 56, textAlign: 'right' }}>总数</th>
                        <th style={{ padding: '4px 10px', width: 220 }}>通过率</th>
                        <th style={{ padding: '4px 10px', width: 56, textAlign: 'right' }}>不通过</th>
                        <th style={{ padding: '4px 10px', width: 56, textAlign: 'right' }}>异常</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labelBreakdown.map((it) => {
                        const total = it.totalCount || 0;
                        const finished = it.passCount + it.failCount + it.exceptionCount;
                        const passRate = finished > 0 ? it.passCount / finished : 0;
                        return (
                          <tr
                            key={it.label}
                            style={{
                              borderBottom: '1px solid #f3f4f6',
                              color: '#1f2937',
                              height: 24,
                            }}
                          >
                            <td style={{ padding: '2px 10px', maxWidth: 240 }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '0 6px',
                                  borderRadius: 3,
                                  background: it.label === '未分类' ? '#f3f4f6' : '#eef2ff',
                                  color: it.label === '未分类' ? '#6b7280' : '#4338ca',
                                  fontSize: 11,
                                  fontWeight: 500,
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  lineHeight: '18px',
                                }}
                                title={it.label}
                              >
                                {it.label}
                              </span>
                            </td>
                            <td style={{ padding: '2px 10px', textAlign: 'right', fontWeight: 600 }}>
                              {total}
                            </td>
                            <td style={{ padding: '2px 10px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div
                                  style={{
                                    flex: 1,
                                    height: 4,
                                    background: '#f3f4f6',
                                    borderRadius: 999,
                                    overflow: 'hidden',
                                    minWidth: 50,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${(passRate * 100).toFixed(1)}%`,
                                      height: '100%',
                                      background: '#16a34a',
                                      transition: 'width 200ms',
                                    }}
                                  />
                                </div>
                                <span style={{ fontWeight: 600, color: '#16a34a', minWidth: 42, textAlign: 'right' }}>
                                  {finished > 0 ? `${(passRate * 100).toFixed(1)}%` : '-'}
                                </span>
                                <span style={{ color: '#9ca3af', fontSize: 11, minWidth: 56, textAlign: 'right' }}>
                                  {it.passCount}/{finished || '-'}
                                </span>
                              </div>
                            </td>
                            <td style={{ padding: '2px 10px', textAlign: 'right', color: it.failCount > 0 ? '#d97706' : '#6b7280' }}>
                              {it.failCount}
                            </td>
                            <td style={{ padding: '2px 10px', textAlign: 'right', color: it.exceptionCount > 0 ? '#ef4444' : '#6b7280' }}>
                              {it.exceptionCount}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()
        )}
      </div>
    );
  };

  /**
   * 「按评测器聚合明细」面板（statsMode 专用）。
   *
   * 单 Job 版评测器矩阵：每个评测器一行，展示 场景数（占总）/ pass / fail / error，
   * 样式参考「任务对比」评测器矩阵（同一套表头/配色/百分比胶囊）。pass/fail 口径与对比页
   * 评测器矩阵单侧一致，唯独把异常终态（执行异常/系统异常/coredump）从 fail 拆出、单列
   * 为 error。仅展示评测器（名字以 ``_evaluator`` 结尾），过滤 meta_info_computer 等
   * 非评测器统计项，以及 _check 检查项（type=3 独立成类，不进评测器明细）。
   */
  const renderEvaluatorStatsPanel = () => {
    const totalScene = evaluatorStats?.totalSceneCount ?? 0;
    // 已登记评估器参数聚合声明的评估器（纯统计型 / 通过型均可），从 pass/fail/error 表拆出，
    // 改到下方「评估器参数」卡片里点选查看其参数的聚合指标。
    const aggNames = new Set(
      metricAggSpecs.map((s) => canonicalEvaluatorName(s.evaluator)),
    );
    const rows = (evaluatorStats?.evaluators ?? []).filter(
      (r) =>
        isEvaluatorName(r.evaluatorName) &&
        !aggNames.has(canonicalEvaluatorName(r.evaluatorName)),
    );
    // 与对比页 fmtPct 一致：分母为 0 显示 '-'
    const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '-');
    // pass / fail / error 三列：对齐对比页象限列的「彩色下边框 + 彩色表头」风格。
    // fail 只含评测器真实回传失败；异常终态（执行异常/系统异常/coredump）单列 error。
    const resultCols: {
      key: 'pass' | 'fail' | 'error';
      label: string;
      color: string;
      get: (r: { passCount: number; failCount: number; errorCount: number }) => number;
    }[] = [
      { key: 'pass', label: 'pass', color: '#16a34a', get: (r) => r.passCount },
      { key: 'fail', label: 'fail', color: '#d97706', get: (r) => r.failCount },
      { key: 'error', label: 'error', color: '#dc2626', get: (r) => r.errorCount },
    ];
    return (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500 }}>
            按评测器聚合明细
            <Tooltip
              title={
                <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 320 }}>
                  <div>单 Job 各评测器（type=1）的 pass / fail / error 统计。</div>
                  <div>· 场景数 = 该评测器 pass + fail + error 的场景数。</div>
                  <div>· 占比 = 场景数 / 本 Job 总场景数（{totalScene}）。</div>
                  <div>· pass / fail 按差分口径推导（算法侧只上报 fail，pass 由场景评测器全集差分）。</div>
                  <div style={{ color: '#fca5a5' }}>· error = 异常终态（执行异常/系统异常/coredump），单列、不计入 fail。</div>
                </div>
              }
              placement="topLeft"
              styles={{ root: { maxWidth: 360 } }}
            >
              <QuestionCircleOutlined style={{ marginLeft: 6, color: '#c2c4c9', cursor: 'help' }} />
            </Tooltip>
            <span style={{ fontSize: 11, color: '#c2c4c9', marginLeft: 8 }}>
              pass / fail / error 百分比占该评测器场景数（点击格子联动筛选下方列表）
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {evalCellFilter && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                color: '#5e6ad2', background: '#eef0fb', borderRadius: 6, padding: '2px 4px 2px 8px',
              }}>
                已联动：{getEvaluatorDisplay(evalCellFilter.name)} · {evalCellFilter.verdict}
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => { void applyEvaluatorCellFilter(evalCellFilter.name, evalCellFilter.verdict); }}
                >
                  清除
                </Button>
              </span>
            )}
            <span style={{ fontSize: 12, color: '#6b6f76' }}>总场景数 {totalScene}</span>
          </span>
        </div>
        <Spin spinning={evaluatorStatsLoading}>
          {rows.length > 0 ? (
            <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 690 }}>
                <thead>
                  <tr style={{ background: '#fafafa', color: '#6b6f76', fontWeight: 500 }}>
                    <th style={{
                      padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #ebebeb',
                      position: 'sticky', top: 0, background: '#fafafa', zIndex: 1,
                    }}>
                      评测器
                      <Tooltip title="评测器名（已统一归一为 snake_case，CamelCase 与 snake_case 视为同一评测器）">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#c2c4c9', cursor: 'help' }} />
                      </Tooltip>
                    </th>
                    <th style={{
                      padding: '8px 12px', textAlign: 'right', width: 168, minWidth: 168, whiteSpace: 'nowrap',
                      borderBottom: '1px solid #ebebeb', position: 'sticky', top: 0, background: '#fafafa', zIndex: 1,
                    }}>
                      场景数
                      <span style={{ fontWeight: 400, color: '#c2c4c9' }}>（占总）</span>
                      <Tooltip title="该评测器在本 Job 中的场景数（= pass + fail + error）；百分比 = 占本 Job 总场景数的比例">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#c2c4c9', cursor: 'help' }} />
                      </Tooltip>
                    </th>
                    {resultCols.map((c) => (
                      <th key={c.key} style={{
                        padding: '8px 12px', textAlign: 'right', width: 130,
                        borderBottom: `2px solid ${c.color}`, color: c.color, fontWeight: 600,
                        position: 'sticky', top: 0, background: '#fafafa', zIndex: 1,
                      }}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.evaluatorName}>
                      <td style={{
                        padding: '10px 12px', color: '#1d1d1f', fontWeight: 500,
                        borderBottom: '1px solid #f5f5f5',
                        maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={r.evaluatorName}>
                        {getEvaluatorDisplay(r.evaluatorName)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f5f5f5', fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{r.sceneCount}</span>
                        <span style={{ fontSize: 11, color: '#9b9da3', marginLeft: 4 }}>
                          {pct(r.sceneCount, totalScene)}
                        </span>
                      </td>
                      {resultCols.map((c) => {
                        const val = c.get(r);
                        const clickable = val > 0;
                        const selected =
                          evalCellFilter?.name === r.evaluatorName && evalCellFilter?.verdict === c.key;
                        return (
                          <td
                            key={c.key}
                            onClick={clickable ? () => { void applyEvaluatorCellFilter(r.evaluatorName, c.key); } : undefined}
                            title={clickable ? (selected ? '点击取消联动筛选' : `点击筛选该评测器 ${c.label} 的场景`) : undefined}
                            style={{
                              padding: '10px 12px', textAlign: 'right',
                              borderBottom: '1px solid #f5f5f5', fontVariantNumeric: 'tabular-nums',
                              cursor: clickable ? 'pointer' : 'default',
                              background: selected ? `${c.color}1a` : undefined,
                              boxShadow: selected ? `inset 0 0 0 1.5px ${c.color}` : undefined,
                              transition: 'background 0.15s',
                            }}
                          >
                            <span style={{
                              fontWeight: 600,
                              color: val > 0 ? c.color : '#c2c4c9',
                              textDecoration: clickable ? 'underline dotted' : undefined,
                              textUnderlineOffset: 3,
                            }}>{val}</span>
                            <span style={{ fontSize: 11, color: '#9b9da3', marginLeft: 4 }}>
                              {pct(val, r.sceneCount)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : evaluatorStatsLoading ? null : (
            <Alert type="info" showIcon message="暂无可聚合的评测器数据（评测器需在本 Job 场景上给出可判定结果）" />
          )}
        </Spin>
      </div>
    );
  };

  /**
   * 「评估器参数」聚合卡片（statsMode 专用）。
   *
   * 纯统计型评估器（恒定只出 success，价值全在结构化 metrics）的聚合视图。
   * 它们已从上面的 pass/fail/error 表拆出，改在此卡片里：顶部一个评估器点选器
   * （候选 = 系统配置登记了聚合声明 ∩ 本 Job 实际跑过的评估器），选中后展示：
   *   · 总体 KPI 区：∑计数键 + 派生率（率 = ∑分子/∑分母×scale，∑分母=0 → 「—」）；
   *   · 逐场景明细表：3B 分布键缺席渲染「—」而非 0；未触发场景（trigger_key=0）标灰。
   * 数据源：/daily/job/evaluator-metric-agg。未登记任何评估器时整卡不渲染。
   */
  const renderMetricAggPanel = () => {
    if (jobMetricAggSpecs.length === 0) return null;

    // 逐场景明细表列：sceneId / sceneName / 各 metric key（求和键 + 分布键联合）。
    // 列集合取自当前返回的 scenes（metrics 的 key 并集，保持后端顺序稳定）。
    const scenes = metricAggData?.scenes ?? [];
    const metricKeys: string[] = [];
    const seen = new Set<string>();
    for (const s of scenes) {
      for (const k of Object.keys(s.metrics)) {
        if (!seen.has(k)) {
          seen.add(k);
          metricKeys.push(k);
        }
      }
    }
    // summary 里能拿到每个 key 的展示 label / 是否派生率（率的 key 不在 metrics 里）。
    const summary = metricAggData?.summary ?? [];
    const labelOf = (key: string): string => {
      const item = summary.find((m) => m.key === key);
      return item?.label && item.label !== key ? item.label : key;
    };

    const detailColumns = [
      {
        title: '场景',
        dataIndex: 'sceneName',
        key: 'sceneName',
        fixed: 'left' as const,
        width: 220,
        render: (name: string, row: EvaluatorMetricAggSceneRow) => (
          <span style={{ color: row.triggered ? undefined : '#9b9da3' }}>
            {name}
            {!row.triggered && (
              <Tag style={{ marginLeft: 6 }} color="default">未触发</Tag>
            )}
          </span>
        ),
      },
      ...metricKeys.map((k) => ({
        title: labelOf(k),
        dataIndex: ['metrics', k],
        key: k,
        align: 'right' as const,
        width: 140,
        // 3B 缺席键：该行 metrics 里没有此 key → 渲染 EMPTY（区别于合法测值 0）。
        render: (_: unknown, row: EvaluatorMetricAggSceneRow) =>
          k in row.metrics ? fmtCount(row.metrics[k]) : EMPTY,
        // null-aware 排序：缺席值排在最后（-Infinity 占位）。
        sorter: (a: EvaluatorMetricAggSceneRow, b: EvaluatorMetricAggSceneRow) =>
          (k in a.metrics ? a.metrics[k] : -Infinity) -
          (k in b.metrics ? b.metrics[k] : -Infinity),
      })),
    ];

    return (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500 }}>
            评估器参数
            <Tooltip
              title={
                <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 340 }}>
                  <div>纯统计型评估器恒定只出 success，价值全在结构化 metrics（此处按登记的聚合声明汇总）。</div>
                  <div>· 派生率口径：计数先跨场景求和、比率由求和后计数重算（Σ分子/Σ分母），不是各场景比率的平均。</div>
                  <div>· 「—」= 该场景无样本（分布键缺席），区别于合法测值 0。</div>
                  <div>· 未触发 = 该评估器 trigger_key 为 0 的空场。</div>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ marginLeft: 6, color: '#c2c4c9' }} />
            </Tooltip>
          </span>
          <Select
            style={{ minWidth: 200 }}
            value={selectedMetricEvaluator ?? undefined}
            onChange={(v) => handleSelectMetricEvaluator(String(v))}
            options={jobMetricAggSpecs.map((s) => ({ label: s.label, value: s.evaluator }))}
            placeholder="选择评估器参数"
          />
        </div>
        <Spin spinning={metricAggLoading}>
          {selectedMetricEvaluator == null ? (
            <Empty description="选择评估器参数查看聚合指标" />
          ) : (
            <>
              {/* —— 总体 KPI 区：∑计数键 + 派生率 —— */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12,
              }}>
                {summary.length === 0 && !metricAggLoading ? (
                  <span style={{ fontSize: 12, color: '#9b9da3' }}>暂无聚合指标</span>
                ) : (
                  summary.filter((m) => !m.hidden).map((m) => {
                    // 计数键（unit=count）显示千分位整数；派生率显示 label 里带单位、null→「—」。
                    const isRate = m.unit !== 'count';
                    const text = m.value === null
                      ? EMPTY
                      : isRate
                        ? (m.unit === 'ratio' ? fmtRatio(m.value) : m.value.toLocaleString('zh-CN', { maximumFractionDigits: 3 }))
                        : fmtCount(m.value);
                    return (
                      <div key={m.key} style={{
                        minWidth: 140,
                        padding: '8px 12px',
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 6,
                      }}>
                        <div style={{ fontSize: 12, color: '#9b9da3', marginBottom: 4 }}>
                          {m.label}
                          {isRate && m.unit && m.unit !== 'ratio' && (
                            <span style={{ marginLeft: 4 }}>({m.unit})</span>
                          )}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2329' }}>
                          {text}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ fontSize: 12, color: '#9b9da3', marginBottom: 8 }}>
                共 {metricAggData?.sceneCount ?? 0} 场景，其中已触发 {metricAggData?.triggeredCount ?? 0} 场。
              </div>
              {/* —— 图表区：按 spec.charts 声明，数据取上面同一份 summary —— */}
              <MetricAggCharts charts={metricAggData?.charts ?? []} summary={summary} />
              {/* —— 逐场景明细表：默认收起，点击才渲染（场景多时避免一进页面就撑慢） —— */}
              <Button
                type="link"
                size="small"
                style={{ paddingLeft: 0 }}
                icon={metricAggDetailOpen ? <UpOutlined /> : <DownOutlined />}
                onClick={handleMetricAggDetailToggle}
              >
                {metricAggDetailOpen ? '收起逐场景明细' : `展开逐场景明细（${scenes.length}）`}
              </Button>
              {metricAggDetailOpen && (
                <Table<EvaluatorMetricAggSceneRow>
                  size="small"
                  rowKey="sceneId"
                  columns={detailColumns}
                  dataSource={scenes}
                  scroll={{ x: 'max-content' }}
                  pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
                  locale={{ emptyText: <Empty description="暂无场景数据" /> }}
                  style={{ marginTop: 8 }}
                />
              )}
            </>
          )}
        </Spin>
      </div>
    );
  };

  /**
   * task 列表「仿真结果」列的紧凑 chip。
   *
   * 语义（与 evaluation_service.get_statics_by_ids 对齐）：
   * - n = evaluation_static.failedCount（type=1 评测器中 result=Failed 的行数，即
   *   「未通过评测器数」）。**不用 evaluatorCount（总行数）**：纯统计类评测器
   *   （如 lane_change_trigger_evaluator）恒上报 Success 行，会把总数抬到 ≥1，
   *   导致明明全 Success 的任务被误染黄。按失败数着色才对应「这行有没有问题」。
   * - m = evaluation_static.respEventCount（type=2 respEvent 行数）
   *
   * 颜色：
   * - status > 20（异常）→ 红色，文案「任务异常」，仍可点击查看明细
   * - 无 evaluation_static（未回传评测）→ 灰色「-」，不可点击
   * - n === 0 && m === 0 → 绿色「评测 0  有无责 0」
   * - 其它 → 黄色「评测 n  有无责 m」
   *
   * 风格：Linear 紧凑 chip，inline-flex，色彩点缀而非大面积铺色。
   */
  const renderEvaluatorChip = (record: TaskListItem) => {
    const isException = (record.status ?? 0) > 20;
    const stat = record.evaluationStatic ?? null;
    const n = stat?.failedCount ?? 0;
    const m = stat?.respEventCount ?? 0;

    // 三档语义色：rose / amber / emerald；统一深色文字 + 浅色背景 + 同色描边
    type Tone = { fg: string; bg: string; border: string };
    const TONE_RED: Tone = { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca' };
    const TONE_AMBER: Tone = { fg: '#b45309', bg: '#fffbeb', border: '#fde68a' };
    const TONE_GREEN: Tone = { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' };

    let tone: Tone;
    let label: React.ReactNode;
    if (isException) {
      tone = TONE_RED;
      label = '任务异常';
    } else if (!stat) {
      // 中性占位：未回传评测，不可点击
      return <span style={{ color: '#9b9da3' }}>-</span>;
    } else if (n === 0 && m === 0) {
      tone = TONE_GREEN;
      label = (
        <>
          评测 <strong>0</strong>
          <span style={{ margin: '0 2px', color: '#86efac' }}>·</span>
          有无责 <strong>0</strong>
        </>
      );
    } else {
      tone = TONE_AMBER;
      label = (
        <>
          评测 <strong>{n}</strong>
          <span style={{ margin: '0 2px', color: '#fcd34d' }}>·</span>
          有无责 <strong>{m}</strong>
        </>
      );
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 12,
          lineHeight: '20px',
          color: tone.fg,
          background: tone.bg,
          border: `1px solid ${tone.border}`,
          fontVariantNumeric: 'tabular-nums',
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  /**
   * 「仿真结果」列 chip 下方的碰撞速度行：仅当碰撞**且**拿到速度时渲染。
   *
   * - 无碰撞 / 异常任务 → 不渲染（null）
   * - 有碰撞 + 规划相对速度（物理/责任碰撞 msg "relative speed X m/s"）→「碰撞 4.4 m/s」
   * - 若同时拿到真实(record 侧)碰撞速度（msg "record relative speed X m/s"）→ 再追加
   *   一行「原始碰撞 X m/s」，展示回放真值里的实际碰撞速度
   * - 有碰撞但两个速度都无（碰路肩/碰栅栏等，评测数据暂无车速）→ 不渲染（null）
   *
   * 速度口径与后端 ``get_statics_by_ids`` 一致：任意碰撞评估器双源提取。
   */
  const renderCollisionLine = (record: TaskListItem) => {
    const isException = (record.status ?? 0) > 20;
    const stat = record.evaluationStatic ?? null;
    if (isException || !stat?.hasCollision) return null;
    const speed = stat.collisionRelativeSpeed;
    const recordSpeed = stat.collisionRecordSpeed;
    const hasSpeed = typeof speed === 'number' && Number.isFinite(speed);
    const hasRecordSpeed = typeof recordSpeed === 'number' && Number.isFinite(recordSpeed);
    if (!hasSpeed && !hasRecordSpeed) return null;
    const lineStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      marginTop: 4,
      fontSize: 12,
      lineHeight: '16px',
      color: '#b91c1c',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
    };
    return (
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
        {hasSpeed && (
          <span style={lineStyle} title="碰撞相对速度（规划）">
            碰撞 <strong>{(speed as number).toFixed(1)}</strong> m/s
          </span>
        )}
        {hasRecordSpeed && (
          <span style={lineStyle} title="原始碰撞相对速度（回放真值）">
            原始碰撞 <strong>{(recordSpeed as number).toFixed(1)}</strong> m/s
          </span>
        )}
      </div>
    );
  };

  /**
   * 单个 checker 明细弹层内容：只看该 checker 在最新一次评测里的回传行
   *（pickCheckerRows），逐条列 结果 / 时间 / 说明 / 结构化指标。急刹这类每次触发
   * 一行的 checker 会列出多条。通过且无回传行 = 算法侧只回传不通过项，给出说明。
   */
  const renderCheckerDetail = (record: TaskListItem, checker: CheckerVerdict) => {
    const tone = checkerVerdictTone(checker.verdict);
    const loading = checkerDetailLoadingTaskId === record.taskId && !evaluatorCache[record.taskId];
    const rows = pickCheckerRows(
      evaluatorCache[record.taskId],
      checker.name,
      record.evaluationStatic?.latestEvaluationResultId,
      canonicalEvaluatorName,
    );
    const header = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: '#374151' }}>{getEvaluatorDisplay(checker.name)}</span>
        <span
          style={{
            padding: '0 6px',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: '18px',
            color: tone.fg,
            background: tone.bg,
            border: `1px solid ${tone.border}`,
          }}
        >
          {tone.label}
        </span>
      </div>
    );
    let body: React.ReactNode;
    if (loading) {
      body = <div style={{ padding: 16, textAlign: 'center' }}><Spin size="small" /></div>;
    } else if (rows.length === 0) {
      body = (
        <div style={{ color: '#9b9da3' }}>
          {checker.verdict === 'pass'
            ? '通过：算法侧只回传不通过的项，该 checker 没有回传明细。'
            : '暂无明细'}
        </div>
      );
    } else {
      body = (
        <ol style={{ margin: 0, paddingLeft: rows.length > 1 ? 20 : 0, listStyle: rows.length > 1 ? 'decimal' : 'none' }}>
          {rows.map((row, idx) => {
            const metrics = row.metrics && typeof row.metrics === 'object'
              ? Object.entries(row.metrics as Record<string, { label?: string; value?: unknown; unit?: string; order?: number }>)
                .sort(([, a], [, b]) => (a?.order ?? 0) - (b?.order ?? 0))
              : [];
            return (
              <li key={row.id ?? idx} style={{ marginBottom: idx === rows.length - 1 ? 0 : 10 }}>
                <div><strong>结果：</strong>{row.result ?? '-'}</div>
                {row.time ? <div><strong>时间：</strong>{row.time}</div> : null}
                {row.msg ? <div style={{ wordBreak: 'break-word' }}><strong>说明：</strong>{row.msg}</div> : null}
                {metrics.length > 0 && (
                  <div>
                    <strong>指标：</strong>
                    {metrics.map(([key, m]) => (
                      <span key={key} style={{ marginRight: 10, whiteSpace: 'nowrap' }}>
                        {m?.label || key} {String(m?.value ?? '-')}{m?.unit && m.unit !== 'count' ? ` ${m.unit}` : ''}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      );
    }
    return (
      <div style={{ minWidth: 280, maxWidth: 460, maxHeight: 360, overflow: 'auto' }}>
        {header}
        {body}
      </div>
    );
  };

  /**
   * 「仿真结果」列 chip 下方逐个列出本 task 最新一次评测跑过的 checker（评测器 + 检查项）。
   * 判定与排序由后端给（evaluationStatic.checkers，fail → pass → other）；
   * 颜色：通过=绿、不通过=红、其他=灰。名称优先中文短名；点击弹出该 checker 的明细。
   *
   * 只给感知 Job 展示（PNC 及其它 simType 的仿真结果列保持原样：chip + 碰撞行）。
   */
  const renderCheckerList = (record: TaskListItem) => {
    if (!isPerceptionJob) return null;
    const checkers = record.evaluationStatic?.checkers ?? [];
    if (checkers.length === 0) return null;
    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 4,
          marginTop: 6,
          maxWidth: '100%',
        }}
      >
        {checkers.map((c) => {
          const tone = checkerVerdictTone(c.verdict);
          const key = `${record.taskId}:${c.name}`;
          return (
            <Popover
              key={`${c.type ?? 1}-${c.name}`}
              trigger="click"
              open={checkerPopoverKey === key}
              onOpenChange={(open) => handleCheckerPopoverOpenChange(open, record.taskId, c.name)}
              content={renderCheckerDetail(record, c)}
            >
              <span
                title={`${getEvaluatorDisplay(c.name)} · ${tone.label}（点击查看明细）`}
                style={{
                  padding: '0 6px',
                  borderRadius: 4,
                  fontSize: 11,
                  lineHeight: '18px',
                  color: tone.fg,
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }}
              >
                {getEvaluatorLabel(c.name)}
              </span>
            </Popover>
          );
        })}
      </div>
    );
  };

  // 渲染仿真结果摘要（status > 20 为异常状态，不展示评估结果）
  /**
   * 仿真结果 Popover 内容渲染（task 列表 + 对比页共用，保证内容一致）。
   *
   * 数据按 ORM ``type`` 分 3 段：
   * - type=1 评测器（_evaluator）：失败置顶、成功灰字
   * - type=2 有无责任（respEvent）：彩色「有责/无责」徽章 + 责任归属（ego 红字）
   * - type=3 检查项（_check）：失败置顶、成功灰字
   *
   * 旧数据可能缺 ``type`` 字段，缺省视为 1（评测器）；含 SectionTitle 等内部组件
   * 全部内联在此，避免重复定义。
   */
  const renderEvaluatorPopoverContent = (
    items: EvaluatorItem[] | null | undefined,
    loading: boolean,
  ) => {
    return (
      <div style={{ minWidth: 320, maxWidth: 480 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin size="small" />
          </div>
        ) : (() => {
          const list = items ?? [];
          if (list.length === 0) {
            return <div style={{ color: '#9b9da3' }}>暂无数据</div>;
          }
          const isSuccess = (r?: string | null) =>
            typeof r === 'string' && r.trim().toLowerCase() === 'success';
          const evaluators = list.filter((it) => (it.type ?? 1) === 1);
          const respEvents = list.filter((it) => it.type === 2);
          const checkItems = list.filter((it) => it.type === 3);
          const sortedEvals = [...evaluators].sort((a, b) => {
            const aFail = isSuccess(a.result) ? 1 : 0;
            const bFail = isSuccess(b.result) ? 1 : 0;
            return aFail - bFail;
          });
          const sortedChecks = [...checkItems].sort((a, b) => {
            const aFail = isSuccess(a.result) ? 1 : 0;
            const bFail = isSuccess(b.result) ? 1 : 0;
            return aFail - bFail;
          });
          const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
            <div
              style={{
                fontWeight: 600,
                color: '#374151',
                fontSize: 13,
                margin: '4px 0 8px',
                paddingBottom: 4,
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              {children}
            </div>
          );
          return (
            <div style={{ maxHeight: 420, overflow: 'auto' }}>
              {sortedEvals.length > 0 && (
                <>
                  <SectionTitle>评测器结果（{sortedEvals.length}）</SectionTitle>
                  <ul style={{ margin: 0, paddingLeft: 20, marginBottom: respEvents.length > 0 ? 16 : 0 }}>
                    {sortedEvals.map((item, index) => {
                      const success = isSuccess(item.result);
                      const accent = success ? '#9b9da3' : '#dc2626';
                      return (
                        <li
                          key={`ev-${item.evaluatorName ?? index}`}
                          style={{ marginBottom: 12, color: success ? '#9b9da3' : undefined }}
                        >
                          <div>
                            <strong>评估项：</strong>{getEvaluatorDisplay(item.evaluatorName)}
                            {getEvaluatorDocUrl(item.evaluatorName) && (
                              <a
                                href={getEvaluatorDocUrl(item.evaluatorName)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ marginLeft: 8 }}
                              >
                                评估器文档🔗
                              </a>
                            )}
                          </div>
                          <div>
                            <strong>结果：</strong>
                            <span style={{ color: accent, fontWeight: success ? 400 : 600 }}>
                              {item.result ?? '-'}
                            </span>
                          </div>
                          {item.msg ? <div><strong>说明：</strong>{item.msg}</div> : null}
                          {item.time ? <div><strong>时间：</strong>{item.time}</div> : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {respEvents.length > 0 && (
                <>
                  <SectionTitle>有无责任结果（{respEvents.length}）</SectionTitle>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {respEvents.map((item, index) => {
                      const respText = typeof item.responsibility === 'string'
                        ? item.responsibility
                        : '';
                      const isEgoResp = respText.trim().toLowerCase() === 'ego';
                      const liable =
                        typeof item.passed === 'boolean' ? !item.passed : null;
                      return (
                        <li
                          key={`re-${item.evaluatorName ?? index}`}
                          style={{ marginBottom: 12 }}
                        >
                          <div><strong>事件：</strong>{item.evaluatorName || '-'}</div>
                          {liable !== null && (
                            <div>
                              <strong>判定：</strong>
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '0 8px',
                                  height: 20,
                                  borderRadius: 10,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  color: liable ? '#b91c1c' : '#15803d',
                                  background: liable ? '#fef2f2' : '#f0fdf4',
                                  border: `1px solid ${liable ? '#fecaca' : '#bbf7d0'}`,
                                }}
                              >
                                <span
                                  style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: liable ? '#dc2626' : '#16a34a',
                                    display: 'inline-block',
                                  }}
                                />
                                {liable ? '有责' : '无责'}
                              </span>
                            </div>
                          )}
                          {respText ? (
                            <div>
                              <strong>责任：</strong>
                              <span
                                style={{
                                  color: isEgoResp ? '#dc2626' : '#374151',
                                  fontWeight: isEgoResp ? 600 : 400,
                                }}
                              >
                                {respText}
                              </span>
                            </div>
                          ) : null}
                          {item.msg ? <div><strong>说明：</strong>{item.msg}</div> : null}
                          {item.time ? <div><strong>时间：</strong>{item.time}</div> : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {sortedChecks.length > 0 && (
                <>
                  <SectionTitle>检查项结果（{sortedChecks.length}）</SectionTitle>
                  <ul style={{ margin: 0, paddingLeft: 20, marginTop: respEvents.length > 0 ? 16 : 0 }}>
                    {sortedChecks.map((item, index) => {
                      const success = isSuccess(item.result);
                      const accent = success ? '#9b9da3' : '#dc2626';
                      return (
                        <li
                          key={`ck-${item.evaluatorName ?? index}`}
                          style={{ marginBottom: 12, color: success ? '#9b9da3' : undefined }}
                        >
                          <div><strong>检查项：</strong>{getEvaluatorDisplay(item.evaluatorName)}</div>
                          <div>
                            <strong>结果：</strong>
                            <span style={{ color: accent, fontWeight: success ? 400 : 600 }}>
                              {item.result ?? '-'}
                            </span>
                          </div>
                          {item.msg ? <div><strong>说明：</strong>{item.msg}</div> : null}
                          {item.time ? <div><strong>时间：</strong>{item.time}</div> : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderEvaluatorSummary = (task: TaskListItem | null | undefined) => {
    if (!task) return <span style={{ color: '#d1d5db' }}>—</span>;
    if (task.status > 20) return <span style={{ color: '#9b9da3' }}>-</span>;
    const items = compareEvaluatorCache[task.taskId];
    // 触发器（chip）：通过显示绿 Tag、未通过显示红 Tag、加载中显示 Spin；
    // 点击 chip 后弹出与 task list 一致的 3 段 Popover（评测器 / 有无责任 / 检查项）。
    if (!items) return <Spin size="small" />;
    const isSuccess = (r?: string | null) =>
      typeof r === 'string' && r.trim().toLowerCase() === 'success';
    const failedItems = items.filter((e) => !isSuccess(e.result));
    const chip = failedItems.length === 0
      ? <Tag color="success">通过</Tag>
      : <Tag color="error" style={{ cursor: 'pointer' }}>{failedItems.length} 项未通过</Tag>;
    return (
      <Popover
        title="仿真结果"
        trigger="click"
        content={renderEvaluatorPopoverContent(items, false)}
      >
        {chip}
      </Popover>
    );
  };

  // 判断一行对比是否「不一致」：改读后端整场景一致性判定（compareConsistencyMap），
  // 与 /task/list?diffConsistency= 的筛选同源，避免列与筛选口径漂移、也不再依赖逐
  // task 评测明细缓存是否已加载。判定逻辑（状态 + 失败评测项名字+msg）见后端
  // daily_service._consistency_scene_map。
  const isRowDiff = (row: CompareRow): boolean => {
    const sid = row.currentTask?.sceneId;
    if (sid == null) return false;
    // only_current（仅当前 Job 有该场景）单独由「仅当前」标签展示，不算差异
    return compareConsistencyMap[sid] === 'diff';
  };

  /**
   * 从 compareEvaluatorCache 里取指定 task 的 VRU 责任碰撞评估器 relative_speed(m/s)。
   * 双源兼容 (与后端 _extract_vru_relative_speed 口径一致):
   * 1) 独立字段 relative_speed / relativeSpeed (理想上报形态)
   * 2) msg 文本里 "relative speed 4.419 m/s" 片段 (实际观测,老数据未拆字段)
   * 找不到 / 非数字返 null。
   */
  const getVruRelativeSpeed = (taskId: number | undefined): number | null => {
    if (!taskId) return null;
    const items = compareEvaluatorCache[taskId];
    if (!items || items.length === 0) return null;
    const vru = items.find((it) =>
      it.evaluatorName === 'vru_responsibility_collision_evaluator',
    );
    if (!vru) return null;
    const bag = vru as unknown as Record<string, unknown>;
    // 独立字段优先
    for (const key of ['relative_speed', 'relativeSpeed']) {
      const raw = bag[key];
      if (raw != null && raw !== '') {
        const n = typeof raw === 'number' ? raw : Number(raw);
        if (Number.isFinite(n)) return n;
      }
    }
    // 回退 msg 文本正则
    const msg = typeof bag.msg === 'string' ? bag.msg : null;
    if (msg) {
      const m = msg.match(/relative\s*speed\s*([-+]?\d+(?:\.\d+)?)\s*m\/s/i);
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n)) return n;
      }
    }
    return null;
  };

  // 根据当前 Task 列表异步加载预览图 URL
  useEffect(() => {
    const loadPreviewUrls = async () => {
      if (!data || data.length === 0) {
        setPreviewUrlMap({});
        return;
      }

      const keys: string[] = [];
      const keyToTaskId: Record<string, number> = {};

      data.forEach((item) => {
        const key = `${item.taskId}/preview_image/preview.webp`;
        keys.push(key);
        keyToTaskId[key] = item.taskId;
      });

      setPreviewLoading(true);
      try {
        const list = await getEvaluationFileUrls(keys);
        const map: Record<number, string> = {};
        list.forEach((file) => {
          const taskId = keyToTaskId[file.path];
          if (taskId) {
            map[taskId] = file.url;
          }
        });
        setPreviewUrlMap(map);
      } catch (err) {
        console.error('加载预览图URL失败:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreviewUrls();
  }, [data]);

  if (!jobId || Number.isNaN(jobId)) {
    return (
      <div style={{ padding: 24 }}>
        <p>无效的 Job ID</p>
        <Button type="link" onClick={handleBackToJobList}>
          返回
        </Button>
      </div>
    );
  }

  /**
   * 「不通过统计」页「指标」列是否出现 —— 三条件缺一不可：
   * ① statsMode：只有该页有这一列（对比页用自己的「差异项」列）；
   * ② 选了评估项：指标隶属于评估器，没有评估器就无从拉清单；
   * ③ 选了指标：**未选指标时不留空列**（用户明确要求「没选指标应该没内容」）。
   */
  const statsMetricColumnActive = !!(
    statsMode && queryParams.evaluatorName && queryParams.metricKey
  );

  const columns: any[] = [
    {
      title: 'TaskID',
      dataIndex: 'taskId',
      key: 'taskId',
      width: columnWidths.taskId,
      align: 'center',
      onHeaderCell: resizeHeaderCell('taskId'),
      render: (text: number) => text ?? '-',
    },
    {
      title: '场景名称',
      dataIndex: 'sceneName',
      key: 'sceneName',
      width: columnWidths.sceneName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('sceneName'),
      render: (text: string, record: TaskListItem) => {
        const display = text || '-';
        const isConfidential = (record.securityLevel ?? 0) > 0;
        // 场景名 → 跳转到该场景详情页；无 sceneId（老 data）时降级为纯文本。
        // 带 from=job-tasks 让详情页「返回」走 navigate(-1) 回到本 Task 列表原位置
        // （分页由本页 URL page/pageSize 记忆恢复），而不是 fallback 跳场景管理。
        const canJump = record.sceneId && record.sceneId > 0;
        const label = <MiddleEllipsis text={display} />;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            {canJump ? (
              <Link
                to={`/scene-annotate?sceneId=${record.sceneId}&from=job-tasks`}
                style={{ color: '#5e6ad2', cursor: 'pointer', minWidth: 0, flex: 1, textAlign: 'center' }}
              >
                {label}
              </Link>
            ) : label}
            {isConfidential && (
              <Tooltip title="保密：仅具备查看权限的角色可见">
                <LockFilled style={{ color: '#cf1322', fontSize: 12, flexShrink: 0 }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: '区域',
      dataIndex: 'region',
      key: 'region',
      width: columnWidths.region,
      align: 'center',
      onHeaderCell: resizeHeaderCell('region'),
      render: (text: string | null | undefined) => {
        // scene_inspection.region: cn/ae/sea；空值（老数据/异常）显示占位符。
        // 国内（绝大多数行）用中性 Tag 不抢视觉；海外用 warning 色 —— 三套
        // case-flow cluster 数据互不相通，一眼分出「这行是不是海外」比认字更重要。
        const display = sceneRegionDisplay(text);
        if (!display) return <span style={{ color: '#9b9da3' }}>-</span>;
        const isOverseas = (text || '').trim().toLowerCase() !== 'cn';
        return isOverseas ? <Tag color="warning">{display}</Tag> : <Tag>{display}</Tag>;
      },
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      key: 'apiCode',
      width: columnWidths.apiCode,
      align: 'center',
      onHeaderCell: resizeHeaderCell('apiCode'),
      render: (text: string | null | undefined, record: TaskListItem) => <ApiCodeLink code={text} source={record.source} region={record.region} />,
    },
    {
      title: '任务类型',
      dataIndex: 'taskTypeName',
      key: 'taskTypeName',
      width: columnWidths.taskTypeName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('taskTypeName'),
      render: (text: string) => {
        const display = text || '-';
        return <MiddleEllipsis text={display} />;
      },
    },
    {
      title: '状态',
      dataIndex: 'statusName',
      key: 'statusName',
      width: columnWidths.statusName,
      align: 'center',
      onHeaderCell: resizeHeaderCell('statusName'),
      render: (_: unknown, record: TaskListItem) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    {
      title: '场景标签',
      dataIndex: 'label',
      key: 'label',
      width: columnWidths.label,
      align: 'center',
      onHeaderCell: resizeHeaderCell('label'),
      // scene_inspection.label(logsim label);NULL/空 label 显示"未分类"、灰化,
      // 与顶部筛选栏下拉「未分类」哨兵值(__unlabeled__)口径一致。
      render: (text: string | null | undefined) => {
        if (!text) {
          return <span style={{ color: '#9b9da3' }}>未分类</span>;
        }
        return <MiddleEllipsis text={text} />;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: columnWidths.createTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('createTime'),
      render: (text: string) => {
        if (!text) return '-';
        try {
          const display = new Date(text).toLocaleString('zh-CN');
          return <MiddleEllipsis text={display} />;
        } catch {
          return <MiddleEllipsis text={text} />;
        }
      },
    },
    {
      title: '结束时间',
      dataIndex: 'endTime',
      key: 'endTime',
      width: columnWidths.endTime,
      align: 'center',
      onHeaderCell: resizeHeaderCell('endTime'),
      render: (text: string | null) => {
        if (!text) return '-';
        try {
          const display = new Date(text).toLocaleString('zh-CN');
          return <MiddleEllipsis text={display} />;
        } catch {
          return <MiddleEllipsis text={text} />;
        }
      },
    },
    {
      title: '仿真结果',
      key: 'evaluator',
      width: columnWidths.evaluator,
      align: 'center',
      onHeaderCell: resizeHeaderCell('evaluator'),
      render: (_: unknown, record: TaskListItem) => {
        const isException = (record.status ?? 0) > 20;
        // 未回传评测 + 非异常 → 中性 `-`，不弹层；其它都包 Popover 让用户能查明细
        if (!record.evaluationStatic && !isException) {
          return <span style={{ color: '#9b9da3' }}>-</span>;
        }
        return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
          <Popover
            title="仿真结果"
            open={evaluatorPopoverTaskId === record.taskId}
            onOpenChange={(open) => handleEvaluatorPopoverOpenChange(open, record.taskId)}
            content={renderEvaluatorPopoverContent(evaluatorListData, evaluatorListLoading)}
            trigger="click"
          >
            {renderEvaluatorChip(record)}
          </Popover>
          {renderCollisionLine(record)}
          {renderCheckerList(record)}
        </div>
        );
      },
    },
    // ── statsMode 专用「指标」列 ──
    // 只在「不通过统计」页、且**真正选了指**标时才出现：未选指标时表格不该多一列
    // 全 `—` 的空白（用户明确要求）。此处与上面 effect 的驱动源同为 queryParams，
    // 保证「列出现」与「清单/取值已就绪」总是同一份条件。
    ...(statsMetricColumnActive ? [{
      title: (() => {
        const mdef = statsMetricDefs.find((m) => m.key === queryParams.metricKey);
        const label = mdef?.label ?? queryParams.metricKey;
        return (
          <Tooltip title={
            <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 280 }}>
              <div><b>{label}</b>{mdef?.unit && mdef.unit !== 'count' ? `(${mdef.unit})` : ''}</div>
              <div>数据来源：评估器上报的结构化量化指标（metrics）</div>
              <div>按场景取值；该场景未被此评估器评测时不显示</div>
            </div>
          }>
            <span>
              {statsMetricValuesLoading && <Spin size="small" style={{ marginRight: 4 }} />}
              {label} <QuestionCircleOutlined style={{ fontSize: 11, color: '#c4c6cc' }} />
            </span>
          </Tooltip>
        );
      })(),
      key: 'statsMetric',
      width: columnWidths.statsMetric,
      align: 'center',
      onHeaderCell: resizeHeaderCell('statsMetric'),
      /**
       * 指标列**服务端全局排序**（排序键 = 指标值本身；单 Job 无对比面，故不是差值）。
       *
       * `sorter: true` 而不是比较函数：antd 只在 sorter 是函数 / 带 compare 的对象时才做
       * 本地排序，`true` 只出箭头与点击事件，**不会**把当前页再排一遍 —— 否则页内局部序
       * 会盖住后端算好的全局序（第 2 页的行看起来「没排对」）。
       *
       * 两态 descend→ascend，第三下由 antd 给出 undefined（回到默认序）。首点即 descend =
       * 「从大到小」默认方向。状态存在 `queryParams.metricSort`，与翻页 / URL 同一份快照。
       */
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: queryParams.metricSort === 'desc' ? 'descend'
        : queryParams.metricSort === 'asc' ? 'ascend' : null,
      render: (_: unknown, record: TaskListItem) => {
        const v = statsMetricValueMap[record.sceneId];
        if (v === undefined || v === null) {
          return <span style={{ color: '#c4c6cc' }}>—</span>;
        }
        // 整数原样、小数保留 2 位 —— 与对比页「差异项」列的数值口径一致，
        // 同一份数据在两个页面看起来是同一个数。
        const display = Number.isInteger(v) ? String(v) : v.toFixed(2);
        return (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{display}</span>
        );
      },
    }] : []),
    {
      title: '预览',
      key: 'preview',
      align: 'center',
      width: columnWidths.preview,
      onHeaderCell: resizeHeaderCell('preview'),
      render: (_: unknown, record: TaskListItem) => {
        const url = previewUrlMap[record.taskId];
        const cellStyle = {
          width: 220,
          height: 140,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };

        if (previewLoading && !url) {
          return <div style={cellStyle}><Spin size="small" /></div>;
        }

        if (!url) {
          return <div style={cellStyle}><span style={{ color: '#9b9da3' }}>-</span></div>;
        }

        return (
          <div style={cellStyle}>
            <Image
              src={url}
              alt="预览"
              width={200}
              style={{ display: 'block', cursor: 'pointer' }}
              preview={{ mask: '点击放大' }}
            />
          </div>
        );
      },
    },
    {
      title: 'Foxglove',
      key: 'foxglove',
      align: 'center',
      width: columnWidths.foxglove,
      onHeaderCell: resizeHeaderCell('foxglove'),
      render: (_: unknown, record: TaskListItem) => {
        const loadingThis = foxgloveLoadingTaskId === record.taskId;
        const disabled = !record.taskId || !record.sceneName || loadingThis;
        return (
          <Tooltip title={disabled && !loadingThis ? '暂无可回放数据' : '在 Foxglove 中打开回放'}>
            <button
              type="button"
              className="fox-link-btn"
              disabled={disabled}
              onClick={() => handleOpenFoxglove(record.taskId, record.sceneName)}
            >
              {loadingThis ? <Spin size="small" /> : <LinkOutlined />}
              foxglove
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: '评论',
      key: 'comment',
      width: columnWidths.comment,
      align: 'center',
      onHeaderCell: resizeHeaderCell('comment'),
      onCell: () => ({ style: { overflow: 'visible' } }),
      render: (_: unknown, record: TaskListItem) => {
        const s = commentSummary[record.sceneId];
        const hasComment = s != null && s.total > 0;
        const label = hasComment
          ? `${s.latestTitle ?? '评论'} (${s.total})`
          : '评论';
        return (
          <Button
            size="small"
            type="link"
            icon={<CommentOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setActionPopoverTaskId(null);
              setCommentTarget({
                sceneId: record.sceneId,
                sceneName: record.sceneName ?? null,
                jobId: record.jobId,
                taskId: record.taskId,
              });
            }}
            title={hasComment ? `最新：${s.latestTitle ?? ''}` : '点击新建评论'}
            style={{ padding: '0 4px', maxWidth: '100%', overflow: 'hidden' }}
          >
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{label}</span>
          </Button>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: columnWidths.action,
      align: 'center',
      onHeaderCell: resizeHeaderCell('action'),
      render: (_: unknown, record: TaskListItem) => renderTaskActionPopover(record),
    },
  ];

  /**
   * 「差异项」列的服务端排序是否开放 —— 仅指标模式。
   *
   * 排序键在后端固定为「差值 = 当前 − 对比」（指标模式的口径）。默认 VRU 相对速度
   * 模式用的是**相反**符号（对比 − 当前，见列 render 里的注释），若在那里也放开表头
   * 排序，用户会得到一份方向与列上箭头对不上的序。
   */
  const metricSortEnabled = !!(compareFilterMetricKey && compareFilterEvaluatorName);

  /**
   * 「原因」列是否有内容可展示 —— 决定该列渲不渲染。
   *
   * 两个条件都要：① 选了评测器（原因是评测器维度的）；② **至少有一个场景的某一侧真的
   * 报了失败 / 豁免原因**（v2 统计两桶任一非空）。第二条正是为算法侧尚未落地上报的那段
   * 时间准备的：此时统计两桶皆空（干净通过），若照判定渲染，整列会是一列「无」——
   * 用户看到一列空值却不知道是功能坏了还是数据没有。
   *
   * 判定用「有没有原因」而不是「有没有 diff 桶」：两侧原因**完全相同**时也有原因可看
   *（这正是 fail_fail / pass_pass 里「原因一样」的场景），不能因为没差异就藏列。
   */
  const reasonColumnActive = !!compareFilterEvaluatorName && Object.values(reasonDiffMap)
    .some((cell) => [cell?.today, cell?.compare].some(
      (stats) => !!stats && (stats.fail.length > 0 || stats.exempt.length > 0),
    ));

  // 共享的「对比结果」内容：原 Modal 弹窗与「任务对比」独立页（?mode=compare）共用同一份
  // body，确保两种入口的功能与样式完全一致；切换不需要复制实现。
  const compareResultBody = (
    <>
      <style>{`
        /* 子列 header（第二行）：克制 uppercase + 灰阶 */
        .compare-table .ant-table-thead > tr:nth-child(2) > th {
          padding: 6px 8px !important;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #9b9da3;
        }
        /* 分组 header（第一行）：常规字号 + 不全大写，避免中文别扭 */
        .compare-table .ant-table-thead > tr:first-child > th {
          padding: 8px 10px !important;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: none;
        }
        /* 行底色按 diff 语义（一致 / 不一致 / 仅当前），与分组色阶正交 */
        .compare-table .compare-row-diff > td {
          background: #fef2f2 !important;
        }
        .compare-table .compare-row-only > td {
          background: #fffbeb !important;
        }
        .compare-table .compare-row-same > td {
          background: #fff !important;
        }
        .compare-table .ant-table-cell {
          vertical-align: middle !important;
        }
        .compare-table .ant-table-tbody > tr > td {
          height: 120px;
          padding: 6px 8px !important;
          border-bottom: 1px solid #f0f0f0;
        }
        /* 三色阶左边线：仅作用在每个分组的第一个子列，其余子列保持 antd 默认 1px 灰线 */
        .compare-table .scene-col-first {
          border-left: 3px solid #d4d4d8 !important;
        }
        .compare-table .cur-col-first {
          border-left: 3px solid #5e6ad2 !important;
        }
        .compare-table .cmp-col-first {
          border-left: 3px solid #d97706 !important;
        }
      `}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>场景集：</span>
        <TreeSelect
          aria-label="对比明细场景集"
          placeholder="全部场景集（含下级）" allowClear showSearch
          treeData={compareSceneSetOptions} treeNodeLabelProp="label" treeNodeFilterProp="label"
          value={compareFilterSceneSetId} loading={sceneSetComparison.loading}
          style={{ width: 280 }} styles={{ popup: { root: { maxHeight: 420, overflow: 'auto' } } }}
          onChange={(value) => { void applyCompareSceneSet(value as number | undefined); }}
          notFoundContent={sceneSetComparison.error ? <Button type="link" onClick={sceneSetComparison.onRetry}>加载失败，点击重试</Button> : undefined}
        />
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>结果类型：</span>
        <Select
          aria-label="对比明细结果类型" placeholder="全部" allowClear style={{ width: 195 }}
          value={compareDiffMode}
          onChange={(value) => {
            // 已选评测器时，「结果类型」只是在**该评测器内**切象限，不能把评测器清掉：
            // applyCompareDiffMode 是给顶部整体 2×2 矩阵用的（场景级，与评测器联动互斥），
            // 会把评测器 / 指标 / 原因全清 —— 用户在评测器上选好后一改结果类型，评测器就
            // 「刷新」没了。走 applyEvaluatorQuadrant 保留评测器，只换象限。
            if (compareFilterEvaluatorName) void applyEvaluatorQuadrant(value);
            else void applyCompareDiffMode(value, compareFilterSceneSetId);
          }}
          options={[
            { value: 'pass_pass', label: 'PassPass · 均通过' },
            { value: 'pass_fail', label: 'PassFail · 新增失败' },
            { value: 'fail_pass', label: 'FailPass · 新增通过' },
            { value: 'fail_fail', label: 'FailFail · 均失败' },
            ...(compareDiffMode === 'all' ? [{ value: 'all', label: '全部可判定（评测器）' }] : []),
            ...(compareDiffMode === 'new_success' ? [{ value: 'new_success', label: '新增通过（含单侧场景）' }] : []),
            ...(compareDiffMode === 'new_fail' ? [{ value: 'new_fail', label: '新增失败（含单侧场景）' }] : []),
          ]}
        />
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>场景名称：</span>
        <Input
          placeholder="请输入场景名称"
          allowClear
          value={compareFilterSceneName}
          onChange={(e) => setCompareFilterSceneName(e.target.value || undefined)}
          onPressEnter={handleCompareSearch}
          style={{ width: 200 }}
        />
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>当前任务状态：</span>
        <Select
          placeholder="请选择状态"
          allowClear
          value={compareFilterStatus}
          onChange={(v) => setCompareFilterStatus(v)}
          style={{ width: 150 }}
        >
          {statusList.map((item, index) => (
            <Select.Option
              key={item.code ?? item.value ?? index}
              value={item.value}
            >
              {item.label}
            </Select.Option>
          ))}
        </Select>
        {/* 「差异」= **场景级**一致性（diffConsistency）：两边这个场景的通过状态 + 失败
            评测项集合是不是一样。与下面「原因」的区别就在粒度 —— 那个盯的是**某一个
            评测器**报的原因枚举。两个筛选正交、同传取交集。挂个 tooltip 是因为它们
            并排摆着，光看「一致 / 不一致」两个词分不出谁是谁（用户反馈过表述不明确）。 */}
        <Tooltip title={
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>
            <div><b>场景级差异（整个场景的对比结论）</b></div>
            <div>一致：两侧状态相同、且失败评测项集合相同</div>
            <div>不一致：状态不同，或状态相同但失败项不同</div>
            <div>仅当前：对比任务没有这个场景</div>
            <div style={{ opacity: 0.75 }}>与右侧「原因」正交，可同时生效</div>
          </div>
        }>
          <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500, cursor: 'help' }}>差异：</span>
        </Tooltip>
        <Select
          placeholder="全部"
          allowClear
          value={compareDiffConsistency}
          onChange={(v) => { void handleCompareConsistencyChange(v as CompareConsistency | undefined); }}
          style={{ width: 110 }}
          options={[
            { value: 'same', label: '一致' },
            { value: 'diff', label: '不一致' },
            { value: 'only_current', label: '仅当前' },
          ]}
        />
        {/* 「行为结论」筛选：按下方「行为对比」区块里的判定标签筛场景（需复核 / 显著差异 / 轻微差异 /
            一致 / 无法判断 / 两侧结论不一致）。与翻转象限（Pass·Fail 等）、「差异」列正交，同传取交集：
            · Pass·Fail ∩ 需复核 = 新增失败、行为也确实变了 —— 优先看；
            · Pass·Fail ∩ 一致   = 新增失败但行为没变 —— 更可能是评估器/阈值抖动，可后看。
            这对任务还没有行为对比报告时筛出 0 行（不会退化成全量），选项旁给出提示。
            无 menu:behavior-compare 权限时整个控件不渲染。 */}
        {hasPerm('menu:behavior-compare') && (
          <>
            <Tooltip title={
              <div style={{ fontSize: 12, lineHeight: 1.7 }}>
                <div><b>行为结论（自车行为有没有变）</b></div>
                <div>按「行为对比」区块的判定标签筛场景，与上面的翻转象限、「差异」正交，同传取交集。</div>
                <div>例：Pass·Fail ∩ 需复核 = 新增失败且行为也变了；Pass·Fail ∩ 一致 = 新增失败但行为没变。</div>
                <div style={{ opacity: 0.75 }}>这两个任务还没有行为对比报告时，筛选结果为空。</div>
              </div>
            }>
              <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500, cursor: 'help' }}>行为：</span>
            </Tooltip>
            <Select
              placeholder="全部"
              allowClear
              value={compareBehaviorLabel}
              onChange={(v) => { void applyBehaviorFilters({ label: v as BehaviorFilterValue | undefined, strong: compareBehaviorStrong }); }}
              style={{ width: 140 }}
              options={[
                { value: 'NEEDS_REVIEW', label: '⚠ 需复核' },
                { value: 'SIGNIFICANT', label: '▲ 显著差异' },
                { value: 'SLIGHT', label: '△ 轻微差异' },
                { value: 'CONSISTENT', label: '✓ 一致' },
                { value: 'INCONCLUSIVE', label: '? 无法判断' },
                { value: 'MISMATCH', label: '两侧结论不一致' },
              ]}
            />
            {compareBehaviorStrong ? (
              <Tag
                closable color="red" style={{ margin: 0 }}
                onClose={(e) => { e.preventDefault(); void applyBehaviorFilters({ label: compareBehaviorLabel, strong: undefined }); }}
              >
                强事件：{compareBehaviorStrong === 'ANY' ? '任一' : (BEHAVIOR_STRONG_NAMES[compareBehaviorStrong] ?? compareBehaviorStrong)}
              </Tag>
            ) : null}
          </>
        )}
        {/* 「原因」筛选：比的是选中评测器上报的 fail 失败原因 / pass 豁免原因
            （evaluate_info.reason 的 code）。原因是**评测器维度的**，故未选评测器时
            置灰 —— 没有评测器就没有「谁的原因」可比。
            与上面「差异」筛选正交：那个是场景级状态比对，这个是评测器内容比对，
            同传取交集（后端 reason_scene_ids ∩ consistency_scene_ids）。

            选项分三档（见 reasonFilterOptions）：
            - 「两侧比对」：两侧原因 code 一致 / 不一致 / 仅当前有该评测器结果；
            - 「当前任务 · 失败原因」：**当前侧未通过**的象限（fail_fail / pass_fail /
              new_fail）才挂 —— 这时当前侧只可能报失败原因；
            - 「当前任务 · 豁免原因」：**当前侧通过**的象限（pass_pass / fail_pass /
              new_success）才挂 —— 这时当前侧只可能报豁免原因。
            两档互斥、与「两侧比对」共用本控件（选中即清另一个），见 REASON_CODE_VALUE_PREFIX。

            该评测器**一条原因都没上报**时整体禁用：此时所有场景判定的都是「两侧都无原因
            = 一致」，给个能选却必然筛出 0 行的控件只会误导（参 reasonFilterDisabled）。 */}
        {/* 「原因」筛选：比的是选中评测器上报的 fail 失败原因 / pass 豁免原因 */}
        <Tooltip title={
          <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 320 }}>
            <div><b>原因比对（单个评测器报的 fail / pass 原因）</b></div>
            <div>两侧比对 · 一致：两侧原因 code 相同（两侧都没报原因也算一致）</div>
            <div>两侧比对 · 不一致：code 不同，或一侧报了另一侧没报</div>
            <div>当前任务 · 失败原因：当前任务这一侧挂掉的原因（选 fail_fail /
              pass_fail 象限时出现）</div>
            <div>当前任务 · 豁免原因：当前任务这一侧被放行的原因（选 pass_pass /
              fail_pass 象限时出现）</div>
            <div style={{ opacity: 0.75 }}>要先选「评测器」——原因是评测器维度的</div>
          </div>
        }>
          <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500, cursor: 'help' }}>原因：</span>
        </Tooltip>
        <Select
          placeholder={
            !compareFilterEvaluatorName
              ? '先选评测器'
              : (reasonFilterDisabled ? '该评测器未上报原因' : '全部')
          }
          allowClear
          disabled={reasonFilterDisabled}
          value={compareFilterReasonCode
            ? `${REASON_CODE_VALUE_PREFIX}${compareFilterReasonCode}`
            : compareFilterReasonDiff}
          loading={reasonDiffLoading}
          onChange={handleReasonFilterChange}
          style={{ width: 160 }}
          options={reasonFilterOptions}
        />
        {/* 评测器筛选下拉框：列出白名单中所有评测器（含指标型），支持模糊搜索。
            指标型评测器（如 iLQR、换道触发检测）在下方 2×2 矩阵中恒为全 pass_pass、
            且会被前端从矩阵里剔除移到"评估器参数对比"卡片，无法从矩阵选中。
            本下拉框补回该路径——选中后即可继续选「指标」「对比类型」做参数对比。 */}
        <span style={{ fontSize: 13, color: '#5e6ad2', fontWeight: 500 }}>评测器：</span>
        <Select
          placeholder="选择评测器（可搜索）"
          allowClear
          showSearch
          value={compareFilterEvaluatorName}
          loading={evaluatorNameLoading}
          onChange={(v) => { void handleEvalFilterSelect(v); }}
          style={{ width: 220 }}
          options={evaluatorNameOptions.map((name) => ({
            label: getEvaluatorDisplay(name),
            value: name,
          }))}
          filterOption={(input, option) => {
            const q = input.toLowerCase();
            return (
              String(option?.label ?? '').toLowerCase().includes(q) ||
              String(option?.value ?? '').toLowerCase().includes(q)
            );
          }}
          notFoundContent={
            evaluatorNameLoading ? <Spin size="small" /> : '无可用评测器'
          }
        />
        {/* 评测器量化指标下钻：指标 + 对比类型。须先在上方评测器下拉或矩阵中选中一个
            评测器（compareFilterEvaluatorName）才可选；指标选项由后端动态返回（评估器
            上报的 evaluate_info.metrics，纯结构化、无写死注册表）。
            选指标即改「差异项」列展示该指标；再选对比类型（增大/缩小）才跨分页筛选场景。 */}
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>指标：</span>
        <Select
          placeholder={!compareFilterEvaluatorName ? '先选评测器' : (compareMetricDefs.length ? '请选择指标' : '该评测器无量化指标')}
          allowClear
          disabled={!compareFilterEvaluatorName || compareMetricDefs.length === 0}
          value={compareFilterMetricKey}
          onChange={(v) => { void applyMetricFilter(v as string | undefined, compareFilterMetricCompare); }}
          style={{ width: 150 }}
          options={compareMetricDefs.map((m) => ({ value: m.key, label: m.label }))}
        />
        <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>对比类型：</span>
        <Select
          placeholder="请选择"
          allowClear
          disabled={!compareFilterMetricKey}
          value={compareFilterMetricCompare}
          onChange={(v) => { void applyMetricFilter(compareFilterMetricKey, v as MetricCompare | undefined); }}
          style={{ width: 110 }}
          options={[
            { value: 'increase', label: '增大' },
            { value: 'decrease', label: '缩小' },
            { value: 'equal', label: '持平' },
          ]}
        />
        {/* 选中指标后立即出的方向计数：增大/减少/持平。点某个即按该方向筛选场景
            （等价于把「对比类型」设为该方向），再点同方向取消。共有场景全集口径，
            与列表筛选一致。坏方向（higherIsWorse）红、好方向绿；持平中性灰。 */}
        {compareFilterMetricKey && metricDirCounts && (() => {
          const higherIsWorse = findMetricDef(compareFilterMetricKey)?.higherIsWorse;
          // 「增大」是好是坏取决于指标方向语义：higherIsWorse=true → 增大为坏(红)、减少为好(绿)；
          // false → 反之；未知(null/undefined) → 都用中性蓝，不误导。
          const incColor = higherIsWorse == null ? 'blue' : (higherIsWorse ? 'error' : 'success');
          const decColor = higherIsWorse == null ? 'blue' : (higherIsWorse ? 'success' : 'error');
          const items: Array<{ dir: MetricCompare; label: string; count: number; color: string }> = [
            { dir: 'increase', label: '增大', count: metricDirCounts.increase, color: incColor },
            { dir: 'decrease', label: '减少', count: metricDirCounts.decrease, color: decColor },
            { dir: 'equal', label: '持平', count: metricDirCounts.equal, color: 'default' },
          ];
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              {items.map((it) => {
                const active = compareFilterMetricCompare === it.dir;
                return (
                  <Tag
                    key={it.dir}
                    color={active ? it.color : undefined}
                    style={{
                      cursor: 'pointer',
                      marginInlineEnd: 2,
                      fontWeight: active ? 600 : 400,
                      border: active ? undefined : '1px solid #d9d9d9',
                    }}
                    onClick={() => {
                      // 点已选方向 → 取消筛选；否则按该方向筛选（指标不变，计数不重拉）。
                      void applyMetricFilter(compareFilterMetricKey, active ? undefined : it.dir);
                    }}
                    title={`${it.label} ${it.count} 个场景，点击按该方向筛选`}
                  >
                    {it.label} {it.count}
                  </Tag>
                );
              })}
            </span>
          );
        })()}
        {/* 评论筛选:当前任务侧 / 对比任务侧各一个,各按自己那侧的 jobId 做 EXISTS。
            两侧的组合方式由「且/或」开关决定 —— 「且」如「当前有 + 对比无」= 本次新增
            评论;「或」如「两侧都=有评论」= 任一边有评论。单侧内部的关键词与有无评论恒
            取 AND,故开关只在两侧都设了条件时才出现,平时不占地方。
            每个都合并 关键词/scope/有无 三档到单按钮 Popover;应用后立即调
            loadComparePage 触发查询,与普通视图 onChange 立即刷新对齐。 */}
        <span style={{ fontSize: 13, color: '#5e6ad2', fontWeight: 500 }}>当前任务评论：</span>
        <CommentFilterPopover
          value={compareCommentFilter}
          placeholder="当前任务评论筛选"
          onChange={(v) => {
            setCompareCommentFilter(v);
            setComparePage(1);
            if (compareJobIdRef.current) {
              void loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
                compareSceneSetId: compareFilterSceneSetId,
                sceneName: compareFilterSceneName,
                status: compareFilterStatus,
                diffMode: compareDiffMode,
                evaluatorName: compareFilterEvaluatorName,
                evaluatorDiff: !!compareFilterEvaluatorName,
                diffConsistency: compareDiffConsistency,
                behaviorLabel: compareBehaviorLabel,
                behaviorStrong: compareBehaviorStrong,
                reasonDiff: compareFilterReasonDiff,
                reasonCode: compareFilterReasonCode,
                metricKey: compareFilterMetricKey,
                metricCompare: compareFilterMetricCompare,
                metricSort: compareFilterMetricSort,
                comment: v,
                compareSideComment: compareSideCommentFilter,
                commentCombine,
              });
            }
          }}
        />
        <span style={{ fontSize: 13, color: '#d97706', fontWeight: 500 }}>对比任务评论：</span>
        <CommentFilterPopover
          value={compareSideCommentFilter}
          placeholder="对比任务评论筛选"
          onChange={(v) => {
            setCompareSideCommentFilter(v);
            setComparePage(1);
            if (compareJobIdRef.current) {
              void loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
                compareSceneSetId: compareFilterSceneSetId,
                sceneName: compareFilterSceneName,
                status: compareFilterStatus,
                diffMode: compareDiffMode,
                evaluatorName: compareFilterEvaluatorName,
                evaluatorDiff: !!compareFilterEvaluatorName,
                diffConsistency: compareDiffConsistency,
                behaviorLabel: compareBehaviorLabel,
                behaviorStrong: compareBehaviorStrong,
                reasonDiff: compareFilterReasonDiff,
                reasonCode: compareFilterReasonCode,
                metricKey: compareFilterMetricKey,
                metricCompare: compareFilterMetricCompare,
                metricSort: compareFilterMetricSort,
                comment: compareCommentFilter,
                compareSideComment: v,
                commentCombine,
              });
            }
          }}
        />
        {/* 「且/或」开关:仅两侧都设了条件时才有意义(仅一侧时后端也会忽略),故条件渲染。 */}
        {isCommentFilterActive(compareCommentFilter)
          && isCommentFilterActive(compareSideCommentFilter) && (
          <>
            <span style={{ fontSize: 13, color: '#6b6f76', fontWeight: 500 }}>组合：</span>
            <Tooltip title="且=两侧条件都满足；或=任一侧满足（如两侧都选「有评论」+「或」= 任一边有评论）">
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={commentCombine}
                options={[
                  { label: '且', value: 'and' },
                  { label: '或', value: 'or' },
                ]}
                onChange={(e) => {
                  const next = e.target.value as 'and' | 'or';
                  setCommentCombine(next);
                  setComparePage(1);
                  if (compareJobIdRef.current) {
                    void loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
                      compareSceneSetId: compareFilterSceneSetId,
                      sceneName: compareFilterSceneName,
                      status: compareFilterStatus,
                      diffMode: compareDiffMode,
                      evaluatorName: compareFilterEvaluatorName,
                      evaluatorDiff: !!compareFilterEvaluatorName,
                      diffConsistency: compareDiffConsistency,
                      behaviorLabel: compareBehaviorLabel,
                      behaviorStrong: compareBehaviorStrong,
                      reasonDiff: compareFilterReasonDiff,
                      reasonCode: compareFilterReasonCode,
                      metricKey: compareFilterMetricKey,
                      metricCompare: compareFilterMetricCompare,
                      metricSort: compareFilterMetricSort,
                      comment: compareCommentFilter,
                      compareSideComment: compareSideCommentFilter,
                      commentCombine: next,
                    });
                  }
                }}
              />
            </Tooltip>
          </>
        )}
        <Button type="primary" size="small" onClick={handleCompareSearch}>查询</Button>
        <Button size="small" onClick={handleCompareFilterReset}>重置</Button>
        {/* 「基于当前查询发起任务」入口(对比页版) —— 与非对比页 FilteredStatRow 后
            的同款按钮口径完全一致,filter 快照涵盖 compareJobId / diffMode /
            diffConsistency / metricKey / compareSideComment 等全部对比 filter,
            后端反查会重现当前列表看到的场景池。 */}
        {selectedCompareJobId != null && (() => {
          const sceneCount = compareFilteredCount?.sceneCount ?? 0;
          const notQueried = !compareFilteredCount;
          const disabled = notQueried || sceneCount === 0;
          const overThreshold = sceneCount > 200;
          const tip = notQueried
            ? '暂未选择场景 —— 请先查询后再发起任务'
            : sceneCount === 0
              ? '暂未选择场景 —— 当前筛选无命中场景'
              : overThreshold
                ? `当前 ${sceneCount} 场景,发起后会占用较多算力`
                : `按当前对比筛选发起新任务(${sceneCount} 场景)`;
          const snapshot: TaskListParams = buildCompareQueryParams(
            selectedCompareJobId, 1, 10, {
              compareSceneSetId: compareFilterSceneSetId,
              sceneName: compareFilterSceneName,
              status: compareFilterStatus,
              evaluatorName: compareFilterEvaluatorName,
              evaluatorDiff: !!compareFilterEvaluatorName,
              diffMode: compareDiffMode,
              diffConsistency: compareDiffConsistency,
              behaviorLabel: compareBehaviorLabel,
              behaviorStrong: compareBehaviorStrong,
              reasonDiff: compareFilterReasonDiff,
              reasonCode: compareFilterReasonCode,
              metricKey: compareFilterMetricKey,
              metricCompare: compareFilterMetricCompare,
              metricSort: compareFilterMetricSort,
              comment: compareCommentFilter,
              compareSideComment: compareSideCommentFilter,
              commentCombine,
            },
          );
          return (
            <Tooltip title={tip}>
              <span style={{ display: 'inline-block' }}>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    setRerunFilters(snapshot);
                    setRerunConfirmOpen(true);
                  }}
                  disabled={disabled}
                  icon={
                    overThreshold
                      ? <WarningOutlined style={{ color: '#faad14' }} />
                      : <ThunderboltOutlined />
                  }
                  loading={compareFilteredCountLoading && !compareFilteredCount}
                >
                  基于当前查询发起任务{
                    !notQueried && sceneCount > 0 ? ` (${sceneCount})` : ''
                  }
                </Button>
              </span>
            </Tooltip>
          );
        })()}
        {canSeeBehavior && selectedCompareJobId != null && jobId ? (
          <BehaviorLaunchButton
            jobId={jobId}
            compareJobId={selectedCompareJobId}
            onProgress={() => setBehaviorRefreshKey((k) => k + 1)}
          />
        ) : null}
      </div>
      {/* DailySummaryCard 仅供原 Modal 弹窗使用；compareMode 独立页里用专属「对比仿真结果」模块取代 */}
      {!compareMode && (
        <Spin spinning={compareSummaryLoading}>
          {compareSummary ? (
            <div style={{ marginBottom: 12 }}>
              <DailySummaryCard
                title="对比 仿真结果"
                summary={compareSummary}
                clickablePart="value"
                onClickNewSuccess={
                  compareSummary.newSuccessSceneCount > 0
                    ? () => {
                      void applyCompareDiffMode('new_success');
                    }
                    : undefined
                }
                onClickNewFail={
                  compareSummary.newFailSceneCount > 0
                    ? () => {
                      void applyCompareDiffMode('new_fail');
                    }
                    : undefined
                }
              />
            </div>
          ) : null}
        </Spin>
      )}
      <Spin spinning={compareLoading}>
        <Table
          className="compare-table"
          rowKey="key"
          dataSource={compareData}
          size="small"
          components={{ header: { cell: ResizableTitle } }}
          pagination={{
            current: comparePage,
            pageSize: comparePageSize,
            total: compareTotal,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: handleComparePageChange,
          }}
          scroll={{ x: 'max-content' }}
          bordered
          rowClassName={(record: CompareRow) => {
            if (record.onlyInCurrent) return 'compare-row-only';
            if (isRowDiff(record)) return 'compare-row-diff';
            return 'compare-row-same';
          }}
          /**
           * 指标列排序的唯一入口（表头箭头）。
           *
           * 只在 action === 'sort' 时动手：翻页也会走这个 onChange（并附带当前
           * sorter），不滤掉就会在每次翻页时多打一次一模一样的请求。
           *
           * 排序变了必须**回到第 1 页**：全局序已整体重排，停在第 3 页会看到完全
           * 另一段数据，用户会当成「排序没生效」。页码由前端负责，后端只按传入的
           * current 切页。
           */
          onChange={(_pagination, _filters, sorter, extra) => {
            if (extra?.action !== 'sort') return;
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            const next: MetricSort | undefined =
              s?.order === 'descend' ? 'desc'
                : s?.order === 'ascend' ? 'asc' : undefined;
            if (next === compareFilterMetricSort) return;
            setCompareFilterMetricSort(next);
            setComparePage(1);
            if (compareJobIdRef.current) {
              void loadComparePage(compareJobIdRef.current, 1, comparePageSize, {
                sceneName: compareFilterSceneName,
                status: compareFilterStatus,
                diffMode: compareDiffMode,
                evaluatorName: compareFilterEvaluatorName,
                evaluatorDiff: !!compareFilterEvaluatorName,
                diffConsistency: compareDiffConsistency,
                behaviorLabel: compareBehaviorLabel,
                behaviorStrong: compareBehaviorStrong,
                reasonDiff: compareFilterReasonDiff,
                reasonCode: compareFilterReasonCode,
                metricKey: compareFilterMetricKey,
                metricCompare: compareFilterMetricCompare,
                metricSort: next,
                comment: compareCommentFilter,
                compareSideComment: compareSideCommentFilter,
                commentCombine,
              });
            }
          }}
          columns={[
            // ── 场景信息区（中性灰）：sceneName / API_CODE / 差异 ──
            // 「评论」列拆到两侧分组各 1 列，按各自 Job 的 (sceneId, jobId) 维度独立挂载。
            {
              title: <span style={{ color: '#6b6f76', fontWeight: 600 }}>场景信息</span>,
              children: [
                {
                  title: '场景名称',
                  dataIndex: 'sceneName',
                  key: 'sceneName',
                  fixed: 'left',
                  width: columnWidths.cmpSceneName,
                  ellipsis: true,
                  className: 'scene-col-first',
                  onHeaderCell: cmpHeaderCell('cmpSceneName', '#fafafa'),
                  // 场景名 → 跳转到该场景详情页；scene_id 从两侧任一 task 取,
                  // 「仅当前 / 一致」两种行都能拿到。都没有(极端脏 data)降级为纯文本。
                  // 带 from=job-tasks 让详情页「返回」走 navigate(-1) 回到本对比页原位置。
                  render: (text: string, record: CompareRow) => {
                    const sceneId = record.currentTask?.sceneId || record.compareTask?.sceneId;
                    const display = text || '-';
                    if (!sceneId || sceneId <= 0) return display;
                    return (
                      <Link
                        to={`/scene-annotate?sceneId=${sceneId}&from=job-tasks`}
                        style={{ color: '#5e6ad2', cursor: 'pointer' }}
                      >
                        {display}
                      </Link>
                    );
                  },
                },
                {
                  title: 'API_CODE',
                  dataIndex: 'apiCode',
                  key: 'apiCode',
                  width: columnWidths.cmpApiCode,
                  align: 'center',
                  fixed: 'left',
                  onHeaderCell: cmpHeaderCell('cmpApiCode', '#fafafa'),
                  render: (_: unknown, record: CompareRow) => <ApiCodeLink code={record.apiCode} source={record.source} region={record.region} />,
                },
                {
                  title: '差异',
                  key: 'diff',
                  width: columnWidths.cmpDiff,
                  align: 'center',
                  fixed: 'left',
                  onHeaderCell: cmpHeaderCell('cmpDiff', '#fafafa'),
                  render: (_: unknown, record: CompareRow) => {
                    if (record.onlyInCurrent) return <Tag color="warning">仅当前</Tag>;
                    if (isRowDiff(record)) return <Tag color="error">不一致</Tag>;
                    return <Tag color="success">一致</Tag>;
                  },
                },
                ...(canSeeBehavior ? [{
                  // 行为结论列：自车行为有没有变（需复核 / 显著差异 / 轻微差异 / 一致 / 无法判断）。
                  // 数据来自 /behavior-compare/scene-labels，与「行为」筛选同一张报告表。
                  // 没有报告时整列显示「—」，不是「一致」——没算不等于没变。
                  title: '行为',
                  key: 'behavior',
                  width: columnWidths.cmpBehavior,
                  align: 'center' as const,
                  fixed: 'left' as const,
                  onHeaderCell: cmpHeaderCell('cmpBehavior', '#fafafa'),
                  render: (_: unknown, record: CompareRow) => {
                    const sid = record.currentTask?.sceneId;
                    const b = sid == null ? undefined : compareBehaviorMap[sid];
                    if (!b) return <span style={{ color: '#c4c6cc' }}>—</span>;
                    const meta = BEHAVIOR_LABEL_META[b.label];
                    const tip = (
                      <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 360 }}>
                        <div><b>{meta.name}</b>{b.mismatch ? '（两侧平台结论不一致）' : ''}</div>
                        {b.strong?.length ? <div style={{ color: '#fca5a5' }}>强事件：{b.strong.join('、')}</div> : null}
                        {b.diffs?.length
                          ? b.diffs.map((d) => <div key={d}>· {shortDiffText(d).short}</div>)
                          : <div style={{ opacity: 0.75 }}>两侧自车行为没有检出差异</div>}
                        <div style={{ opacity: 0.6, marginTop: 4 }}>点击查看详情</div>
                      </div>
                    );
                    return (
                      <Tooltip title={tip}>
                        <span
                          onClick={() => setBehaviorDetailSceneId(sid ?? null)}
                          style={{
                            cursor: 'pointer', color: meta.color, background: meta.bg, border: `1px solid ${meta.border}`,
                            borderRadius: 4, padding: '0 6px', fontSize: 12, whiteSpace: 'nowrap',
                          }}
                        >
                          {meta.icon} {meta.name}{b.mismatch ? '*' : ''}
                        </span>
                      </Tooltip>
                    );
                  },
                }] : []),
                ...(canSeeBehavior ? [
                  {
                    // 主要差异：强事件在前，至多 5 条；终止原因的英文原文去掉（悬停看原文）。
                    title: '主要差异',
                    key: 'behaviorDiffs',
                    width: columnWidths.cmpBehaviorDiffs,
                    onHeaderCell: cmpHeaderCell('cmpBehaviorDiffs', '#fafafa'),
                    render: (_: unknown, record: CompareRow) => {
                      const row = compareBehaviorRows[record.currentTask?.sceneId ?? -1];
                      if (!row) return <span style={{ color: '#c4c6cc' }}>—</span>;
                      if (row.mainDiffs.length === 0) {
                        return <span style={{ color: '#9b9da3', fontSize: 12 }}>{row.label === 'INCONCLUSIVE' ? '无闭环对比窗口' : '无明显差异'}</span>;
                      }
                      return (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {row.mainDiffs.map((d) => {
                            const { short, full } = shortDiffText(d.text);
                            return (
                              <Tooltip key={d.code} title={short !== full ? full : undefined}>
                                <span style={{
                                  background: d.strong ? '#fff1eb' : '#f3f4f6', color: d.strong ? '#c2410c' : 'inherit',
                                  borderRadius: 3, padding: '0 6px', fontSize: 12, lineHeight: '20px',
                                }}>{short}</span>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    },
                  },
                  {
                    // 差异随时间变化：整段闭环上每 0.1 秒各通道归一后的最大差异，峰值红点。
                    title: '差异随时间变化',
                    key: 'behaviorCurve',
                    width: columnWidths.cmpBehaviorCurve,
                    align: 'center' as const,
                    onHeaderCell: cmpHeaderCell('cmpBehaviorCurve', '#fafafa'),
                    render: (_: unknown, record: CompareRow) => (
                      <BehaviorSparkline curve={compareBehaviorRows[record.currentTask?.sceneId ?? -1]?.curve} width={160} />
                    ),
                  },
                  {
                    // 分类摘要（最差 2 秒）：运动 / 交互 / 动力。没有近距障碍物可比时「交互」显示「—」。
                    title: '分类摘要（最差 2s）',
                    key: 'behaviorSummary',
                    width: columnWidths.cmpBehaviorSummary,
                    onHeaderCell: cmpHeaderCell('cmpBehaviorSummary', '#fafafa'),
                    render: (_: unknown, record: CompareRow) => {
                      const row = compareBehaviorRows[record.currentTask?.sceneId ?? -1];
                      if (!row) return <span style={{ color: '#c4c6cc' }}>—</span>;
                      return <BehaviorSummaryBars summary={row.summary} hasInteraction={row.summary?.interaction != null} />;
                    },
                  },
                ] : []),
                {
                  // 差异项列：默认展示 VRU 相对速度；一旦选中评测器指标（compareFilterMetricKey），
                  // 改展示该指标的 [当前任务值] [对比任务值] [差值(=当前−对比)+色+箭头] 3 段。
                  // 差值方向语义按指标 higherIsWorse 上色：增大是坏→红↑、是好→绿↑。
                  title: (() => {
                    const mdef = compareFilterMetricKey
                      ? findMetricDef(compareFilterMetricKey)
                      : undefined;
                    if (mdef) {
                      return (
                        <Tooltip title={
                          <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 280 }}>
                            <div><b>{mdef.label}</b>{mdef.unit && mdef.unit !== 'count' ? `(${mdef.unit})` : ''}</div>
                            <div>当前任务 / 对比任务 / 差值(=当前任务−对比任务)</div>
                            <div>数据来源：评测器上报的结构化量化指标（metrics）</div>
                          </div>
                        }>
                          <span>{mdef.label} <QuestionCircleOutlined style={{ fontSize: 11, color: '#c4c6cc' }} /></span>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={
                        <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 280 }}>
                          <div><b>VRU 相对速度差异</b>(m/s)</div>
                          <div>当前任务 / 对比任务 / 差值(=对比任务−当前任务)</div>
                          <div>差值为负(绿↓): 对比任务更低,风险改善</div>
                          <div>差值为正(红↑): 对比任务更高,风险加剧</div>
                        </div>
                      }>
                        <span>差异项 <QuestionCircleOutlined style={{ fontSize: 11, color: '#c4c6cc' }} /></span>
                      </Tooltip>
                    );
                  })(),
                  key: 'vru-speed-diff',
                  width: columnWidths.cmpVruSpeedDiff,
                  align: 'center',
                  fixed: 'left',
                  onHeaderCell: cmpHeaderCell('cmpVruSpeedDiff', '#fafafa'),
                  /**
                   * 指标列**服务端全局排序**（未选指标时该列是 VRU 相对速度，不开放，
                   * 理由见 metricSortEnabled）。
                   *
                   * 传 `sorter: true` 而不是比较函数，是有意的：antd 只在 sorter 是
                   * 函数/带 compare 的对象时才做本地排序，`true` 只出箭头与点击事件，
                   * **不会**把这一页重新排一遍 —— 否则页内局部序会盖住后端算好的全局序
                   * （第 2 页的行看起来「没排对」，其实只是被本地重排了）。
                   *
                   * sortDirections 两态 descend→ascend，第三下由 antd 自己给出
                   * undefined(不排序、回到默认序)。首点即 descend = 用户要的默认方向。
                   */
                  sorter: metricSortEnabled,
                  sortDirections: ['descend', 'ascend'],
                  sortOrder: compareFilterMetricSort === 'desc' ? 'descend'
                    : compareFilterMetricSort === 'asc' ? 'ascend' : null,
                  render: (_: unknown, record: CompareRow) => {
                    const mdef = compareFilterMetricKey && compareFilterEvaluatorName
                      ? findMetricDef(compareFilterMetricKey)
                      : undefined;
                    // ── 指标模式：展示选中评测器指标的两侧数值 + 差值 ──
                    if (mdef && compareFilterEvaluatorName) {
                      const curTid = record.currentTask?.taskId;
                      const cmpTid = record.compareTask?.taskId;
                      const cur = extractMetricFromItems(
                        curTid != null ? compareEvaluatorCache[curTid] : undefined,
                        compareFilterEvaluatorName, mdef.key,
                      );
                      const cmp = extractMetricFromItems(
                        cmpTid != null ? compareEvaluatorCache[cmpTid] : undefined,
                        compareFilterEvaluatorName, mdef.key,
                      );
                      if (cur == null && cmp == null) {
                        return <span style={{ color: '#c4c6cc' }}>-</span>;
                      }
                      const fmt = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));
                      const num = (v: number | null, color: string) => (
                        v == null
                          ? <span style={{ color: '#c4c6cc' }}>—</span>
                          : <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</span>
                      );
                      let diffNode: React.ReactNode = <span style={{ color: '#c4c6cc' }}>—</span>;
                      let tooltipDiff = '双侧数据不全,无法计算差值';
                      if (cur != null && cmp != null) {
                        const diff = cur - cmp;  // 当前 − 对比：正=增大
                        tooltipDiff = `差值 = ${diff > 0 ? '+' : ''}${fmt(diff)}`;
                        if (diff === 0) {
                          diffNode = <span style={{ color: '#8b8b8b', fontVariantNumeric: 'tabular-nums' }}>0</span>;
                        } else {
                          const isUp = diff > 0;
                          // higherIsWorse: 增大是坏→红；否则增大→绿。未知方向(null/缺省)按中性蓝。
                          const worse = mdef.higherIsWorse;
                          const color = worse === undefined || worse === null
                            ? '#5e6ad2'
                            : (isUp === worse ? '#dc2626' : '#16a34a');
                          const Icon = isUp ? ArrowUpOutlined : ArrowDownOutlined;
                          diffNode = (
                            <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              {fmt(Math.abs(diff))}
                              <Icon style={{ marginLeft: 2, fontSize: 11 }} />
                            </span>
                          );
                        }
                      }
                      return (
                        <Tooltip title={
                          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                            <div>{mdef.label}</div>
                            <div>当前任务: {cur != null ? fmt(cur) : '无'}</div>
                            <div>对比任务: {cmp != null ? fmt(cmp) : '无'}</div>
                            <div>{tooltipDiff}</div>
                          </div>
                        }>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                            {num(cur, '#5e6ad2')}
                            <span style={{ color: '#c4c6cc' }}>/</span>
                            {num(cmp, '#8b5cf6')}
                            <span style={{ color: '#c4c6cc' }}>/</span>
                            {diffNode}
                          </span>
                        </Tooltip>
                      );
                    }
                    // ── 默认：VRU 相对速度 ──
                    const cur = getVruRelativeSpeed(record.currentTask?.taskId);
                    const cmp = getVruRelativeSpeed(record.compareTask?.taskId);
                    if (cur == null && cmp == null) {
                      return <span style={{ color: '#c4c6cc' }}>-</span>;
                    }
                    // 单值样式:当前任务=蓝紫(与顶部当前任务色一致 #5e6ad2)
                    // 对比任务=紫(与顶部对比任务色 #8b5cf6),缺失显示 '—'
                    const num = (v: number | null, color: string) => (
                      v == null
                        ? <span style={{ color: '#c4c6cc' }}>—</span>
                        : <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>{v.toFixed(2)}</span>
                    );
                    let diffNode: React.ReactNode = <span style={{ color: '#c4c6cc' }}>—</span>;
                    let tooltipDiff = '双侧数据不全,无法计算差值';
                    if (cur != null && cmp != null) {
                      const diff = cmp - cur;
                      tooltipDiff = `差值 = ${diff > 0 ? '+' : ''}${diff.toFixed(2)} m/s`;
                      if (Math.abs(diff) < 0.01) {
                        // 差异 < 0.01 视作相同,灰 0.00 不给方向箭头
                        diffNode = <span style={{ color: '#8b8b8b', fontVariantNumeric: 'tabular-nums' }}>0.00</span>;
                      } else {
                        const isDown = diff < 0;
                        const color = isDown ? '#16a34a' : '#dc2626';
                        const Icon = isDown ? ArrowDownOutlined : ArrowUpOutlined;
                        diffNode = (
                          <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {Math.abs(diff).toFixed(2)}
                            <Icon style={{ marginLeft: 2, fontSize: 11 }} />
                          </span>
                        );
                      }
                    }
                    return (
                      <Tooltip title={
                        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                          <div>当前任务: {cur != null ? cur.toFixed(2) + ' m/s' : '无'}</div>
                          <div>对比任务: {cmp != null ? cmp.toFixed(2) + ' m/s' : '无'}</div>
                          <div>{tooltipDiff}</div>
                        </div>
                      }>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                        }}>
                          {num(cur, '#5e6ad2')}
                          <span style={{ color: '#c4c6cc' }}>/</span>
                          {num(cmp, '#8b5cf6')}
                          <span style={{ color: '#c4c6cc' }}>/</span>
                          {diffNode}
                        </span>
                      </Tooltip>
                    );
                  },
                },
                // 「原因」列：选中评测器**且该评测器真的上报了 reason** 时出现（见
                // reasonColumnActive）。两侧各渲染自己聚合后的原因（label × 次数），判定为
                // 「不一致」时整格标红 + Tooltip 对照两侧 code / 次数 / 时间 / 位置 —— 让
                // fail_fail 里「失败原因不一样」、pass_pass 里「豁免逻辑不一样」一眼可辨，
                // 这正是本列存在的理由。
                ...(reasonColumnActive ? [{
                  title: (
                    <Tooltip title={
                      <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 320 }}>
                        <div><b>原因比对</b></div>
                        <div>评测器上报的 fail 失败原因 / pass 豁免原因</div>
                        <div>按 (原因类别, code, 次数) 精确比对：code 不同、次数不同、或一侧有原因另一侧无，都算不一致</div>
                        <div>当前任务 / 对比任务 两侧原因并列展示</div>
                      </div>
                    }>
                      <span>原因 <QuestionCircleOutlined style={{ fontSize: 11, color: '#c4c6cc' }} /></span>
                    </Tooltip>
                  ),
                  key: 'reasonDiff',
                  width: columnWidths.cmpReason,
                  align: 'center' as const,
                  fixed: 'left' as const,
                  onHeaderCell: cmpHeaderCell('cmpReason', '#fafafa'),
                  render: (_: unknown, record: CompareRow) => {
                    const sceneId = record.currentTask?.sceneId || record.compareTask?.sceneId;
                    const cell = sceneId != null ? reasonDiffMap[sceneId] : undefined;
                    if (!cell) return <span style={{ color: '#c4c6cc' }}>—</span>;
                    /**
                     * 单侧原因渲染：每桶按次数降序（后端已排好），`label × N` 直接显示；
                     * 失败原因红、豁免原因橙斜体，一眼分清「为什么挂了」和「为什么放行」。
                     * 该侧无原因时给「无」而不是空白：这是**有信息量的**一档
                     *（干净通过 / 无豁免），与「这列没数据」必须可区分。
                     */
                    const side = (stats: EvaluatorReasonStats | null, color: string) => {
                      if (!stats) return <span style={{ color: '#c4c6cc' }}>无</span>;
                      const items = [...stats.fail, ...stats.exempt];
                      if (!items.length) return <span style={{ color: '#c4c6cc' }}>无</span>;
                      return (
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                          {items.map((r, i) => (
                            <span
                              key={`${r.kind}-${r.code ?? 'none'}-${i}`}
                              style={{
                                color: r.kind === 'fail' ? color : '#d97706',
                                fontSize: 12,
                                ...(r.kind === 'fail' ? {} : { fontStyle: 'italic' as const }),
                              }}
                            >
                              {r.label}{r.count > 1 ? ` ×${r.count}` : ''}
                              {i < items.length - 1 ? '、' : ''}
                            </span>
                          ))}
                        </span>
                      );
                    };
                    const body = (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                        {side(cell.today, '#dc2626')}
                        <span style={{ color: '#c4c6cc' }}>/</span>
                        {side(cell.compare, '#8b5cf6')}
                      </span>
                    );
                    // 仅「不一致」需要额外提示：一致的行扫一眼 label 就够了。
                    // Tooltip 左右并排：左「当前任务」、右「对比任务」，每个 (kind, code) 一行，
                    // 逐个发生点按序号对齐显示评估器上报的**说明（desc）**，一侧缺的写「—」。
                    // 不显示时间和位置：坐标人不会读，位置/时间对照交给缩略图（点位 + #序号）。
                    // 旧数据没有 occurrences / 逐点 desc：退回聚合层的首条 desc。
                    const descLines = (r: EvaluatorReasonStat | undefined): string[] => {
                      if (!r) return [];
                      const ds = (r.occurrences ?? []).map((o) => o.desc).filter((d): d is string => !!d);
                      return ds.length ? ds : (r.desc ? [r.desc] : []);
                    };
                    type TipRow = { key: string; kind: 'fail' | 'exempt'; code: string | null; label: string;
                                    t?: EvaluatorReasonStat; c?: EvaluatorReasonStat };
                    const tipRows: TipRow[] = (() => {
                      const rows = new Map<string, TipRow>();
                      const put = (stats: EvaluatorReasonStats | null, side: 't' | 'c') => {
                        [...(stats?.fail ?? []), ...(stats?.exempt ?? [])].forEach((r) => {
                          const key = `${r.kind}|${r.code ?? ''}`;
                          const row = rows.get(key) ?? { key, kind: r.kind, code: r.code, label: r.label };
                          row[side] = r;
                          rows.set(key, row);
                        });
                      };
                      put(cell.today, 't');
                      put(cell.compare, 'c');
                      return Array.from(rows.values());
                    })();
                    const sideState = (stats: EvaluatorReasonStats | null) =>
                      !stats ? '无该评测器结果' : (stats.fail.length + stats.exempt.length ? null : '无原因（干净通过）');
                    const cellBox = (r: EvaluatorReasonStat | undefined, accent: string) => {
                      if (!r) return <span style={{ opacity: 0.5 }}>—</span>;
                      const lines = descLines(r);
                      return (
                        <div style={{ borderLeft: `2px solid ${accent}`, paddingLeft: 6 }}>
                          <div style={{ opacity: 0.8 }}>
                            {r.kind === 'exempt' ? '豁免' : '失败'} × {r.count}
                          </div>
                          {lines.map((d, k) => (
                            <div key={k}>{lines.length > 1 ? `${k + 1}. ` : ''}{d}</div>
                          ))}
                        </div>
                      );
                    };
                    // 「原因同·时空异」只留一句话：为什么判它，具体点位去缩略图对照。
                    const gapTip = cell.verdict === 'diff_spatial' ? (
                      <div style={{ marginTop: 6, borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: 4, opacity: 0.9 }}>
                        {cell.gaps?.some((g) => g.overPos || g.overTime)
                          ? '有发生点的位置或时间对不上，请对照两侧缩略图上的点位'
                          : '点数相同但无法一一对应，请对照两侧缩略图上的点位'}
                      </div>
                    ) : null;
                    const tip = (
                      <div style={{ fontSize: 12, lineHeight: 1.7, width: 640 }}>
                        <div style={{ marginBottom: 4 }}>
                          原因判定：
                          {cell.verdict === 'diff' ? '不一致'
                            : cell.verdict === 'diff_spatial' ? '原因同·时空异（code、次数相同，但发生点的位置或时间在阈值内配不上）'
                            : cell.verdict === 'only_current' ? '仅当前任务有该评测器结果' : '一致'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16, rowGap: 6 }}>
                          <div style={{ fontWeight: 600 }}>当前任务</div>
                          <div style={{ fontWeight: 600 }}>对比任务</div>
                          {sideState(cell.today) ? <div>{sideState(cell.today)}</div> : null}
                          {sideState(cell.compare) ? <div>{sideState(cell.compare)}</div> : null}
                          {tipRows.map((row) => (
                            <Fragment key={row.key}>
                              <div style={{ gridColumn: '1 / -1', opacity: 0.9 }}>
                                · {row.label}（{row.code ?? '未上报 code'}）
                              </div>
                              {cellBox(row.t, '#5e6ad2')}
                              {cellBox(row.c, '#8b5cf6')}
                            </Fragment>
                          ))}
                        </div>
                        {gapTip}
                      </div>
                    );
                    return (
                      <Tooltip title={tip} styles={{ root: { maxWidth: 700 } }}>
                        {cell.verdict === 'diff'
                          ? <Tag color="error" style={{ marginInlineEnd: 0 }}>{body}</Tag>
                          : cell.verdict === 'diff_spatial'
                            // 与「不一致」的红色区分：原因本身一致，只是时空对不上，用警示橙
                            ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>{body}</Tag>
                            : body}
                      </Tooltip>
                    );
                  },
                }] : []),
              ],
            },
            // ── 当前任务区（蓝紫 #5e6ad2） ──
            {
              title: <span style={{ color: '#5e6ad2', fontWeight: 600 }}>{`当前任务 (${currentJobName || 'Job ' + jobId})`}</span>,
              children: [
                {
                  title: '状态',
                  key: 'curStatus',
                  width: columnWidths.cmpSideStatus,
                  align: 'center',
                  className: 'cur-col-first',
                  onHeaderCell: cmpHeaderCell('cmpSideStatus', '#eef0ff'),
                  render: (_: unknown, r: CompareRow) => renderStatusText(r.currentTask),
                },
                {
                  title: '仿真结果',
                  key: 'curEval',
                  width: columnWidths.cmpSideEval,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideEval', '#eef0ff'),
                  render: (_: unknown, r: CompareRow) => renderEvaluatorSummary(r.currentTask),
                },
                {
                  title: '预览',
                  key: 'curPreview',
                  width: columnWidths.cmpSidePreview,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSidePreview', '#eef0ff'),
                  render: (_: unknown, r: CompareRow) => renderComparePreview(r.currentTask?.taskId, r),
                },
                {
                  title: 'Foxglove',
                  key: 'curFox',
                  width: columnWidths.cmpSideFox,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideFox', '#eef0ff'),
                  render: (_: unknown, r: CompareRow) => (
                    <Button
                      size="small"
                      type="link"
                      disabled={!r.currentTask?.taskId || !r.currentTask?.sceneName}
                      onClick={() => {
                        if (r.currentTask?.taskId && r.currentTask?.sceneName) {
                          void handleOpenFoxglove(r.currentTask.taskId, r.currentTask.sceneName);
                        }
                      }}
                    >
                      链接
                    </Button>
                  ),
                },
                {
                  // 当前任务评论：按 (sceneId, jobId) 维度，挂在当前 currentTask 的 task 上
                  title: '评论',
                  key: 'curComment',
                  width: columnWidths.cmpSideComment,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideComment', '#eef0ff'),
                  onCell: () => ({ style: { overflow: 'visible' } }),
                  render: (_: unknown, r: CompareRow) => {
                    const sceneId = r.currentTask?.sceneId ?? 0;
                    const taskId = r.currentTask?.taskId;
                    const targetJobId = r.currentTask?.jobId ?? jobId;
                    const s = commentSummary[sceneId];
                    const hasComment = s != null && s.total > 0;
                    const label = hasComment
                      ? `${s.latestTitle ?? '评论'} (${s.total})`
                      : '评论';
                    return (
                      <Button
                        size="small"
                        type="link"
                        icon={<CommentOutlined />}
                        disabled={!taskId || sceneId <= 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!taskId) return;
                          setCommentTarget({
                            sceneId,
                            sceneName: r.sceneName ?? null,
                            jobId: targetJobId,
                            taskId,
                          });
                        }}
                        title={hasComment ? `最新：${s.latestTitle ?? ''}` : '点击新建评论'}
                        style={{ padding: '0 4px', maxWidth: '100%', overflow: 'hidden' }}
                      >
                        <span style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{label}</span>
                      </Button>
                    );
                  },
                },
                {
                  // 当前任务侧「操作」:与普通视图同 popover(Task 日志/mcap/复现命令/保密),
                  // 复用 renderTaskActionPopover;无 currentTask 的 onlyInCurrent=false
                  // 场景(理论不会到这)禁用按钮避免 crash。
                  title: '操作',
                  key: 'curAction',
                  width: columnWidths.cmpSideAction,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideAction', '#eef0ff'),
                  render: (_: unknown, r: CompareRow) =>
                    r.currentTask
                      ? renderTaskActionPopover(r.currentTask)
                      : <Button size="small" icon={<MoreOutlined />} disabled />,
                },
              ],
            },
            // ── 对比任务区（琥珀 #d97706） ──
            {
              title: <span style={{ color: '#d97706', fontWeight: 600 }}>{`对比任务 (${compareJobName})`}</span>,
              children: [
                {
                  title: '状态',
                  key: 'cmpStatus',
                  width: columnWidths.cmpSideStatus,
                  align: 'center',
                  className: 'cmp-col-first',
                  onHeaderCell: cmpHeaderCell('cmpSideStatus', '#fff7ed'),
                  render: (_: unknown, r: CompareRow) => renderStatusText(r.compareTask),
                },
                {
                  title: '仿真结果',
                  key: 'cmpEval',
                  width: columnWidths.cmpSideEval,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideEval', '#fff7ed'),
                  render: (_: unknown, r: CompareRow) => renderEvaluatorSummary(r.compareTask),
                },
                {
                  title: '预览',
                  key: 'cmpPreview',
                  width: columnWidths.cmpSidePreview,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSidePreview', '#fff7ed'),
                  render: (_: unknown, r: CompareRow) => renderComparePreview(r.compareTask?.taskId, r),
                },
                {
                  title: 'Foxglove',
                  key: 'cmpFox',
                  width: columnWidths.cmpSideFox,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideFox', '#fff7ed'),
                  render: (_: unknown, r: CompareRow) => (
                    <Button
                      size="small"
                      type="link"
                      disabled={!r.compareTask?.taskId || !r.compareTask?.sceneName}
                      onClick={() => {
                        if (r.compareTask?.taskId && r.compareTask?.sceneName) {
                          void handleOpenFoxglove(r.compareTask.taskId, r.compareTask.sceneName);
                        }
                      }}
                    >
                      链接
                    </Button>
                  ),
                },
                {
                  // 对比任务评论：按 (sceneId, compareJobId) 维度，挂在 compareTask 上；
                  // onlyInCurrent 行右侧无 compareTask，按钮 disabled。
                  title: '评论',
                  key: 'cmpComment',
                  width: columnWidths.cmpSideComment,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideComment', '#fff7ed'),
                  onCell: () => ({ style: { overflow: 'visible' } }),
                  render: (_: unknown, r: CompareRow) => {
                    const sceneId = r.compareTask?.sceneId ?? 0;
                    const taskId = r.compareTask?.taskId;
                    const targetJobId = r.compareTask?.jobId ?? selectedCompareJobId ?? 0;
                    const s = compareCommentSummary[sceneId];
                    const hasComment = s != null && s.total > 0;
                    const label = hasComment
                      ? `${s.latestTitle ?? '评论'} (${s.total})`
                      : '评论';
                    return (
                      <Button
                        size="small"
                        type="link"
                        icon={<CommentOutlined />}
                        disabled={!taskId || sceneId <= 0 || !targetJobId}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!taskId || !targetJobId) return;
                          setCommentTarget({
                            sceneId,
                            sceneName: r.compareTask?.sceneName ?? r.sceneName ?? null,
                            jobId: targetJobId,
                            taskId,
                          });
                        }}
                        title={hasComment ? `最新：${s.latestTitle ?? ''}` : '点击新建评论'}
                        style={{ padding: '0 4px', maxWidth: '100%', overflow: 'hidden' }}
                      >
                        <span style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{label}</span>
                      </Button>
                    );
                  },
                },
                {
                  // 对比任务侧「操作」:与左侧同 popover(Task 日志/mcap/复现命令/保密),
                  // 复用 renderTaskActionPopover;无 compareTask 时(onlyInCurrent 行)按钮 disabled。
                  title: '操作',
                  key: 'cmpAction',
                  width: columnWidths.cmpSideAction,
                  align: 'center',
                  onHeaderCell: cmpHeaderCell('cmpSideAction', '#fff7ed'),
                  render: (_: unknown, r: CompareRow) =>
                    r.compareTask
                      ? renderTaskActionPopover(r.compareTask)
                      : <Button size="small" icon={<MoreOutlined />} disabled />,
                },
              ],
            },
          ]}
        />
      </Spin>
      {/* 双图放大对比 modal —— A1 路径，左右并排两张 preview 独立缩放/拖拽 */}
      <Modal
        open={!!compareLightboxRow}
        onCancel={() => setCompareLightboxRow(null)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        styles={{
          body: { padding: 0, background: '#0a0a0a' },
          content: { padding: 0, background: '#0a0a0a' },
          header: { display: 'none' },
        }}
        destroyOnHidden
        title={null}
      >
        {compareLightboxRow && (
          <div style={{ height: '88vh', display: 'flex', flexDirection: 'column' }}>
            {/* 顶部信息条：场景名 + 提示 */}
            <div
              style={{
                padding: '8px 16px',
                color: '#e5e7eb',
                fontSize: 13,
                background: 'rgba(255,255,255,0.04)',
                borderBottom: '1px solid #1f1f1f',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexShrink: 0,
              }}
            >
              <span style={{ fontWeight: 600 }}>{compareLightboxRow.sceneName}</span>
              <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 11 }}>
                滚轮缩放 · 拖拽平移 · 双击复位 · ESC 关闭
              </span>
            </div>
            {/* 双图并排画布 */}
            <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
              <ComparePreviewPane
                url={
                  compareLightboxRow.currentTask?.taskId
                    ? comparePreviewUrlMap[compareLightboxRow.currentTask.taskId]
                    : undefined
                }
                label={`当前任务 · ${currentJobName || '当前 Job'}`}
                taskName={compareLightboxRow.currentTask?.sceneName}
                emptyHint={
                  compareLightboxRow.currentTask?.taskId ? '预览加载中…' : '当前任务缺该场景'
                }
              />
              <ComparePreviewPane
                url={
                  compareLightboxRow.compareTask?.taskId
                    ? comparePreviewUrlMap[compareLightboxRow.compareTask.taskId]
                    : undefined
                }
                label={`对比任务 · ${compareJobName || '对比 Job'}`}
                taskName={compareLightboxRow.compareTask?.sceneName}
                emptyHint={
                  compareLightboxRow.compareTask?.taskId ? '预览加载中…' : '对比任务缺该场景'
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );

  // 普通 Task 列表页与「不通过统计」独立页（statsMode）共用的筛选表单 + 任务表格。
  // 抽出为常量，避免两处布局重复维护；statsMode 下表单内「任务对比」按钮自动隐藏。
  const taskFilterAndTable = (
    <>
      {/* onFinish + 查询按钮 htmlType="submit":输入框内按 Enter 即查询。详见 JobList 同处注释。
          handleSearch 是 async,显式 void 包一层:rc-field-form 的 submit 只 catch 同步抛错,
          不包会把 rejected promise 漏成 unhandled rejection。 */}
      <Form form={form} layout="inline" style={{ marginBottom: 16 }} onFinish={() => { void handleSearch(); }}>
        {/* 任务级筛选：按 task / scene 维度过滤（taskId、场景、标签、集合、JIRA、状态） */}
        <div style={FILTER_GROUP_STYLE}>
          <Tag color="blue" style={FILTER_GROUP_TAG_STYLE}>任务级</Tag>
          <Form.Item name="taskId" label="TaskID">
            <InputNumber
              placeholder="请输入TaskID"
              min={1}
              controls={false}
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item name="sceneName" label="场景名称">
            <Input placeholder="请输入场景名称" allowClear style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="mapName" label="地图名">
            <Input placeholder="请输入地图名(子串)" allowClear style={{ width: 200 }} />
          </Form.Item>
          {/* 国家区域：按 scene_inspection.region 多值精确匹配(IN/OR)。
              不选 = 不过滤（全部区域）——空值不能兜底成 cn，否则海外任务被静默隐藏。
              值域与后端 _REGION_WHITELIST 同源于 @/constants/region。 */}
          <Form.Item name="regions" label="国家区域">
            <Select
              {...enterToSearch(10)}
              mode="multiple"
              allowClear
              placeholder="全部区域"
              options={SCENE_REGION_OPTIONS}
              maxTagCount="responsive"
              style={{ minWidth: 170 }}
            />
          </Form.Item>
          <Form.Item name="label" label="场景标签">
            <Select
              {...enterToSearch(1)}
              placeholder="选择 logsim label"
              allowClear
              showSearch
              loading={sceneLabelLoading}
              notFoundContent={
                sceneLabelLoading ? <Spin size="small" /> : '该任务暂无场景标签'
              }
              style={{ width: 200 }}
              options={sceneLabelOptions.map((lab) => ({
                // __unlabeled__ 哨兵在 UI 显示为「未分类」，与按场景分类抽屉口径一致
                label: lab === '__unlabeled__' ? '未分类' : lab,
                value: lab,
              }))}
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          {sceneSetFilterAllowed && (
            <Form.Item label="场景集">
              <TreeSelect
                placeholder="按任务归属场景集筛选（含下级）"
                allowClear showSearch
                style={{ minWidth: 220, maxWidth: 360 }}
                treeData={jobSceneSetOptions} treeNodeLabelProp="label" treeNodeFilterProp="label"
                value={queryParams.sceneSetId} loading={sceneSetStats.loading}
                notFoundContent={sceneSetStats.error ? <Button type="link" onClick={sceneSetStats.onRetry}>加载失败，点击重试</Button> : undefined}
                onChange={(value: number | undefined) => handleSceneSetTreeChange(value)}
              />
            </Form.Item>
          )}
          <Form.Item name="treeNodeIds" label="场景集合">
            <TreeSelect
              {...enterToSearch(2)}
              placeholder="选择场景集合（可多选，选目录=选其下全部）"
              allowClear
              multiple
              treeCheckable
              showCheckedStrategy="SHOW_PARENT"
              maxTagCount="responsive"
              showSearch
              loading={sceneTreeLoading}
              notFoundContent={sceneTreeLoading ? <Spin size="small" /> : '该任务暂无场景集合'}
              style={{ minWidth: 220, maxWidth: 360 }}
              treeData={toTreeSelectData(sceneTreeNodes)}
              treeNodeFilterProp="title"
              treeDefaultExpandAll
            />
          </Form.Item>
          <Form.Item name="apiCode" label="API_CODE">
            <Input placeholder="如 AIP-226186" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status" label="任务状态">
            <Select
              {...enterToSearch(3)}
              placeholder="请选择状态"
              allowClear
              style={{ width: 150 }}
            >
              {statusList.map((item, index) => (
                <Select.Option
                  key={item.code ?? item.value ?? index}
                  value={item.value}
                >
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {/* 评论筛选:合三为一(关键词 + scope + 有/无),详见 CommentFilterPopover。
              受控 state = commentFilter;查询在 handleSearch 里从 state 组装到 params。
              onChange 后立即触发一次查询,避免"改了但没查"的困惑。 */}
          <Form.Item label="评论">
            <CommentFilterPopover
              value={commentFilter}
              onChange={(v) => {
                setCommentFilter(v);
                const commentPart = commentFilterToParams(v);
                const newParams: TaskListParams = {
                  ...queryParams,
                  ...form.getFieldsValue(),
                  commentKeyword: undefined,
                  commentScope: undefined,
                  hasComment: undefined,
                  ...commentPart,
                  current: 1,
                };
                setQueryParams(newParams);
                void loadTaskList(newParams);
                void loadFilteredCount(newParams);
              }}
            />
          </Form.Item>
        </div>
        {/* 评估器级筛选：基于 evaluator 表（评测器名 / type==2 有责无责判定） */}
        <div style={FILTER_GROUP_STYLE}>
          <Tag color="purple" style={FILTER_GROUP_TAG_STYLE}>评估器级</Tag>
          <Form.Item name="evaluatorName" label="评估项">
            <Select
              {...enterToSearch(4)}
              placeholder="选择评估项"
              allowClear
              showSearch
              loading={evaluatorNameLoading}
              notFoundContent={
                evaluatorNameLoading ? <Spin size="small" /> : '该任务暂无评测记录'
              }
              style={{ width: 200 }}
              options={evaluatorNameOptions.map((name) => ({ label: getEvaluatorDisplay(name), value: name }))}
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label ?? '').toLowerCase().includes(q) ||
                  String(option?.value ?? '').toLowerCase().includes(q)
                );
              }}
              /**
               * **stats 页即时生效**：选完评估项立刻重查，不等「查询」按钮。
               *
               * 这一页是「看某个评估项的明细」，评估项一换，整页内容（列表 + 「指标」下拉 +
               * 指标列）都该跟着换；再要求按一次「查询」纯属多一步。对比页的评测器下拉
               * 就是这个行为，这里对齐它。
               *
               * 只在此页加 —— 普通任务列表页的筛选是「凑齐一组条件再查」的批量表单
               * （场景名/状态/标签…），逐个即时生效会连着打好几次接口，反而更慢。
               */
              onChange={() => {
                if (!statsMode) return;
                // 指标隶属于评估项：换评估项后旧指标必然失效，先清掉再查，
                // 避免这一趟把旧 metricKey 带到新评估项上（后端不认，列上会全是 `—`）。
                form.setFieldsValue({ metricKey: undefined, reasonFailCode: undefined, reasonExemptCode: undefined });
                void handleSearch();
              }}
            />
          </Form.Item>
          <Form.Item name="evaluatorStatus" label="评估项状态">
            <Select
              {...enterToSearch(5)}
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '通过', value: 'pass' },
                { label: '不通过', value: 'fail' },
                { label: '异常', value: 'error' },
              ]}
              /**
               * **stats 页即时生效**，与上面「评估项」同款：状态是评估项的判定维度，
               * 选完却要再点「查询」才变，用户会看到「通过 / 不通过结果一样」（其实根本没重查）。
               * 只在 stats 页加，普通列表页仍是「凑齐条件再查」的批量表单。
               * 清空（allowClear）同样走这里，恢复为该评估项的全部场景。
               */
              onChange={(v) => {
                if (!statsMode) return;
                // 状态决定允许的原因种类：通过 → 只有豁免原因；不通过 → 只有失败原因。
                // 已选的、属于被排除那一类的原因必须一并清掉，否则留下「下拉置灰了、却仍在后端
                // 生效」的隐形筛选，列表会被筛成空而用户看不出为什么。
                const scope = REASON_KIND_BY_EVAL_STATUS[v ?? ''];
                if (scope === 'exempt') form.setFieldsValue({ reasonFailCode: undefined });
                else if (scope === 'fail') form.setFieldsValue({ reasonExemptCode: undefined });
                void handleSearch();
              }}
            />
          </Form.Item>
          {/* 「不通过统计」页专用：选指标即多出一列逐场景参数值（见 columns 里的
              statsMetric）。只在 statsMode 渲染 —— 普通列表页没有这一列，放个下拉
              只会让人以为选了会有变化。评估项未选时禁用：指标清单是按评估器拉的。 */}
          {statsMode && (
            <Form.Item name="metricKey" label="指标">
              <Select
                {...enterToSearch(11)}
                placeholder="选择指标"
                allowClear
                showSearch
                disabled={!queryParams.evaluatorName}
                loading={statsMetricDefsLoading}
                notFoundContent={
                  statsMetricDefsLoading
                    ? <Spin size="small" />
                    : (queryParams.evaluatorName ? '该评估项未上报量化指标' : '请先选择评估项')
                }
                style={{ width: 200 }}
                options={statsMetricDefs.map((m) => ({ label: m.label || m.key, value: m.key }))}
                filterOption={(input, option) => {
                  const q = input.toLowerCase();
                  return (
                    String(option?.label ?? '').toLowerCase().includes(q) ||
                    String(option?.value ?? '').toLowerCase().includes(q)
                  );
                }}
                /**
                 * 与「评估项」同款**即时生效**：选完指标立刻重查，指标列当场出现/更新，
                 * 不用再按「查询」。指标清单是评估项变化时自动拉好的（见 statsMetricDefs
                 * 的 effect），这里只负责把选中值落到查询条件上。
                 */
                onChange={() => { void handleSearch(); }}
              />
            </Form.Item>
          )}
          {/* 「不通过统计」页专用：按评估器上报的**失败原因 / 豁免原因**筛场景。两个下拉并列、互斥
              （选一个清另一个）——它们筛的是同一个 reason，只是种类不同；并排给用户，是因为
              「失败原因」只在未通过的场景里有、「豁免原因」只在靠豁免通过的场景里有，用户心里
              想的是两个独立的问题。选项来自评估器自描述（statsReasonDefs），无注册表。
              评估项未选 / 评估器没上报任何该类原因时置灰（当普通场景，不筛选）。 */}
          {statsMode && (
            <>
              {([
                { name: 'reasonFailCode', label: '失败原因', other: 'reasonExemptCode', defs: statsFailReasons, tag: 'fail' },
                { name: 'reasonExemptCode', label: '豁免原因', other: 'reasonFailCode', defs: statsExemptReasons, tag: 'exempt' },
              ] as const).map((cfg, i) => (
                <Form.Item key={cfg.name} name={cfg.name} label={cfg.label}>
                  <Select
                    {...enterToSearch(12 + i)}
                    placeholder={
                      !queryParams.evaluatorName ? '先选评估项'
                        : statsReasonScope && statsReasonScope !== cfg.tag
                          ? (statsReasonScope === 'exempt' ? '通过状态下仅可筛豁免原因' : '不通过状态下仅可筛失败原因')
                          : statsReasonDefsLoading ? '加载中…'
                            : cfg.defs.length === 0 ? `该评估项未上报${cfg.label}` : '全部'
                    }
                    allowClear
                    showSearch
                    disabled={
                      !queryParams.evaluatorName
                      || (!!statsReasonScope && statsReasonScope !== cfg.tag)
                      || (!statsReasonDefsLoading && cfg.defs.length === 0)
                    }
                    loading={statsReasonDefsLoading}
                    style={{ width: 180 }}
                    options={cfg.defs.map((d) => ({ label: d.label || d.code, value: d.code }))}
                    filterOption={(input, option) => {
                      const q = input.toLowerCase();
                      return (
                        String(option?.label ?? '').toLowerCase().includes(q) ||
                        String(option?.value ?? '').toLowerCase().includes(q)
                      );
                    }}
                    /**
                     * 与「评估项 / 指标 / 评估项状态」同款**即时生效**。选了就把另一个下拉清掉
                     * （同一个 reason，不能同时是失败原因又是豁免原因，否则必然筛成空集、
                     * 用户只会以为「筛坏了」）。清空（allowClear）同样走这里，恢复全部。
                     */
                    onChange={(v) => {
                      if (v) form.setFieldsValue({ [cfg.other]: undefined });
                      void handleSearch();
                    }}
                  />
                </Form.Item>
              ))}
            </>
          )}
          <Form.Item name="respEventName" label="有无责事件">
            <Select
              {...enterToSearch(6)}
              placeholder="选择有无责事件"
              allowClear
              showSearch
              loading={respEventNameLoading}
              notFoundContent={
                respEventNameLoading ? <Spin size="small" /> : '该任务暂无有无责事件'
              }
              style={{ width: 200 }}
              options={respEventNameOptions.map((name) => ({ label: getEvaluatorDisplay(name), value: name }))}
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label ?? '').toLowerCase().includes(q) ||
                  String(option?.value ?? '').toLowerCase().includes(q)
                );
              }}
            />
          </Form.Item>
          <Form.Item name="responsibility" label="责任判定">
            <Select
              {...enterToSearch(7)}
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '有责', value: 'with' },
                { label: '无责', value: 'without' },
              ]}
            />
          </Form.Item>
          <Form.Item name="checkName" label="检查项">
            <Select
              {...enterToSearch(8)}
              placeholder="选择检查项"
              allowClear
              showSearch
              loading={checkNameLoading}
              notFoundContent={
                checkNameLoading ? <Spin size="small" /> : '该任务暂无检查项'
              }
              style={{ width: 200 }}
              options={checkNameOptions.map((name) => ({ label: getEvaluatorDisplay(name), value: name }))}
              filterOption={(input, option) => {
                const q = input.toLowerCase();
                return (
                  String(option?.label ?? '').toLowerCase().includes(q) ||
                  String(option?.value ?? '').toLowerCase().includes(q)
                );
              }}
            />
          </Form.Item>
          <Form.Item name="checkStatus" label="检查项状态">
            <Select
              {...enterToSearch(9)}
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '通过', value: 'pass' },
                { label: '不通过', value: 'fail' },
              ]}
            />
          </Form.Item>
          <Form.Item label="忽略 check 不通过的场景">
            <Tooltip
              title={
                taskCount?.excludedCheckFailedCount
                  ? `当前已剔除 ${taskCount.excludedCheckFailedCount} 个场景；总览/通过率/对比均按剔除后口径计算`
                  : '打开后该 Job 内 check 不通过（type=3 evaluator pass=false）的场景将整条剔除，不计入分子分母'
              }
            >
              <Switch
                checked={Boolean(taskCount?.excludeCheckFailed)}
                loading={excludeCheckToggling}
                onChange={handleToggleExcludeCheckFailed}
                disabled={!jobId}
              />
            </Tooltip>
          </Form.Item>
        </div>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
            {!statsMode && (
              <Tooltip title={compareEntryHint}>
                <Button onClick={openCompareEntry}>{compareEntryText}</Button>
              </Tooltip>
            )}
            <Button icon={<DownloadOutlined />} loading={exportLoading} onClick={handleExportCsv}>
              导出报表
            </Button>
            {/* 「基于当前筛选项创建任务」入口 —— 就近放在查询/重置/导出按钮组里,始终展示。
                未查询 / sceneCount=0 → disabled + tooltip 提示暂未选择场景;
                sceneCount>200 → 加 ⚠ 图标 + tooltip 提示算力占用。 */}
            {(() => {
              const sceneCount = filteredCount?.sceneCount ?? 0;
              const notQueried = !filteredCount;
              const disabled = notQueried || sceneCount === 0;
              const overThreshold = sceneCount > 200;
              const tip = notQueried
                ? '暂未选择场景 —— 请先在上方查询筛选场景后再创建任务'
                : sceneCount === 0
                  ? '暂未选择场景 —— 当前筛选无命中场景,请调整筛选条件'
                  : overThreshold
                    ? `当前 ${sceneCount} 场景,创建后会占用较多算力`
                    : `按当前筛选项创建新任务(${sceneCount} 场景)`;
              return (
                <Tooltip title={tip}>
                  {/* Button disabled 时不触发子元素事件,tooltip 也会被吞;
                      antd 官方文档建议禁用态外面包一层 span 让 hover 事件冒到 Tooltip。 */}
                  <span style={{ display: 'inline-block' }}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setRerunFilters(queryParams);
                        setRerunConfirmOpen(true);
                      }}
                      disabled={disabled}
                      icon={
                        overThreshold
                          ? <WarningOutlined style={{ color: '#faad14' }} />
                          : <ThunderboltOutlined />
                      }
                    >
                      基于当前筛选项创建任务{
                        !notQueried && sceneCount > 0 ? ` (${sceneCount})` : ''
                      }
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </Space>
        </Form.Item>
      </Form>

      {/* 「筛选后统计」独立一行：查询后出现，跟随当前筛选条件展示 场景数/通过/不通过/异常 */}
      <FilteredStatRow
        count={filteredCount}
        loading={filteredCountLoading}
      />

      <style>{`
        .fox-link-btn {
          display: inline-flex; align-items: center; gap: 4px;
          height: 24px; padding: 0 9px; margin: 0;
          font-size: 12px; line-height: 1; font-weight: 500;
          color: #5e6ad2; background: #f4f4fd;
          border: 1px solid #e3e2fb; border-radius: 999px;
          cursor: pointer; user-select: none;
          transition: background .18s ease, border-color .18s ease, color .18s ease, box-shadow .18s ease, transform .18s ease;
        }
        .fox-link-btn:hover {
          background: #5e6ad2; border-color: #5e6ad2; color: #fff;
          box-shadow: 0 2px 8px rgba(94,106,210,.32);
          transform: translateY(-1px);
        }
        .fox-link-btn:active { transform: translateY(0); box-shadow: none; }
        .fox-link-btn:disabled {
          color: #c4c4cc; background: #f6f6f8; border-color: #ececf0;
          cursor: not-allowed; box-shadow: none; transform: none;
        }
        .fox-link-btn .anticon { font-size: 12px; line-height: 0; }
      `}</style>
      <Table
        rowKey="taskId"
        dataSource={data}
        loading={loading}
        size="small"
        components={{ header: { cell: ResizableTitle } }}
        tableLayout="fixed"
        scroll={{ x: Object.values(columnWidths).reduce((a, b) => a + b, 0) }}
        columns={columns}
        /**
         * stats 页指标列排序的唯一入口（表头箭头）。
         *
         * 只在 action === 'sort' 时动手：翻页也会走这个 onChange（并附带当前 sorter），
         * 不滤掉就会在每次翻页时多打一次一模一样的请求（分页自己走下面 pagination.onChange）。
         * 排序变了必须**回到第 1 页**：全局序已整体重排，停在第 3 页会看到完全另一段数据，
         * 用户会当成「排序没生效」。
         */
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra?.action !== 'sort') return;
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          const next: MetricSort | undefined =
            s?.order === 'descend' ? 'desc'
              : s?.order === 'ascend' ? 'asc' : undefined;
          if (next === queryParams.metricSort) return;
          const newParams: TaskListParams = { ...queryParams, metricSort: next, current: 1 };
          setQueryParams(newParams);
          syncTaskFiltersToUrl(newParams);
          void loadTaskList(newParams);
        }}
        pagination={{
          current: queryParams.current,
          pageSize: queryParams.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100', '1000'],
          showQuickJumper: true,
          showTotal: (t) => `总共 ${t} 条`,
          onChange: async (page, pageSize) => {
            const newParams = {
              ...queryParams,
              current: page,
              pageSize: pageSize ?? queryParams.pageSize,
            };
            setQueryParams(newParams);
            // 页码/页大小连同全部筛选同步到 URL，使分享链接与从场景编辑返回
            // 都能恢复到当前这一页、这组条件
            syncTaskFiltersToUrl(newParams);
            await loadTaskList(newParams);
          },
        }}
      />
    </>
  );

  // 任务列表评论 Drawer：statsMode / compareMode / 默认 return 三处都需要挂载，
  // 否则对应页点「评论」时 commentTarget 已 set 但 Drawer 不在 DOM 中，肉眼看就是「点了没反应」。
  const commentDrawerNode = (
    <SceneCommentDrawer
      open={commentTarget !== null}
      onClose={() => {
        setCommentTarget(null);
        refreshCommentSummary();
        // 对比模式下两侧 summary 都可能被刚才的评论改动影响
        if (compareMode) refreshCompareCommentSummary();
      }}
      sceneId={commentTarget?.sceneId ?? 0}
      sceneName={commentTarget?.sceneName ?? null}
      jobId={commentTarget?.jobId}
      taskId={commentTarget?.taskId}
      title="任务评论"
    />
  );

  // 「基于当前查询发起任务」两步流的 modal 挂载 —— statsMode / compareMode 两个
  // 独立 return 分支与主 return 三处都需要(各分支的 taskFilterAndTable 都带触发
  // 按钮),漏挂任一分支都会"点了没反应"。sceneCount 按 rerunFilters
  // .compareJobId 是否有值判定用 compareFilteredCount 还是 filteredCount,与
  // 触发按钮口径一致。
  const rerunModalsNode = (
    <>
      {rerunConfirmOpen && rerunFilters && (() => {
        const isFromCompare = rerunFilters.compareJobId != null;
        const activeCount = isFromCompare ? compareFilteredCount : filteredCount;
        return (
          <RerunFromQueryConfirmModal
            open={rerunConfirmOpen}
            onCancel={() => setRerunConfirmOpen(false)}
            onConfirm={() => {
              setRerunConfirmOpen(false);
              setRerunCreateOpen(true);
            }}
            jobId={jobId}
            jobName={jobBasicInfo?.jobName || currentJobName || undefined}
            sceneCount={activeCount?.sceneCount ?? 0}
            taskCount={activeCount?.totalCount ?? 0}
            filters={extractNonEmptyFilters(toQueryFilters(rerunFilters), {
              statusList,
            })}
          />
        );
      })()}
      {rerunCreateOpen && rerunFilters && (() => {
        const isFromCompare = rerunFilters.compareJobId != null;
        const activeCount = isFromCompare ? compareFilteredCount : filteredCount;
        return (
          <CreateJobModal
            open={rerunCreateOpen}
            onClose={() => setRerunCreateOpen(false)}
            onSuccess={() => {
              setRerunCreateOpen(false);
              message.success('任务已创建');
            }}
            // 用当前 Job 的元信息(算法包/baseVersion/优先级/任务类型)作复制默认值。
            // 与 byTaskQuery 共用:CreateJobModal 打开时先 applyCopyRecord 填版本,
            // 再切 sceneTab=byJob 让场景池锁在 byTaskQuery.filters,提交时只读
            // filters 反查、忽略 sceneIds/sceneSetIds,不会互相冲突。
            copyRecord={jobMeta}
            byTaskQuery={{
              jobId,
              filters: toQueryFilters(rerunFilters),
              filterEntries: extractNonEmptyFilters(toQueryFilters(rerunFilters), {
                statusList,
              }),
              jobName: jobBasicInfo?.jobName || currentJobName || undefined,
              sceneCount: activeCount?.sceneCount ?? 0,
            }}
          />
        );
      })()}
    </>
  );

  // 「不通过统计」独立页（statsMode）：参考「任务对比」独立页布局，但只看单个 Job——
  //   单个任务基本信息 + 单个仿真结果（通过率/总数/通过数/不通过数/异常数）+
  //   评测器明细（场景数/占比/pass/fail）+ Job 内 task 列表（带全部筛选，默认筛不通过）。
  if (statsMode) {
    // 基本信息字段（参考对比页 renderInfoCol：左侧灰色标签 + 右侧值，定义网格对齐）。
    // statsMode 用的是 /job/detail 返回的 JobDetailData（jobBasicInfo）；比普通模式
    // 的 JobItem 少 buildType / sceneGroupRefs / sourceName 等——bazel 相关的 build
    // 元数据从并行加载的 jobMeta（JobItem）补上，保证「算法版本」与 Job 列表口径一致。
    const statsBaseVer = pickBaseVersion(jobMeta);
    const statsCurVer = pickCurrentVersion(jobMeta)
      ?? jobBasicInfo?.algorithmVersion?.trim()
      ?? null;
    const infoFields: JobInfoField[] = [
      { label: '任务类型', value: jobBasicInfo?.taskTypeName || '—' },
      { label: '状态', value: jobBasicInfo?.statusName || '—' },
      {
        label: '优先级',
        value: (() => {
          const editable = jobBasicInfo != null
            && PRIORITY_EDITABLE_STATUSES.has(jobBasicInfo.status);
          return (
            <PriorityTag
              priority={jobBasicInfo?.priority}
              label={jobBasicInfo?.priorityName}
              editable={editable}
              onClick={editable ? () => setPriorityModalOpen(true) : undefined}
            />
          );
        })(),
      },
      { label: '创建人', value: jobBasicInfo?.creator || '—' },
      { label: '创建时间', value: fmtTime(jobBasicInfo?.createTime) },
      { label: '结束时间', value: fmtTime(jobBasicInfo?.endTime) },
    ];
    const savedCustomParams = jobBasicInfo?.customParams ?? jobMeta?.customParams;
    if (savedCustomParams && Object.keys(savedCustomParams).length > 0) {
      infoFields.push({ label: '自定义参数', full: true, value: <JobCustomParametersSummary params={savedCustomParams} /> });
    }
    if (statsBaseVer) {
      infoFields.push({ label: '基线版本号', value: <VersionValue value={statsBaseVer} /> });
    }
    if (statsCurVer) {
      infoFields.push({ label: '当前版本号', value: <VersionValue value={statsCurVer} /> });
    }
    infoFields.push({
      label: '算法版本',
      full: true,
      value: renderAlgorithmVersionCell(jobMeta, onTrace, {
        text: jobBasicInfo?.algorithmVersion ?? jobMeta?.algorithmVersion,
      }),
    });
    // 预测数据版本：/job/detail 不带该字段，从并行加载的 jobMeta（/job/list）取；
    // 非 LPL / 存量 job 为空，不渲染。
    if (jobMeta?.predictionDataVersion) {
      infoFields.push({
        label: '预测数据版本',
        full: true,
        value: <VersionValue value={formatPredictionDataVersion(jobMeta.predictionDataVersion)} />,
      });
    }
    return (
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          {/* 统计页一般由 task 列表「不通过数」在新 tab 打开，navigate(-1) 无历史，直接回 Task 列表 */}
          <Button
            type="link"
            onClick={() => navigate(`/job-tasks/${jobId}`)}
            style={{ paddingLeft: 0 }}
          >
            返回 Task 列表
          </Button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1d1d1f' }}>
            不通过统计
          </h2>
          <Tag color="blue">
            [{jobId}] {jobBasicInfo?.jobName || currentJobName || '-'}
          </Tag>
          <Tooltip title={compareEntryHint}>
            <Button onClick={openCompareEntry} style={{ marginInlineStart: 'auto' }}>
              {compareEntryText}
            </Button>
          </Tooltip>
          <Tooltip title="复制当前页面链接（含筛选状态），分享给他人可看到相同视图">
            <Button icon={<LinkOutlined />} onClick={handleCopyPageLink}>
              复制链接
            </Button>
          </Tooltip>
        </div>

        {/* —— 模块一：单个任务基本信息（参考对比页基本信息模块：彩色竖条 + 标题 + Tag + 定义网格） —— */}
        <div style={{
          marginBottom: 16,
          padding: 16,
          background: '#fff',
          border: '1px solid #ebebeb',
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
            任务基本信息
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <span style={{
              display: 'inline-block', width: 6, height: 14, background: '#5e6ad2', borderRadius: 2,
            }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1d1d1f' }}>任务信息</span>
            <Tag color="blue" style={{ marginInlineStart: 4 }}>
              [{jobId}] {jobBasicInfo?.jobName || currentJobName || '-'}
            </Tag>
          </div>
          <JobInfoGrid fields={infoFields} loading={jobBasicInfoLoading && !jobBasicInfo} />
        </div>

        {/* —— 模块二：单个仿真结果（替换对比页的指标对比 + 4 矩阵） —— */}
        <div style={{
          marginBottom: 16,
          padding: 16,
          background: '#fff',
          border: '1px solid #ebebeb',
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
            仿真结果
          </div>
          <JobStatTiles taskCount={taskCount} loading={taskCountLoading} />
          {sceneSetStatsNode}
        </div>

        {/* —— 模块三：评测器明细（替换对比页评测器矩阵；场景数/占比/pass/fail） —— */}
        {renderEvaluatorStatsPanel()}

        {/* —— 模块三-a：评估器参数聚合卡片（iLQR / stop_sign 等） —— */}
        {renderMetricAggPanel()}

        {/* —— 模块三-b：VRU 责任碰撞失败场景的相对速度分档饼图 —— */}
        <VruRelativeSpeedChart jobId={jobId} />

        {/* —— 模块四：Job 内 task 列表（替换对比页场景级明细；带全部筛选，默认筛不通过） —— */}
        <div style={{
          padding: 16,
          background: '#fff',
          border: '1px solid #ebebeb',
          borderRadius: 8,
        }}>
          <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
            任务明细列表
          </div>
          <div ref={taskDetailRef} />
          {taskFilterAndTable}
        </div>

        {taskLogModalEl}
        {commentDrawerNode}
        {/* statsMode 页的 taskFilterAndTable 里也有「基于当前筛选项创建任务」按钮,
            必须同挂 rerunModalsNode,否则点了 setRerunConfirmOpen(true) 但 modal 不在
            本分支 DOM 中,要等 navigate 回 Task 列表(主 return)才补渲染,即用户所见
            「点了不弹、返回才弹」。 */}
        {rerunModalsNode}
        {versionTraceModal}
      </div>
    );
  }

  // 「任务对比」独立页：基准任务固定为当前 jobId，对比任务在页面顶部 Select 中切换。
  // 候选列表已在 handleOpenCompareSelect 中按 task_type 过滤；默认选最新一条 daily Job。
  if (compareMode) {
    // scene_set_ids (CSV) → 「a/b/c」路径名解析（缺失或未匹配显示原 ID），
    // findScenePath 走 @/common/sceneSetTree 共享工具，与 JobList / 页头保持同一份实现。
    // 返回 [{id, label}] 而非纯 string[]；label 用于展示，id 用于超链接跳转到
    // /scene-management?nodeId=<id>。非数字 token(极端脏 data)id=null 只展示不跳。
    const resolveSceneSetLabels = (csv?: string | null): { id: number | null; label: string }[] => {
      if (!csv) return [];
      return csv.split(',').map((s) => s.trim()).filter(Boolean).map((idStr) => {
        const id = Number(idStr);
        if (!Number.isFinite(id)) return { id: null, label: idStr };
        const path = findScenePath(compareSceneTree, id);
        return { id, label: path ? path.join(' / ') : `#${idStr}` };
      });
    };

    const baseMetrics = compareRaw?.today;
    const cmpMetrics = compareRaw?.compare;

    // 4 矩阵 diffMode 中文显示名（与 daily_service._diff_scene_ids 口径对齐）
    // 后端字段已统一「对比·基准」语义（位 1=对比/历史，位 2=基准/今日），前端直连展示：
    //   pass_fail=对比通过·基准未通过=当前新增失败；fail_pass 反之。
    const _MATRIX_MODE_LABEL: Record<string, string> = {
      pass_pass: 'Pass · Pass（两侧都通过）',
      pass_fail: 'Pass · Fail（对比任务通过、当前任务未通过 = 当前新增失败）',
      fail_pass: 'Fail · Pass（对比任务未通过、当前任务通过 = 当前新增通过）',
      fail_fail: 'Fail · Fail（两侧都失败）',
      new_success: '新增成功',
      new_fail: '新增失败',
      all: '全部场景',
    };

    // —— 基本信息模块 ——
    // jobItem 可选：能拿到完整 JobItem 时（当前任务 = jobMeta；对比任务 = compareJobList
    // 里同 id 那条）用 AlgorithmVersionCell 渲染，跟 Job 列表 / Task 列表页头一致；
    // 拿不到就回退成简写 + Tooltip，避免对比页在 JobItem 未加载时空白。
    const renderInfoCol = (
      title: string,
      jobIdVal: number,
      m: typeof baseMetrics,
      color: string,
      jobItem?: JobItem,
      side: 'base' | 'compare' = 'base',
    ) => {
      const labels = resolveSceneSetLabels(m?.sceneSetIds);
      return (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{
              display: 'inline-block', width: 6, height: 14, background: color, borderRadius: 2,
            }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1d1d1f' }}>{title}</span>
            {/*
             * job 名 + jobId 超链接：点击跳到该 job 的任务列表页；
             * 分享出去的对比链接也可直接点标签回到当前任务/对比任务 job（老版仅静态 Tag）。
             * 保留原 Tag 视觉（背景色由 color 派生，跟当前任务/对比任务主色一致）。
             */}
            <Tooltip title="点击查看该 Job 详情">
              <a
                onClick={() => {
                  if (jobIdVal <= 0) return;
                  // 关键: 显式重置对比相关 URL 参数(mode/compareJobId/diffMode 等),
                  // 避免同路由 params 变化时 React Router 不重挂载,老 ?mode=compare
                  // query 保留导致跳过去仍处于对比模式(而非独立任务详情)。
                  // window.location 硬跳最彻底,清 state / 重挂载所有组件。
                  window.location.href = `/job-tasks/${jobIdVal}`;
                }}
                style={{
                  color: 'inherit', textDecoration: 'none',
                  cursor: jobIdVal > 0 ? 'pointer' : 'default',
                }}
              >
                <Tag color={color === '#5e6ad2' ? 'blue' : 'purple'} style={{ marginInlineStart: 4, cursor: 'inherit' }}>
                  [{jobIdVal}] {m?.jobName || '-'}
                </Tag>
              </a>
            </Tooltip>
          </div>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.9 }}>
            {/* 任务类型：jobItem（/job/list 权威）优先，未加载时退回 metrics 透传值。
                对比双方 simType 必然一致（后端 compare 前置校验），taskTypeName 则
                可能不同——放宽到按 sim_type 判定可比性后，同为 PNC 仿真的不同任务类型
                可互相对比，故这里两者都展示，让用户看清具体在比什么。 */}
            <div>
              <span style={{ color: '#6b6f76' }}>任务类型：</span>
              {(() => {
                const st = jobItem?.simType ?? m?.simType;
                const ttName = jobItem?.taskTypeName ?? m?.taskTypeName;
                if (!st && !ttName) return <span style={{ color: '#9b9da3' }}>—</span>;
                return (
                  <>
                    {st ? (
                      <Tag color={simTypeColor(st)} style={{ marginInlineStart: 0 }}>
                        {simTypeLabel(st)}
                      </Tag>
                    ) : null}
                    {ttName ? <span>{ttName}</span> : null}
                  </>
                );
              })()}
            </div>
            <div>
              <span style={{ color: '#6b6f76' }}>场景集合：</span>
              {labels.length > 0
                ? labels.map((item, i) => {
                    const tag = (
                      <Tag
                        key={i}
                        style={{
                          marginInlineStart: 0, marginBottom: 4,
                          cursor: item.id ? 'pointer' : 'default',
                        }}
                      >
                        {item.label}
                      </Tag>
                    );
                    if (!item.id) return tag;
                    // 场景集合项 → 跳「场景管理」自动选中该节点(URL query nodeId 由 SceneManagement 消费)
                    return (
                      <Tooltip key={i} title="点击查看该场景集">
                        <a
                          onClick={() => window.location.href = `/scene-management?nodeId=${item.id}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          {tag}
                        </a>
                      </Tooltip>
                    );
                  })
                : <span style={{ color: '#9b9da3' }}>—</span>}
            </div>
            <div>
              <span style={{ color: '#6b6f76' }}>算法版本：</span>
              {jobItem ? (
                renderAlgorithmVersionCell(jobItem, onTrace, {
                  text: jobItem.algorithmVersion ?? m?.algorithmVersion,
                })
              ) : (
                <Tooltip title={m?.algorithmVersion || '—'} placement="topLeft">
                  <span style={{ fontFamily: "'JetBrains Mono', Consolas, monospace", fontSize: 12 }}>
                    {formatAlgorithmVersion(m?.algorithmVersion)}
                  </span>
                </Tooltip>
              )}
            </div>
            {/* 预测数据版本：LPL/PNC 仿真 job 建任务时冻结的实际生效值，对比双方各
                自展示；非 LPL / 存量 job（JobItem 未带该字段）不渲染此行。 */}
            {jobItem?.predictionDataVersion ? (
              <div>
                <span style={{ color: '#6b6f76' }}>预测数据版本：</span>
                <VersionValue value={formatPredictionDataVersion(jobItem.predictionDataVersion)} />
              </div>
            ) : null}
            {/* 基线版本号（仅 bazel 编译 job）与当前版本号：来自 JobItem 精确字段，
                跟 Job 详情 / 页头 grid 口径一致。JobItem 未加载或无值时不渲染。 */}
            {(() => {
              const baseVer = pickBaseVersion(jobItem);
              return baseVer ? (
                <div>
                  <span style={{ color: '#6b6f76' }}>基线版本号：</span>
                  <VersionValue value={baseVer} />
                </div>
              ) : null;
            })()}
            {(() => {
              const curVer = pickCurrentVersion(jobItem) ?? m?.algorithmVersion?.trim() ?? null;
              return curVer ? (
                <div>
                  <span style={{ color: '#6b6f76' }}>当前版本号：</span>
                  <VersionValue value={curVer} />
                </div>
              ) : null;
            })()}
            <div>
              <span style={{ color: '#6b6f76' }}>创建人：</span>
              {/* jobItem.creator 优先（/job/list 权威）；m.creator (DailyJobMetrics)
                  在部分 daily 数据里回填异常，作为兜底 */}
              {jobItem?.creator || m?.creator || '—'}
              <span style={{ color: '#d1d5db', margin: '0 8px' }}>|</span>
              <span style={{ color: '#6b6f76' }}>创建时间：</span>
              {(() => {
                const t = jobItem?.createTime ?? m?.createTime;
                return t ? new Date(t).toLocaleString('zh-CN') : '—';
              })()}
            </div>
            {/* 「忽略 check 不通过的场景」开关：双方各一个。checked 绑 m.excludeCheckFailed
                （后端 compare_two_jobs 透传）；切换走 handleToggleCompareExcludeCheck，写后端
                后复刻 handleCompareConfirm 的矩阵 refetch + 任务表重载。文案/口径与单 Job 页
                筛选区开关一致。 */}
            <div style={{ marginTop: 4 }}>
              <span style={{ color: '#6b6f76' }}>忽略 check 不通过的场景：</span>
              <Tooltip
                title={
                  m?.excludedCheckFailedCount
                    ? `当前已剔除 ${m.excludedCheckFailedCount} 个场景；总览/通过率/对比均按剔除后口径计算`
                    : '打开后该 Job 内 check 不通过（type=3 evaluator pass=false）的场景将整条剔除，不计入分子分母'
                }
              >
                <Switch
                  checked={Boolean(m?.excludeCheckFailed)}
                  loading={side === 'base' ? excludeCheckTogglingBase : excludeCheckTogglingCompare}
                  onChange={(v) => handleToggleCompareExcludeCheck(side, v)}
                  disabled={!jobIdVal}
                />
              </Tooltip>
              {m?.excludedCheckFailedCount ? (
                <span style={{ color: '#9b9da3', marginLeft: 8 }}>
                  已剔除 {m.excludedCheckFailedCount} 个
                </span>
              ) : null}
            </div>
          </div>
        </div>
      );
    };

    const compareBasicInfoSection = (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
          对比双方基本信息
        </div>
        <Spin spinning={compareSummaryLoading && !compareRaw}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {/* 统一「当前任务 · 对比任务」顺序：当前任务在前、对比任务在后。
                jobItem 从 compareJobList / jobMeta 拿完整 JobItem，供算法版本走
                AlgorithmVersionCell 渲染，跟 Job 列表 / Task 列表页头对齐。 */}
            {renderInfoCol('当前任务', jobId, baseMetrics, '#5e6ad2', jobMeta ?? undefined, 'base')}
            <div style={{ width: 1, background: '#ebebeb' }} />
            {renderInfoCol(
              '对比任务',
              selectedCompareJobId ?? 0,
              cmpMetrics,
              '#8b5cf6',
              // compareJobMeta（jobIds 多值查的权威源，字段最全）优先；未加载时
              // 退回候选列表 find，仍尽量给出算法版本/分支等信息。
              compareJobMeta
                ?? compareJobList.find((j) => j.jobId === selectedCompareJobId)
                ?? undefined,
              'compare',
            )}
          </div>
        </Spin>
      </div>
    );

    // —— 对比仿真结果模块（指标对比表 + 4 矩阵卡片）——
    const fmtDiff = (
      base: number | undefined,
      cmp: number | undefined,
      suffix = '',
      digits = 2,
    ): { text: string; color: string } => {
      if (base == null || cmp == null) return { text: '—', color: '#9b9da3' };
      const d = base - cmp;
      if (d === 0) return { text: `0${suffix}`, color: '#9b9da3' };
      const sign = d > 0 ? '+' : '';
      // 通过率/异常率/里程「越大越好」用绿色；负值红色。这里通用化：上涨绿、下跌红。
      return {
        text: `${sign}${d.toFixed(digits)}${suffix}`,
        color: d > 0 ? '#16a34a' : '#dc2626',
      };
    };
    type MetricRow = {
      key: string;
      label: string;
      base: string;
      cmp: string;
      diff: { text: string; color: string };
    };
    const metricRows: MetricRow[] = baseMetrics && cmpMetrics ? [
      {
        key: 'total', label: '总场景数',
        base: String(baseMetrics.total ?? 0), cmp: String(cmpMetrics.total ?? 0),
        diff: fmtDiff(baseMetrics.total, cmpMetrics.total, '', 0),
      },
      {
        // 后端透传数据库 raw 值（米），前端除以 1000 转 km 展示
        key: 'mileage', label: '运行里程',
        base: `${((baseMetrics.mileage ?? 0) / 1000).toLocaleString('zh-CN', { maximumFractionDigits: 2 })} km`,
        cmp: `${((cmpMetrics.mileage ?? 0) / 1000).toLocaleString('zh-CN', { maximumFractionDigits: 2 })} km`,
        diff: fmtDiff(
          baseMetrics.mileage != null ? baseMetrics.mileage / 1000 : undefined,
          cmpMetrics.mileage != null ? cmpMetrics.mileage / 1000 : undefined,
          ' km',
        ),
      },
      {
        key: 'passRate', label: '通过率',
        base: `${baseMetrics.passRate.toFixed(2)}%`, cmp: `${cmpMetrics.passRate.toFixed(2)}%`,
        diff: fmtDiff(baseMetrics.passRate, cmpMetrics.passRate, '%'),
      },
      {
        key: 'anomalyRate', label: '异常率',
        base: `${baseMetrics.anomalyRate.toFixed(2)}‰`, cmp: `${cmpMetrics.anomalyRate.toFixed(2)}‰`,
        // 异常率反向：上涨为红色（更差），下跌为绿色（更好）
        diff: (() => {
          const d = fmtDiff(baseMetrics.anomalyRate, cmpMetrics.anomalyRate, '‰');
          return { ...d, color: d.color === '#16a34a' ? '#dc2626' : d.color === '#dc2626' ? '#16a34a' : d.color };
        })(),
      },
    ] : [];

    type MatrixCard = {
      key: string;
      mode: TaskDiffMode;
      label: string;
      desc: string;
      value: number;
      color: string;
      bg: string;
    };
    // 列头「对比任务 · 当前任务」顺序（前=对比任务，后=当前任务）：Pass·Fail = 对比任务通过→当前任务未通过
    // = 当前新增失败。后端字段已是「对比·基准」语义，故 mode/value 与展示一一直连。
    const matrixCards: MatrixCard[] = compareRaw ? [
      {
        key: 'pp', mode: 'pass_pass', label: 'Pass · Pass', desc: '两侧都通过',
        value: compareRaw.passPass, color: '#16a34a', bg: '#f0fdf4',
      },
      {
        key: 'pf', mode: 'pass_fail', label: 'Pass · Fail', desc: '对比任务通过、当前任务未通过（当前新增失败）',
        value: compareRaw.passFail, color: '#d97706', bg: '#fffbeb',
      },
      {
        key: 'fp', mode: 'fail_pass', label: 'Fail · Pass', desc: '对比任务未通过、当前任务通过（当前新增通过）',
        value: compareRaw.failPass, color: '#0284c7', bg: '#f0f9ff',
      },
      {
        key: 'ff', mode: 'fail_fail', label: 'Fail · Fail', desc: '两侧都失败',
        value: compareRaw.failFail, color: '#dc2626', bg: '#fef2f2',
      },
    ] : [];

    const compareMetricsSection = (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
          对比仿真结果
        </div>
        <Spin spinning={compareSummaryLoading}>
          {compareRaw ? (
            <>
              {/* 指标对比表 */}
              <table style={{
                width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16,
              }}>
                <thead>
                  <tr style={{ background: '#fafafa', color: '#6b6f76', fontWeight: 500 }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', width: 140, borderBottom: '1px solid #ebebeb' }}>指标</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ebebeb' }}>
                      <span style={{ color: '#5e6ad2', fontWeight: 600 }}>当前任务</span>
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: '1px solid #ebebeb' }}>
                      <span style={{ color: '#8b5cf6', fontWeight: 600 }}>对比任务</span>
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', width: 140, borderBottom: '1px solid #ebebeb' }}>差值（当前任务 - 对比任务）</th>
                  </tr>
                </thead>
                <tbody>
                  {metricRows.map((r) => (
                    <tr key={r.key}>
                      <td style={{ padding: '10px 12px', color: '#374151', borderBottom: '1px solid #f5f5f5' }}>{r.label}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #f5f5f5' }}>{r.base}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid #f5f5f5' }}>{r.cmp}</td>
                      <td style={{
                        padding: '10px 12px', textAlign: 'right', fontWeight: 600,
                        color: r.diff.color, borderBottom: '1px solid #f5f5f5',
                      }}>{r.diff.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* 4 矩阵卡片 */}
              <div style={{ fontSize: 12, color: '#6b6f76', marginBottom: 8 }}>
                场景级 2×2 矩阵（两侧共有
                <span style={{ color: '#1d1d1f', fontWeight: 600 }}> {compareRaw.intersectionSceneCount} </span>
                个场景）
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}>
                {matrixCards.map((c) => {
                  // 顶部整体矩阵的「已筛选」高亮：仅当处于场景级筛选（无评测器联动）时点亮，
                  // 避免与评测器矩阵共用 compareDiffMode 时被错误点亮。
                  const selected = compareDiffMode === c.mode && !compareFilterEvaluatorName && compareFilterSceneSetId === undefined;
                  const clickable = c.value > 0;
                  // 再次点击已选中的卡片 → 取消筛选；否则切到该矩阵筛选
                  const onClick = clickable
                    ? () => { void applyCompareDiffMode(selected ? undefined : c.mode); }
                    : undefined;
                  return (
                    <div
                      key={c.key}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : -1}
                      aria-pressed={selected}
                      onClick={onClick}
                      onKeyDown={(e) => {
                        if (!onClick) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onClick();
                        }
                      }}
                      style={{
                        position: 'relative',
                        padding: '14px 16px',
                        background: c.bg,
                        border: `${selected ? 2 : 1}px solid ${selected ? c.color : `${c.color}33`}`,
                        boxShadow: selected ? `0 0 0 3px ${c.color}1a` : 'none',
                        borderRadius: 8,
                        cursor: clickable ? 'pointer' : 'not-allowed',
                        opacity: clickable ? 1 : 0.55,
                        userSelect: 'none',
                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                      }}
                      title={
                        clickable
                          ? (selected ? '再次点击取消筛选' : `点击筛选此类场景（${c.value} 条）`)
                          : '无数据，不可筛选'
                      }
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontSize: 12, color: '#6b6f76' }}>{c.label}</span>
                        {selected && (
                          <span style={{
                            fontSize: 10, fontWeight: 600,
                            color: c.color, background: '#fff',
                            padding: '1px 6px', borderRadius: 4,
                            border: `1px solid ${c.color}55`,
                          }}>
                            已筛选
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>{c.value}</div>
                      <div style={{ fontSize: 11, color: '#9b9da3', marginTop: 4 }}>{c.desc}</div>
                    </div>
                  );
                })}
              </div>
              {/* 场景级 vs 评测器级计数关系说明：解释为何两套矩阵数通常不相等，
                  避免用户把「评测器 Σ < 场景级」误判为数据算错。 */}
              <div style={{ marginTop: 8, fontSize: 12, color: '#9b9da3', lineHeight: 1.7 }}>
                说明：场景级计数按<b>场景最终状态</b>（task succeeded/not_pass/异常）判定；
                下方「按评测器聚合」矩阵按<b>单个评测器</b>是否通过判定。一个场景含多个评测器，
                故评测器级计数通常<b>应 ≥ 场景级</b>；若出现评测器级<b>少于</b>场景级，是两侧评测器
                改名/拆分/缺失导致未计入交叉（详见下方评测器矩阵的覆盖率横幅与对账脚注）。
              </div>
              {compareDiffMode && _MATRIX_MODE_LABEL[compareDiffMode] && !compareFilterEvaluatorName && (
                <div style={{
                  marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, color: '#6b6f76',
                }}>
                  <span>当前筛选：</span>
                  <Tag color="processing" style={{ margin: 0 }}>
                    {_MATRIX_MODE_LABEL[compareDiffMode]}
                  </Tag>
                  <Button size="small" type="link" onClick={() => { void applyCompareDiffMode(undefined); }}>
                    清除筛选
                  </Button>
                </div>
              )}
              {selectedCompareJobId && (
                <SceneSetComparison
                  key={String(jobId) + '-' + String(selectedCompareJobId)}
                  {...sceneSetComparison}
                  onSelect={(nodeId, mode) => { void applySceneSetMatrixCell(nodeId, mode); }}
                  selectedNodeId={compareFilterSceneSetId}
                  selectedMode={compareFilterEvaluatorName ? undefined : compareDiffMode}
                />
              )}
            </>
          ) : (
            <Alert type="info" showIcon message="暂无对比仿真结果数据" />
          )}
        </Spin>
      </div>
    );

    // —— 按评测器聚合的 2×2 矩阵 ——
    // 列：评测器 | 场景数(占总场景比例) | PP | PF | FP | FF；
    // 四象限按横向 100%（占该评测器 sceneCount）展示，配色与上方整体矩阵卡片一致。
    const evalMatrix = compareEvaluatorMatrix;
    const evalTotalScenes = evalMatrix?.intersectionSceneCount ?? 0;
    // 覆盖率 + 对账：解释「评测器矩阵数 < 场景级矩阵数」的差额来源（评测器改名/拆分/新增）。
    const evalCoverage = evalMatrix?.coverage ?? null;
    const evalMatched = evalMatrix?.matchedSceneCounts ?? null;
    // 横幅仅当两侧评测器集有差异（某侧有独有项）时渲染，避免全同名场景下噪声。
    const hasCoverageGap =
      !!evalCoverage &&
      (evalCoverage.baseOnly.length > 0 || evalCoverage.compareOnly.length > 0);
    // 脚注：场景级（compareRaw）= 评测器可匹配（去重场景数）+ 无可比；仅对失败象限、
    // 且存在差额时展示（passPass 两侧同名为主、差额通常为 0，不出脚注避免噪声）。
    const scenarioPassFail = compareRaw?.passFail ?? 0;
    const matchedPassFail = evalMatched?.passFail ?? 0;
    const unmatchedPassFail = Math.max(0, scenarioPassFail - matchedPassFail);
    const showPassFailFootnote = scenarioPassFail > matchedPassFail;
    const scenarioFailFail = compareRaw?.failFail ?? 0;
    const matchedFailFail = evalMatched?.failFail ?? 0;
    const unmatchedFailFail = Math.max(0, scenarioFailFail - matchedFailFail);
    const showFailFailFootnote = scenarioFailFail > matchedFailFail;
    // 列头统一「对比任务 · 当前任务」顺序（前=对比任务，后=当前任务），读作时间推进
    // 「对比任务 → 当前任务」：如 Pass·Fail = 对比任务通过→当前任务未通过 = 当前新增失败。
    // 后端字段已统一「对比·基准」语义（位 1=对比/历史，位 2=基准/今日），mode/get 直连展示。
    // trend：相对「对比任务」当前的变化方向 —— Pass·Fail=当前新增失败 ↓；Fail·Pass=当前新增通过 ↑。
    const quadrantCols: { key: string; mode: TaskDiffMode; label: string; color: string; bg: string;
      trend?: 'up' | 'down'; tip: string;
      get: (r: DailyCompareEvaluatorMatrix['evaluators'][number]) => number }[] = [
      { key: 'pp', mode: 'pass_pass', label: 'Pass · Pass', color: '#16a34a', bg: '#f0fdf4',
        tip: '对比任务通过 · 当前任务通过（两边都通过）', get: (r) => r.passPass },
      { key: 'pf', mode: 'pass_fail', label: 'Pass · Fail', color: '#d97706', bg: '#fffbeb', trend: 'down',
        tip: '对比任务通过 · 当前任务未通过；通过→未通过 = 当前新增失败 ↓', get: (r) => r.passFail },
      { key: 'fp', mode: 'fail_pass', label: 'Fail · Pass', color: '#0284c7', bg: '#f0f9ff', trend: 'up',
        tip: '对比任务未通过 · 当前任务通过；未通过→通过 = 当前新增通过 ↑', get: (r) => r.failPass },
      { key: 'ff', mode: 'fail_fail', label: 'Fail · Fail', color: '#dc2626', bg: '#fef2f2',
        tip: '对比任务未通过 · 当前任务未通过（两边都未通过）', get: (r) => r.failFail },
    ];
    const fmtPct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '-');
    // 当前新增通过=绿↑，当前新增失败=红↓（仅 PF/FP 两个「变化」象限展示，且计数>0 时才有意义）
    const TREND_COLOR = { up: '#16a34a', down: '#dc2626' } as const;

    // 默认仅展示前 10 行评测器，超出折叠；选中筛选的评测器若在折叠区则自动放开，避免选中行被藏起
    const EVAL_COLLAPSE_LIMIT = 10;
    // 仅展示评测器（_evaluator 结尾）；过滤 meta_info_computer 等非评测器统计项，
    // 以及 _check 检查项（type=3 独立成类，不进对比页评测器矩阵）。已登记评估器参数聚合
    // 声明的评估器（纯统计型 / 通过型均可）也从矩阵拆出，改在下方「评估器参数对比」卡片里看
    // 其参数聚合 KPI 差值。
    const aggNames = new Set(
      metricAggSpecs.map((s) => canonicalEvaluatorName(s.evaluator)),
    );
    const allEvaluators = (evalMatrix?.evaluators ?? []).filter(
      (r) =>
        isEvaluatorName(r.evaluatorName) &&
        !aggNames.has(canonicalEvaluatorName(r.evaluatorName)),
    );
    const evalCollapsible = allEvaluators.length > EVAL_COLLAPSE_LIMIT;
    const selectedBeyondLimit =
      !!compareFilterEvaluatorName &&
      allEvaluators.findIndex((r) => r.evaluatorName === compareFilterEvaluatorName) >= EVAL_COLLAPSE_LIMIT;
    const evalMatrixShowAll = evalMatrixExpanded || selectedBeyondLimit || !evalCollapsible;
    const visibleEvaluators = evalMatrixShowAll
      ? allEvaluators
      : allEvaluators.slice(0, EVAL_COLLAPSE_LIMIT);

    // 指标说明 tips：贴合后端 daily_service 的口径，帮使用者理解数字/百分比含义，
    // 尤其解释「为何各评测器场景数常小于总交集场景数」（异常/未回传 → 不可判定）。
    const evalMatrixTips = (
      <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 360 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>指标口径说明</div>
        <div><b>总交集场景数</b>：两个 Job 共有的 scene_id 总数，作为「占总」的分母。</div>
        <div><b>场景数（占总）</b>：该评测器在共有场景中<b>可判定</b>的场景数；百分比 = 占总交集场景的比例。</div>
        <div style={{ marginTop: 4 }}>
          <b>四象限</b>（对比任务 · 当前任务，前=对比任务，后=当前任务）：
          <div>· Pass·Pass：两边都通过</div>
          <div>· Pass·Fail：对比任务通过、当前任务未通过 = 当前新增失败（<span style={{ color: '#dc2626' }}>↓ 退化</span>）</div>
          <div>· Fail·Pass：对比任务未通过、当前任务通过 = 当前新增通过（<span style={{ color: '#16a34a' }}>↑ 改善</span>）</div>
          <div>· Fail·Fail：两边都未通过</div>
          <div>象限内百分比 = 占该评测器场景数（同行四象限横向相加 = 100%）。</div>
          <div>箭头表示「对比任务 → 当前任务」的变化：<span style={{ color: '#16a34a' }}>↑</span> 当前新增通过 / <span style={{ color: '#dc2626' }}>↓</span> 当前新增失败。</div>
        </div>
        <div style={{ marginTop: 4 }}>
          <b>通过/未通过</b>：仅统计 type=1 评测器；算法侧只上报「不通过」，通过项由场景评测器
          全集（evaluator_list）差分推导。
        </div>
        <div style={{ marginTop: 4, color: '#facc15' }}>
          注：异常终态（执行异常 / 系统异常 / coredump）按<b>未通过</b>计入（双侧都异常且
          无评测明细时借 Job 评测器全集兜底为 Fail·Fail）；仅当某侧<b>非异常却未回传评测明细</b>
          时该评测器在此场景<b>不可判定</b>、不计入——故各评测器场景数可能略小于总交集场景数。
        </div>
      </div>
    );

    const compareEvaluatorMatrixSection = (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500 }}>
            按评测器聚合的 2×2 矩阵
            <Tooltip title={evalMatrixTips} placement="topLeft" styles={{ root: { maxWidth: 400 } }}>
              <QuestionCircleOutlined style={{ marginLeft: 6, color: '#c2c4c9', cursor: 'help' }} />
            </Tooltip>
            <span style={{ fontSize: 11, color: '#c2c4c9', marginLeft: 8 }}>
              四象限按横向 100% 计（占该评测器场景数）
            </span>
          </span>
          {compareFilterEvaluatorName && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6b6f76' }}>
              <span>明细已联动筛选：</span>
              <Tag color="processing" style={{ margin: 0 }}>
                评测器 {getEvaluatorDisplay(compareFilterEvaluatorName)}
                {compareDiffMode && _MATRIX_MODE_LABEL[compareDiffMode]
                  ? ` · ${_MATRIX_MODE_LABEL[compareDiffMode]}`
                  : ''}
              </Tag>
              <Button
                size="small" type="link"
                onClick={() => {
                  setCompareFilterEvaluatorName(undefined);
                  void applyCompareDiffMode(undefined);
                }}
              >
                清除
              </Button>
            </span>
          )}
        </div>
        {/* 覆盖率横幅：两侧评测器集不一致时提示"未计入交叉"的改名/缺失项及其本侧失败数，
            解释「评测器 Σ Pass·Fail < 场景级 Pass·Fail」的差额来源，避免误判数据算错。 */}
        {hasCoverageGap && evalCoverage ? (() => {
          const baseFails = evalCoverage.baseOnly.filter((e) => e.failCount > 0);
          const cmpFails = evalCoverage.compareOnly.filter((e) => e.failCount > 0);
          return (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.7 }}
              message={
                <div>
                  <div>
                    仅统计两侧<b>同名可匹配</b>的评测器。本对比：当前任务{' '}
                    {evalCoverage.baseEvaluatorCount} 个｜对比任务{' '}
                    {evalCoverage.compareEvaluatorCount} 个｜同名{' '}
                    {evalCoverage.commonEvaluatorCount} 个。
                  </div>
                  {(baseFails.length > 0 || cmpFails.length > 0) && (
                    <div style={{ marginTop: 4 }}>
                      未计入交叉（当前任务侧失败但对比任务侧改名/缺失/新增）：
                      {baseFails.map((e) => (
                        <Tag key={`b-${e.evaluatorName}`} color="orange" style={{ margin: 2 }}>
                          {getEvaluatorDisplay(e.evaluatorName)}（当前任务 fail {e.failCount}）
                        </Tag>
                      ))}
                      {cmpFails.map((e) => (
                        <Tag key={`c-${e.evaluatorName}`} color="purple" style={{ margin: 2 }}>
                          {getEvaluatorDisplay(e.evaluatorName)}（对比任务 fail {e.failCount}）
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
          );
        })() : null}
        <Spin spinning={compareEvaluatorMatrixLoading}>
          {evalMatrix && allEvaluators.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>
                <thead>
                  <tr style={{ background: '#fafafa', color: '#6b6f76', fontWeight: 500 }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #ebebeb', position: 'sticky', left: 0, background: '#fafafa' }}>
                      评测器
                      <Tooltip title="评测器名（已统一归一为 snake_case，CamelCase 与 snake_case 视为同一评测器）">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#c2c4c9', cursor: 'help' }} />
                      </Tooltip>
                    </th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', width: 168, minWidth: 168, whiteSpace: 'nowrap', borderBottom: '1px solid #ebebeb' }}>
                      场景数
                      <span style={{ fontWeight: 400, color: '#c2c4c9' }}>（占总）</span>
                      <Tooltip title="该评测器在两 Job 共有场景中「可判定」的场景数（= 四象限之和）；百分比 = 占总交集场景数的比例">
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#c2c4c9', cursor: 'help' }} />
                      </Tooltip>
                    </th>
                    {quadrantCols.map((q) => (
                      <th key={q.key} style={{
                        padding: '8px 12px', textAlign: 'right', width: 120,
                        borderBottom: `2px solid ${q.color}`, color: q.color, fontWeight: 600,
                      }}>
                        {q.label}
                        <Tooltip title={q.tip}>
                          <QuestionCircleOutlined style={{ marginLeft: 4, color: `${q.color}99`, cursor: 'help', fontWeight: 400 }} />
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleEvaluators.map((r) => (
                    <tr key={r.evaluatorName}>
                      <td style={{
                        padding: '10px 12px', color: '#1d1d1f', fontWeight: 500,
                        borderBottom: '1px solid #f5f5f5', position: 'sticky', left: 0, background: '#fff',
                        maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }} title={getEvaluatorDisplay(r.evaluatorName)}>
                        {getEvaluatorDisplay(r.evaluatorName)}
                      </td>
                      {(() => {
                        // 「场景数」列可点选：点击联动下方明细 = 该评测器全部可判定场景
                        // （evaluator_diff_scene_ids 的 all 模式 = 4 象限并集）。再次点取消。
                        const selected = compareFilterEvaluatorName === r.evaluatorName && compareDiffMode === 'all';
                        const clickable = r.sceneCount > 0;
                        return (
                          <td
                            role={clickable ? 'button' : undefined}
                            tabIndex={clickable ? 0 : -1}
                            aria-pressed={selected}
                            onClick={clickable ? () => { void applyEvaluatorMatrixCell(r.evaluatorName, 'all'); } : undefined}
                            onKeyDown={(e) => {
                              if (!clickable) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void applyEvaluatorMatrixCell(r.evaluatorName, 'all');
                              }
                            }}
                            title={clickable
                              ? (selected ? '再次点击取消联动筛选' : `点击：筛出「${getEvaluatorDisplay(r.evaluatorName)}」跑过的全部场景`)
                              : undefined}
                            style={{
                              padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #f5f5f5',
                              background: selected ? '#eef0ff' : 'transparent',
                              boxShadow: selected ? 'inset 0 0 0 1px #5e6ad266' : 'none',
                              cursor: clickable ? 'pointer' : 'default', userSelect: 'none',
                            }}
                          >
                            <span style={{ fontWeight: 600, color: selected ? '#5e6ad2' : '#374151' }}>{r.sceneCount}</span>
                            <span style={{ fontSize: 11, color: '#9b9da3', marginLeft: 4 }}>
                              {fmtPct(r.sceneCount, evalTotalScenes)}
                            </span>
                          </td>
                        );
                      })()}
                      {quadrantCols.map((q) => {
                        const val = q.get(r);
                        const selected = compareFilterEvaluatorName === r.evaluatorName && compareDiffMode === q.mode;
                        const clickable = val > 0;
                        return (
                          <td
                            key={q.key}
                            role={clickable ? 'button' : undefined}
                            tabIndex={clickable ? 0 : -1}
                            aria-pressed={selected}
                            onClick={clickable ? () => { void applyEvaluatorMatrixCell(r.evaluatorName, q.mode); } : undefined}
                            onKeyDown={(e) => {
                              if (!clickable) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void applyEvaluatorMatrixCell(r.evaluatorName, q.mode);
                              }
                            }}
                            title={clickable
                              ? (selected ? '再次点击取消联动筛选' : `点击：按「${getEvaluatorDisplay(r.evaluatorName)} · ${q.label}」筛选下方明细`)
                              : undefined}
                            style={{
                              padding: '8px 12px', textAlign: 'right',
                              borderBottom: '1px solid #f5f5f5',
                              background: selected ? q.bg : 'transparent',
                              boxShadow: selected ? `inset 0 0 0 1px ${q.color}66` : 'none',
                              cursor: clickable ? 'pointer' : 'default',
                              userSelect: 'none',
                            }}
                          >
                            <span style={{ fontWeight: 600, color: val > 0 ? q.color : '#c2c4c9' }}>{val}</span>
                            {q.trend && val > 0 ? (
                              // 变化象限（PF/FP）：百分比 + 趋势箭头收进一枚同色淡底胶囊，
                              //   绿=当前新增通过(改善)、红=当前新增失败(退化)，对齐、不再松散漂浮
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 2,
                                marginLeft: 6, padding: '0 6px', borderRadius: 999,
                                fontSize: 11, fontWeight: 600, lineHeight: '16px',
                                color: TREND_COLOR[q.trend],
                                background: `${TREND_COLOR[q.trend]}14`,
                              }}>
                                {q.trend === 'up'
                                  ? <ArrowUpOutlined style={{ fontSize: 9 }} />
                                  : <ArrowDownOutlined style={{ fontSize: 9 }} />}
                                {fmtPct(val, r.sceneCount)}
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: '#9b9da3', marginLeft: 4 }}>
                                {fmtPct(val, r.sceneCount)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert type="info" showIcon message="暂无可聚合的评测器数据（两侧需在共有场景上给出可判定结果）" />
          )}
          {evalMatrix && evalCollapsible && (
            <div style={{
              display: 'flex', justifyContent: 'center',
              marginTop: 4, paddingTop: 10, borderTop: '1px solid #f5f5f5',
            }}>
              {selectedBeyondLimit && !evalMatrixExpanded ? (
                <span style={{ fontSize: 12, color: '#c2c4c9' }}>
                  已展开全部 {allEvaluators.length} 条（含选中评测器）
                </span>
              ) : (
                <Button
                  type="text"
                  size="small"
                  onClick={() => setEvalMatrixExpanded((v) => !v)}
                  style={{ color: '#5e6ad2', fontSize: 12, fontWeight: 500 }}
                >
                  {evalMatrixExpanded
                    ? <>收起 <UpOutlined style={{ fontSize: 10 }} /></>
                    : <>展开全部 {allEvaluators.length} 条 <DownOutlined style={{ fontSize: 10 }} /></>}
                </Button>
              )}
            </div>
          )}
        </Spin>
        {/* 对账脚注：把场景级矩阵数拆成「评测器可匹配 + 无可比」，让两套矩阵的差额可解释。
            仅当失败象限存在差额（场景级 > 评测器可匹配）时渲染；passPass 两侧同名为主、
            差额通常为 0，故只对 passFail / failFail 出脚注。 */}
        {(showPassFailFootnote || showFailFailFootnote) ? (
          <div style={{
            marginTop: 10, paddingTop: 8, borderTop: '1px solid #f5f5f5',
            fontSize: 12, color: '#6b6f76', lineHeight: 1.9,
          }}>
            <div style={{ color: '#9b9da3', marginBottom: 2 }}>
              对账（场景级 = 评测器可匹配 + 无可比）
            </div>
            {showPassFailFootnote ? (
              <div>
                Pass · Fail：场景级 {scenarioPassFail} = 可匹配 {matchedPassFail} + 无可比 {unmatchedPassFail}
                （无可比 = 当前任务侧失败但评测器在对比任务侧改名/缺失，未计入交叉）
              </div>
            ) : null}
            {showFailFailFootnote ? (
              <div>
                Fail · Fail：场景级 {scenarioFailFail} = 可匹配 {matchedFailFail} + 无可比 {unmatchedFailFail}
                （同上：两侧失败但评测器在另一侧改名/缺失）
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );

    // —— 评估器参数对比卡片 ——
    // iLQR / stop_sign 等纯统计型评估器无 pass/fail，只比聚合 KPI（∑计数 + 派生率）。
    // 逐 Job 各自跨场景 ∑ 后再算率与差值（先各自 ∑ 再相除，两侧口径一致）。
    // charts / summary / baseSummary 三份派生数据必须 useMemo 缓存：内联 `?? []` / `.map()`
    // 每轮 render 都是新引用，ChartPanel 的 useMemo([chart, summary, baseSummary]) 全 miss，
    // ReactECharts 走 notMerge 全量重 init，图表每帧重建。
    const compareMetricAggCharts = useMemo(() => compareMetricAgg?.charts ?? [], [compareMetricAgg]);
    const compareMetricAggSummary = useMemo(
      () => (compareMetricAgg?.summary ?? []).map((m) => ({ key: m.key, label: m.label, value: m.value })),
      [compareMetricAgg],
    );
    const compareMetricAggBaseSummary = useMemo(
      () => (compareMetricAgg?.summary ?? []).map((m) => ({ key: m.key, label: m.label, value: m.baseValue })),
      [compareMetricAgg],
    );
    const compareMetricAggSection = metricAggSpecs.length === 0 ? null : (
      <div style={{
        marginBottom: 16,
        padding: 16,
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 8,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 12, gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500 }}>
            评估器参数对比
            <Tooltip
              title={
                <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 340 }}>
                  <div>纯统计型评估器无 pass/fail，只比聚合 KPI（∑计数 + 派生率）。</div>
                  <div>· 派生率：各 Job 计数先跨场景求和、比率由求和后计数重算（Σ分子/Σ分母），不是各场景比率的平均。</div>
                  <div>· 「当前 · 对比」= 当前任务 · 对比任务；Δ = 当前 − 对比，任一侧缺样本渲染「—」。</div>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ marginLeft: 6, color: '#c2c4c9' }} />
            </Tooltip>
          </span>
          <Select
            style={{ minWidth: 200 }}
            value={compareMetricEvaluator ?? undefined}
            onChange={(v) => setCompareMetricEvaluator(String(v))}
            options={metricAggSpecs.map((s) => ({ label: s.label, value: s.evaluator }))}
            placeholder="选择评估器参数"
          />
        </div>
        <Spin spinning={compareMetricAggLoading}>
          {(compareMetricAgg?.summary?.length ?? 0) === 0 && !compareMetricAggLoading ? (
            <Alert type="info" showIcon message="暂无可对比的聚合指标" />
          ) : (
            <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: '#9b9da3', textAlign: 'right' }}>
                    <th style={{ textAlign: 'left', padding: '6px 12px' }}>指标</th>
                    <th style={{ padding: '6px 12px' }}>当前任务</th>
                    <th style={{ padding: '6px 12px' }}>对比任务</th>
                    <th style={{ padding: '6px 12px' }}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {(compareMetricAgg?.summary ?? []).filter((m) => !m.hidden).map((m) => {
                    const isRate = m.unit !== 'count';
                    const fmt = (v: number | null) => {
                      if (v === null) return EMPTY;
                      if (!isRate) return fmtCount(v);
                      return m.unit === 'ratio'
                        ? fmtRatio(v)
                        : v.toLocaleString('zh-CN', { maximumFractionDigits: 3 });
                    };
                    // Δ 上色：无方向/持平中性；有方向时结合 higher_is_worse 判好坏
                    // （退化红 / 改善绿）。higher_is_worse 未知 → 中性。
                    let deltaCol: string | undefined;
                    if (m.delta != null && m.delta !== 0 && m.higherIsWorse != null) {
                      const worse = m.higherIsWorse ? m.delta > 0 : m.delta < 0;
                      deltaCol = worse ? '#e03131' : '#2f9e44';
                    }
                    const deltaText = m.delta == null
                      ? EMPTY
                      : `${m.delta > 0 ? '+' : ''}${fmt(m.delta).replace(/^[+]/, '')}`;
                    return (
                      <tr key={m.key} style={{ borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
                        <td style={{ textAlign: 'left', padding: '6px 12px' }}>
                          {m.label}
                          {isRate && m.unit && m.unit !== 'ratio' && (
                            <span style={{ color: '#9b9da3', marginLeft: 4 }}>({m.unit})</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 12px', fontWeight: 600 }}>{fmt(m.value)}</td>
                        <td style={{ padding: '6px 12px', color: '#6b6f76' }}>{fmt(m.baseValue)}</td>
                        <td style={{ padding: '6px 12px', color: deltaCol, fontWeight: 600 }}>
                          {deltaText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* —— 对比图：同一份 spec.charts，当前 / 对比各画一份（无按天，只比两次运行的聚合） —— */}
            <div style={{ marginTop: 12 }}>
              <MetricAggCharts
                charts={compareMetricAggCharts}
                summary={compareMetricAggSummary}
                baseSummary={compareMetricAggBaseSummary}
              />
            </div>
            </>
          )}
        </Spin>
      </div>
    );

    return (
      <div style={{ padding: 24, background: '#f7f7f9', minHeight: '100vh' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1d1d1f' }}>
              任务对比
            </h2>
            <div style={{ color: '#6b6f76', fontSize: 12, marginTop: 4 }}>
              对比同仿真类型任务；默认选中最新一条 daily Job
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#6b6f76', fontSize: 13 }}>当前任务：</span>
            <Tag color="blue" style={{ fontSize: 13, padding: '2px 8px' }}>
              [{jobId}] {currentJobName || '-'}
            </Tag>
            <span style={{ color: '#6b6f76', fontSize: 13, marginLeft: 12 }}>对比任务：</span>
            <Select
              style={{ minWidth: 300 }}
              showSearch
              value={selectedCompareJobId ?? undefined}
              onChange={(val) => setSelectedCompareJobId(val)}
              placeholder={compareJobListLoading ? '加载中...' : '按 jobId 或 jobName 搜索'}
              loading={compareJobListLoading}
              // 关掉客户端 filter,搜索交给 handleCompareCandidateSearch 走后端
              // jobId/jobName 过滤。
              filterOption={false}
              onSearch={handleCompareCandidateSearch}
              // options 首屏兜底:compareJobList(候选池)只在打开选择/搜索时才 populate,
              // 且只含「最近 10 条」;分享链接对比的往往是更老的 job,不在池内。此时
              // 选中值在 options 里无匹配项,antd 只回显裸 jobId。故把 compareJobMeta
              // (jobIds 精确查的权威源,首屏即到)并进来兜底,保证选中项始终有名字。
              options={(() => {
                const opts = compareJobList.map((j) => ({
                  value: j.jobId,
                  label: `[${j.jobId}] ${j.jobName || '-'}`,
                }));
                if (
                  selectedCompareJobId != null
                  && !opts.some((o) => o.value === selectedCompareJobId)
                ) {
                  const name =
                    compareJobMeta?.jobId === selectedCompareJobId
                      ? compareJobMeta.jobName
                      : undefined;
                  opts.unshift({
                    value: selectedCompareJobId,
                    label: `[${selectedCompareJobId}] ${name || '-'}`,
                  });
                }
                return opts;
              })()}
              notFoundContent={
                compareJobListLoading ? <Spin size="small" /> : '暂无同类型可对比任务'
              }
            />
            <Tooltip title="复制当前对比链接（已记住对比任务），分享给他人可看到相同的对比">
              <Button
                icon={<LinkOutlined />}
                onClick={handleCopyCompareLink}
                disabled={!selectedCompareJobId}
              >
                复制链接
              </Button>
            </Tooltip>
            <Tooltip title="互换当前任务与对比任务的 Job（重新加载，计算逻辑不变）">
              <Button
                icon={<SwapOutlined />}
                disabled={!selectedCompareJobId}
                onClick={() => {
                  if (!selectedCompareJobId) return;
                  // 把 path 里的 jobId(当前任务) 与 query 的 compareJobId(对比任务) 对调，
                  // 硬跳整页重挂载 —— selectedCompareJobId 只在 mount 时从 URL 初始化、
                  // 无 effect 回写，软跳会留旧值；与 line 4058 同一套硬跳做法。
                  const sp = new URLSearchParams(searchParams);
                  sp.set('mode', 'compare');
                  sp.set('compareJobId', String(jobId));
                  window.location.href = `/job-tasks/${selectedCompareJobId}?${sp.toString()}`;
                }}
              >
                切换
              </Button>
            </Tooltip>
          </div>
        </div>
        {selectedCompareJobId ? (
          <>
            {compareRaw && (compareRaw.todayDuplicateSceneCount ?? 0) > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  `当前 Job 存在 ${compareRaw.todayDuplicateSceneCount} 个重复场景（运行时同一场景被放入多份、对应多条 task），` +
                  `对比矩阵与下钻明细均按「每场景最新 task」计算。`
                }
              />
            )}
            {compareBasicInfoSection}
            {compareMetricsSection}
            {compareEvaluatorMatrixSection}
            {compareMetricAggSection}
            {/* 行为对比：闭环段上逐场景比较自车怎么开；需 menu:behavior-compare 权限（后端同码兜底） */}
            {hasPerm('menu:behavior-compare') && jobId && selectedCompareJobId != null && (
              <BehaviorCompareSection
                jobId={jobId}
                compareJobId={selectedCompareJobId}
                refreshKey={behaviorRefreshKey}
                activeLabel={compareBehaviorLabel}
                activeStrong={compareBehaviorStrong}
                onFilter={(next) => { void applyBehaviorFilters(next); }}
                openSceneId={behaviorDetailSceneId}
                onCloseScene={() => setBehaviorDetailSceneId(null)}
              />
            )}
            <div ref={compareDetailRef} style={{
              scrollMarginTop: 16,
              padding: 16,
              background: '#fff',
              border: '1px solid #ebebeb',
              borderRadius: 8,
            }}>
              <div style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500, marginBottom: 12 }}>
                场景级对比明细
              </div>
              {compareResultBody}
            </div>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message={
              compareJobListLoading
                ? '加载对比任务列表中...'
                : '请在右上角选择对比任务（同仿真类型）'
            }
          />
        )}
        {commentDrawerNode}
        {versionTraceModal}
        {taskLogModalEl}
        {rerunModalsNode}
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="link" onClick={handleBackToJobList} style={{ paddingLeft: 0 }}>
          返回 Job 列表
        </Button>
      </div>
      {/* 标题行：标题 + Job 状态 + 编译日志按钮（编译类 Job 才显示） */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1d1d1f' }}>
          Task 列表 - Job ID: {jobId}
          {headerStatusInfo?.jobName ? ` (${headerStatusInfo.jobName})` : ''}
        </h2>
        {(headerStatusInfo || isCompileJob) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto', flexShrink: 0 }}>
            {headerStatusInfo && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 13, color: '#9b9da3' }}>Job 状态</span>
                <JobStatusTag
                  status={headerStatusInfo.status}
                  statusName={headerStatusInfo.statusName}
                />
              </span>
            )}
            {isCompileJob && (
              <Button size="small" icon={<FileTextOutlined />} onClick={handleViewBuildLog}>
                编译日志
              </Button>
            )}
            {jobMeta && canRetryBazelJob(jobMeta) && (
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRetryBazelJob}>
                重试编译
              </Button>
            )}
          </div>
        )}
      </div>

      <ModelReleaseStaleAlert jobId={jobId} jobMeta={jobMeta} />
      {/* 页头卡片：仿真结果统计瓷砖 + Job 元信息 grid（自 v5 起替代原扁平灰底横条） */}
      <div
        style={{
          marginBottom: 16,
          padding: 16,
          background: '#fff',
          border: '1px solid #ebebeb',
          borderRadius: 8,
        }}
      >
        <JobStatTiles
          taskCount={taskCount}
          loading={taskCountLoading}
          showFailDrillLink
          failDrillHref={`/job-tasks/${jobId}?mode=stats&taskStatus=20`}
          actions={
            taskCount?.taskTypeId === 2 ? (
              <button
                type="button"
                onClick={handleToggleLabelBreakdown}
                title={labelBreakdownOpen ? '收起按场景分类' : '展开按场景分类'}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  background: labelBreakdownOpen ? '#eef2ff' : '#ffffff',
                  color: labelBreakdownOpen ? '#4338ca' : '#374151',
                  fontSize: 12,
                  lineHeight: '18px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontWeight: 500,
                  transition: 'background 120ms, color 120ms, border-color 120ms',
                }}
              >
                按场景分类
                <span
                  style={{
                    display: 'inline-block',
                    transform: labelBreakdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 160ms',
                    fontSize: 10,
                    lineHeight: 1,
                  }}
                >
                  ▾
                </span>
              </button>
            ) : null
          }
        />
        <div style={{ height: 1, background: '#f0f0f0', margin: '16px 0' }} />
        <JobInfoGrid
          fields={buildJobInfoFields(jobMeta, compareSceneTree, onTrace)}
          loading={!jobMeta}
        />
        {sceneSetStatsNode}
      </div>

      {labelBreakdownOpen && taskCount?.taskTypeId === 2 && renderLabelBreakdownPanel()}

      {diffFilter.compareJobId && diffFilter.diffMode && (
        <Alert
          type={diffFilter.diffMode === 'new_success' ? 'success' : 'error'}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              当前正在筛选 Daily 仿真差异：
              <strong style={{ margin: '0 4px' }}>
                {diffFilter.diffMode === 'new_success' ? '新增成功场景' : '新增失败场景'}
              </strong>
              （当前任务 <Tag color="blue">{jobId}</Tag> vs 对比任务{' '}
              <Tag color="purple">{diffFilter.compareJobId}</Tag>）
            </span>
          }
          action={
            <Button size="small" type="link" onClick={clearDiffFilter}>
              清除差异筛选
            </Button>
          }
        />
      )}

      <div ref={taskDetailRef} />
      {taskFilterAndTable}

      {/* 选择对比任务弹窗 */}
      <Modal
        title="选择对比任务"
        open={compareSelectVisible}
        onCancel={() => setCompareSelectVisible(false)}
        onOk={handleCompareConfirm}
        okText="确认"
        cancelText="取消"
        confirmLoading={compareLoading}
        width={500}
      >
        <div style={{ marginBottom: 12, color: '#6b6f76', fontSize: 13 }}>
          选择一个 Job 与当前 Job（ID: {jobId}）进行任务对比
        </div>
        <Spin spinning={compareJobListLoading}>
          <Select
            placeholder="搜索并选择 Job"
            showSearch
            value={selectedCompareJobId}
            onChange={(val) => setSelectedCompareJobId(val)}
            style={{ width: '100%' }}
            filterOption={(input, option) => {
              const label = String(option?.label || '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
            options={compareJobList.map((j) => ({
              value: j.jobId,
              label: `[${j.jobId}] ${j.jobName || '-'}`,
            }))}
          />
        </Spin>
      </Modal>

      {/* 对比结果 Modal —— 旧的弹窗形态；compareMode=true 时在独立页里直接渲染同一 body */}
      <Modal
        title={`任务对比 — ${currentJobName || 'Job ' + jobId} vs ${compareJobName}`}
        open={compareResultVisible}
        onCancel={() => setCompareResultVisible(false)}
        footer={null}
        width="95vw"
        style={{ top: 20, maxWidth: 1800 }}
      >
        {compareResultBody}
      </Modal>

      {taskLogModalEl}

      {/* 编译日志弹窗（compile_platform；package_bazel 直接新开标签页不入此弹窗） */}
      <Modal
        title={`编译日志 - Job ID: ${jobId}`}
        open={buildLogVisible}
        onCancel={() => setBuildLogVisible(false)}
        footer={null}
        width={900}
      >
        {buildLogUrl && (
          <div style={{ marginBottom: 12 }}>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              href={buildLogUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ paddingLeft: 0 }}
            >
              查看 / 下载完整编译日志
            </Button>
          </div>
        )}
        <Spin spinning={buildLogLoading}>
          <pre
            style={{
              maxHeight: '60vh',
              overflow: 'auto',
              margin: 0,
              padding: 12,
              background: '#f6f7f9',
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {buildLogContent || (buildLogLoading ? '加载中...' : '暂无编译日志')}
          </pre>
        </Spin>
      </Modal>

      {/* 任务列表评论 Drawer（compareMode 与默认 return 共用同一个节点，定义见组件顶部 commentDrawerNode） */}
      {commentDrawerNode}
      {versionTraceModal}

      {/* 「基于当前查询发起任务」两步流:confirm 核对 filter → CreateJobModal
          走完整创建流程,场景池锁定为当时冻结的 rerunFilters 快照。 */}
      {rerunModalsNode}

      {priorityModalOpen && jobBasicInfo && (
        <UpdatePriorityModal
          open={priorityModalOpen}
          onClose={() => setPriorityModalOpen(false)}
          onSuccess={() => {
            // 拉最新 basic info 刷新页头显示;statusName 也可能已变(不太可能,兜底)。
            loadJobBasicInfo(jobId);
          }}
          job={{
            jobId: jobBasicInfo.jobId,
            jobName: jobBasicInfo.jobName,
            priority: jobBasicInfo.priority,
            priorityName: jobBasicInfo.priorityName,
            status: jobBasicInfo.status,
          }}
        />
      )}
    </div>
  );
}
